import { i18n } from '../i18n.js';
import { MoonfallClickGuard } from '../utils/click-guard.js';
import { LoadScheduler } from '../utils/load-scheduler.js';
import { MoonfallAnalytics } from '../analytics/posthog-init.js';
import { DiscordLinks } from './discord-links.js';
import { MoonfallCloudGuard } from '../utils/cloud-mutation-guard.js';
import { creationRefusalLine } from './service-pause.js';
import { showAchievementBanner } from './banner.js';
import { MoonfallDiscoveryPalette } from '../discovery-palette.js';
import { MoonfallCloud } from '../convex-client.js';
/**
 * Collection UI Module
 * Browse owned cards and manage cardbacks
 */


import { AssetPreloader } from './asset-preloader.js';
import { CardbackArt } from './cardback-art.js';
import { CardEligibility } from './card-eligibility.js';
import { CardPacks } from './card-packs.js';
import { Cards } from './cards.js';
import { CardTilt } from './card-tilt.js';
import { CollectionLayout } from './collection-layout.js';
import { createCollectionPageCache } from './collection-page-cache.js';
import { fetchPageAnswer, normalizePageAnswer, pageLabelFor } from './collection-page-answer.js';
import { createHoverIntent } from './hover-intent.js';
import { DeckBuilder } from './deck-builder.js';
import { Effects } from './effects.js';
import { Firebase } from '../firebase-loader.js';
import { FoundryInventoryWarm } from './foundry-inventory-warm.js';
import { FX } from './fx.js';
import { HoverHandoff } from './hover-handoff.js';
import { GameState } from '../game-state.js';
import { MoonExchange } from './moon-exchange.js';
import { Pagination } from './pagination.js';
import { Platform } from './platform.js';
import { SoundEffects } from './sound-effects.js';
import { Stardust } from './stardust.js';
import { announceStardust, lastKnownStardust, onStardustAnnounced } from './stardust-chip.js';
import { StellarAssets } from './stellar-assets.js';
import { StellarFirstBannerDelivery } from './stellar-first-banner-delivery.js';
import { Store } from './store.js';
import { TouchPreview } from './touch-preview.js';
import { cssLengthPx, uiCssPx, uiPx } from './ui-scale.js';
let Collection = {};

Collection = (function () {
    const formatStardust = (value) => Stardust?.format?.(value) ?? String(value);
    /* The chip's NUMBER: the sparkle beside it is the symbol (pills.css), the
       same drawing the lobby chip and the store carry. */
    const formatStardustWithSymbol = (value) => formatStardust(value);
    let allCards = [];
    let cardById = new Map();
    let ownedCards = [];
    let ownedCardMap = new Map();
    let ownedCardStellarMap = new Map();
    let recipeByResultCardId = new Map();
    let discoveredRecipeCardIds = new Set();
    // First-discoverer gamification flags for the viewer (fuse authorship and
    // first-crafted Stellar assets). Display-only; the card faces stay normal.
    let firstDiscoveryBadges = { fuse: new Set(), stellar: new Set() };
    // Whose ribbons those are; null until an answer (or the lobby's warm) arrives.
    let firstDiscoveryBadgesProfileId = null;
    let cardbacks = [];
    let unlockedCardbacks = ['default'];
    let selectedCardback = 'default';
    let forgeCosts = null;
    // `undefined` until the profile read lands: an unknown balance must not
    // be displayed as a confident zero.
    let stardust;
    // The profile read in flight, if any. The card detail waits on it rather
    // than paint a verdict about a balance it does not have yet.
    let pendingProfileRead = null;
    // The open card detail's Craft/Stellar row, kept so a balance or Stellar
    // lock that moves can be re-stated in place (`applyCraftRowState`).
    let activeDetailCraftRow = null;
    let profileId = null;
    let profileFirebaseUid = null;
    let profileUsername = '';
    let isOpen = false;
    let openGeneration = 0;
    let activeSection = 'cards';
    let hoverSurfaceGeneration = 0;
    let activeCardTab = 'characters';
    let currentSort = 'cost-asc';
    let currentFilters = { collection: '', mana: '' };
    let activeSpellOwnerFilter = 'all';
    let currentSearch = '';
    let activeDetailCardId = null;
    let activeDetailVariant = 'normal';
    let lastCardsGridScrollTop = 0;
    let lastDetailRequestId = 0;
    let recipeDetailsByKey = new Map();
    let stellarPollTimer = null;
    let toastHost = null;
    let stellarAssetListenersReady = false;
    let stardustFollowReady = false;
    let holdingsFollowReady = false;
    // The summoners of the Spells on screen, as read for them (readSpellOwners).
    const characterNameByKey = new Map();
    /* Restored with the card-detail metadata below: a Spell's detail names the
       summoner it belongs to, and it does so with that summoner's portrait. */
    const characterPortraitByKey = new Map();
    let suspendedSectionOpenGeneration = 0;
    const CARDS_PAGE_SIZE = 8;
    const MOBILE_CARDS_PAGE_SIZE = CARDS_PAGE_SIZE;
    // Bounded skeleton→cards swap (docs/asset-loading.md law 2 + grace).
    // Long enough that a typical warm page commits atomically complete (the
    // pre-doctrine feel); a genuinely slow page commits with shimmer wells.
    const COLLECTION_REVEAL_CAP_MS = 4000;
    /* A page replacing the ONE loading state already on screen (an open's
       page of wells presented past its grace, or the page a re-query keeps
       on screen under its glints past it) waits longer for its pictures
       while they are still arriving (a raster that hangs with its picture in
       hand gains nothing from it),
       so the wells go in one frame and no picture then loads in front of the
       player. Eight ~835 KB pictures need ~4.5 s on a phone's 4G, and at the
       4 s cap the WebKit phone seat's cold open lost one picture and its warm
       one three, each settling in its tile after the page appeared (round 15,
       28 September 2026: "1 picture(s) loading in front", "9 scattered
       placeholder(s)"); a page turn there committed with two (cold) and six
       (revisit) pictures still on the wire ("page painted half-built"). Past
       this cap the page still commits text-first. */
    const COLLECTION_WELLS_REVEAL_CAP_MS = 6000;
    /* The open is held off-screen until its first page is committed. A page
       that commits inside the shared grace (docs/asset-loading.md, the grace
       rule) is presented whole, with no loading state at all. Past the grace
       it depends on what is left:
        - the page's read or its pictures still on the wire: the modal
          presents with ONE organized loading state (the page of wells), which
          the page replaces whole. The hold was 6 s, so a cold phone open
          showed nothing but the opener's busy style for 4.3 s — 3.7 s silent
          past the grace, where the baseline had its skeletons up at 0.2 s
          (round 14, 26 September 2026, Chrome phone seat on 4G: eight
          ~835 KB pictures need ~4.5 s);
        - only the build (the page read, its pictures in hand, as on a
          returning visit): it finishes unseen and is presented whole when
          its page commits — never over wells. That wait's one loading state
          is the ring turning on the pressed lobby tile (openerRing). The
          page's commit is itself capped (COLLECTION_REVEAL_CAP_MS: past it
          the page commits text-first, each late picture in its own art
          well), and so is the hold (COLLECTION_OPEN_BUILD_CAP_MS). It was
          capped at the doctrine's 1.5 s readiness gate, but on the Chrome
          phone seat (4x CPU) a page's build is 1.5-3 s of raster work: warm
          and revisit opens presented their wells at 1.5 s and the page
          replaced them 1.5 s later — "8 placeholder(s) on a warm cache"
          (round 15, 28 September 2026). The lead's bar: a warm open presents
          the finished page with no loading state.
       "In hand" is the pictures' BYTES: a picture a warm of this surface
       brought in, or that the page's own warm decoded (picturesInHand). A
       warm open whose decode alone outlasted the grace flashed its wells and
       replaced them half a second later (webkitArt review, 26 September).
       A page committed by the grace is presented at once, with the balance
       the lobby knows, never held for its own balance read. */
    const COLLECTION_OPEN_HOLD_CAP_MS = 450;
    // Above the page's own commit cap (the reveal cap, after its art gate and joins).
    const COLLECTION_OPEN_BUILD_CAP_MS = 6000;
    /* A card detail is built behind the grid and swapped in whole. Its cap is
       the doctrine's model for a user-visible readiness gate (the 1500 ms
       mulligan reveal); past it the detail presents text-first, its art wells
       still settling in place. */
    const DETAIL_PRESENT_CAP_MS = 1500;
    /* A press is answered by its result when that lands inside a frame or
       three. Only a wait long enough to see earns a busy state, so a warm
       open or page turn never flashes a spinner for two frames. */
    const PRESS_ACK_DELAY_MS = 60;
    // The shared art grace: a re-query slower than this dims the page it replaces.
    const STALE_PAGE_GRACE_MS = 450;
    /* Page answers: served on open while this young (the server's own catalog
       context lives 30 s; a minute is still one lobby visit), re-read behind a
       presented page once older than the revalidation age, and refreshed by an
       open intent (hover, focus, first touch) once older than the intent age. */
    const PAGE_PRESENT_MAX_AGE_MS = 60000;
    const PAGE_REVALIDATE_AFTER_MS = 5000;
    const PAGE_INTENT_REFRESH_AFTER_MS = 10000;
    /* The next page is built ahead at idle (see `scheduleCardsPrebuild`). An
       idle period with less room than this starts no tile: a tile's build and
       its first raster step never fit in the tail of a frame. Without
       requestIdleCallback (WebKit) a timer stands in for the idle period: it
       waits a beat after the page's own frames and gives itself this budget. */
    const PREBUILD_MIN_IDLE_MS = 10;
    const PREBUILD_FALLBACK_DELAY_MS = 120;
    const PREBUILD_FALLBACK_SLICE_MS = 16;
    /* An input (a press, a key, a wheel, a hover preview starting) holds the
       build ahead until input has been quiet this long, every input starting
       the wait again. A press is held 80-150 ms; the idle period inside it
       rebuilt the tile the press had just dropped and started its raster (the
       50-130 ms task), which the click then threw away. */
    const PREBUILD_INPUT_QUIET_MS = 400;
    const DESKTOP_CARDS_SCALE = 1.05;
    const DESKTOP_READABLE_CARD_WIDTH = 136;
    const DESKTOP_MAX_CARDS_SCALE = 2;
    const MOBILE_CARDS_SCALE = 1.7;
    /* Legibility floor for the phone catalogue: below this a card stops being
       readable or aimable, and the grid should paginate instead of shrink. */
    const MOBILE_MIN_CARDS_SCALE = 0.72;
    const CARD_FIT_SAFETY_INSET = 1;
    const CARDBACKS_PER_PAGE = 5;
    const MOBILE_CARDBACKS_FALLBACK = 3;
    let cardsPageSize = CARDS_PAGE_SIZE;
    let cardsGridColumns = 4;
    /* `measureCardsPageSize` recomputes `cardsGridColumns`, but only
       `applyCardsGridColumns` publishes it, and only `renderCards` calls that.
       These two let the viewport handler publish a fresh measurement without a
       re-render — and refuse to publish one that was never taken. */
    let cardsGridMeasured = false;
    /* THE SCALE BELONGS TO ITS COLUMNS. `measureCardsPageSize` also measures
       the card scale of the capacity it finds, and `applyCardsGridColumns`
       publishes the two together, on the page they were measured for. The
       resize listener wrote the new scale onto the grid whatever the new
       capacity: on a resize that changes how many cards a page holds (a phone
       turned, a breakpoint crossed) the page on screen took the new size in
       its old columns until the debounced rebuild replaced it (review of wave
       E, 28 September 2026). */
    let cardsGridScale = '';
    // The capacity that fit was measured for.
    let cardsGridCapacity = 0;
    // The capacity of the page the grid shows (its commit, or its wells).
    let shownCardsPageSize = 0;
    let renderedCardCount = 0;
    let cardbacksPageSize = CARDBACKS_PER_PAGE;
    let cardsPage = 1;
    let cardbackPage = 1;
    let cardsCacheKey = '';
    let cardsCache = [];
    let cardsDataVersion = 0;
    let deckBuilderSectionReady = false;
    let deckBuilderSectionOpening = null;
    let availableSpellOwners = [];
    // Display names served alongside `spellOwners` by the cards page. The page
    // that offers an owner also names it, so the dropdown is correct on its
    // FIRST render instead of waiting on the separate roster fetch.
    let spellOwnerNamesByKey = new Map();
    let availableCollections = [];
    let currentPageEntries = [];
    let committedCardsSignature = null;
    let lastCardsRequestId = 0;
    // The last render that came to an end on screen (its page, "no cards match", or its failure).
    let settledCardsRequestId = 0;
    /** @type {AbortController | null} */
    let cardsWarmController = null;
    const cardsWarmOwners = new Set();
    let auxiliaryAllCards = [];
    // The language the owned catalog in hand was read in: its names are the
    // Exchange's sell list and the words of its rows.
    let auxiliaryCatalogLanguage = null;
    // false: the player's collection is too large to list whole, so the
    // catalog in hand is empty and the Exchange finds sell cards by search
    // (card-scale, 21 October 2026); null: not read yet.
    let auxiliaryCatalogComplete = null;
    let auxiliaryCardsRequest = null; // { promise, revision, uid, lang } — one /api/cards read in flight
    let exchangeCatalogRequestId = 0;
    let auxiliaryInventoryStale = false;
    let auxiliaryInventoryRevision = 0;
    let collectionSupportDataPromise = null;
    let ownedFoundryDiscoveryRepairPromise = null;
    let ownedFoundryDiscoveryRepairProfileId = null;
    let cardsNeedRemoteRefresh = false;
    /* The page answers read past the server's caches after the last change
       (the lobby's window planned again, rewarmLobbyWindow): the pageDataEpoch
       they were read in, or -1. The open serves them rather than asking for
       the same pages past those caches a second time. */
    let freshAnswersEpoch = -1;
    // The next page, built ahead at idle (`scheduleCardsPrebuild`).
    let cardsPrebuild = null;
    // The last input the build ahead yielded to (yieldCardsPrebuild).
    let lastPrebuildInputAt = -Infinity;
    // The pictures of the page after the one on screen, settling (readNextPageAhead).
    let nextPageArtReady = null;
    // The pictures of the page the latest render shows, settling (renderCards).
    let currentPageArtSettled = null;
    let packWrapperGate = null;
    /* The page on screen, as its commit left it: the question and page it
       answers (cardsPageKey), the render that committed it, its answer and
       its pictures' settling. A press back to it ends a turn at once
       (endCardsTurnOnShownPage). Null when its answers went stale. */
    /** @type {{ key: string, requestId: number, pagination: any, artSettled: Promise<unknown> | null, warmController: AbortController } | null} */
    let shownCardsTurn = null;
    // Bumped whenever the page answers go stale: a look-ahead is for the answers it was read from.
    let pageDataEpoch = 0;
    let i18nListenersReady = false;
    let collectionEventListenersReady = false;
    let collectionKeyHandler = null;
    let collectionReturnFocus = null;
    let collectionBackgroundState = null;
    let collectionFocusFrame = null;
    const collectionRevealTimers = new Set();
    const HOVER_PREVIEW_Z_INDEX = '2147483647';
    const HOVER_PREVIEW_MARGIN = 16;
    const HOVER_PREVIEW_OPTIONS = {
        baseScale: 3.5,
        minScale: 1.6,
        margin: HOVER_PREVIEW_MARGIN,
        origin: 'center center',
        translateYPercent: 0,
    };
    const SURFACE_OWNER = 'collection';
    /* A preview answers a pointer the player MOVED (hover-intent.js). Disarmed
       whenever cards appear under a resting pointer: the modal presenting, a
       page swapping in, the card detail replacing the grid. Every element that
       opens a preview is listed, so the first real move hovers the one it is on. */
    const hoverIntent = createHoverIntent({
        selector: '.collection-card-item, .collection-card-detail-card, .collection-recipe-parent',
        within: () => document.getElementById('collection-modal'),
    });

    function claimLobbySurface() {
        const owner = document.body?.dataset?.lobbySurfaceOwner;
        if (owner && owner !== SURFACE_OWNER) return false;
        if (document.body) document.body.dataset.lobbySurfaceOwner = SURFACE_OWNER;
        return true;
    }

    function releaseLobbySurface() {
        if (document.body?.dataset?.lobbySurfaceOwner === SURFACE_OWNER) {
            delete document.body.dataset.lobbySurfaceOwner;
        }
    }

    function uiText(key, fallback, replacements = {}) {
        const translated = i18n?.t?.(key, replacements);
        const text = translated && translated !== key ? translated : fallback;
        /* BOTH brace styles, double FIRST.
           The copy uses `{{name}}` (as ui.json writes it) and, in a few
           places, single `{name}`. A helper that knows only one leaves the
           other on screen whenever i18n has not resolved. Order matters:
           substituting the single form first turns `{{count}}` into `{3}`,
           which is exactly how starter-reward.js was rendering it. */
        return Object.entries(replacements || {}).reduce(
            (result, [name, value]) => String(result)
                .split(`{{${name}}}`).join(String(value))
                .split(`{${name}}`).join(String(value)),
            String(text),
        );
    }

    function applyCollectionTranslations() {
        if (i18n?.applyTranslations) {
            i18n.applyTranslations();
        }
    }

    /* The phone page (one row of enlarged cards, as many as fit) belongs to the
       phone GRID, and mobile.css builds that grid only inside its phone
       viewport gate. `isMobileUi()` is wider: it also answers yes for any
       browser whose user agent names a phone or tablet, at any size. So an
       iPad, an Android tablet or Chrome DevTools' Responsive mode laid out the
       desktop grid's four columns while this fitted a phone row into them:
       five or six cards, the spill wrapped onto a second row the grid had no
       room for, cut off. Decide by the screen, as the CSS does. */
    function isMobileCollectionUi() {
        return !!Platform?.isMobileViewport?.();
    }

    function defaultCardsPageSize() {
        return isMobileCollectionUi() ? MOBILE_CARDS_PAGE_SIZE : CARDS_PAGE_SIZE;
    }

    function defaultCardbacksPageSize() {
        return isMobileCollectionUi() ? MOBILE_CARDBACKS_FALLBACK : CARDBACKS_PER_PAGE;
    }

    function readCssNumber(style, property, fallback) {
        const numeric = cssLengthPx(style?.getPropertyValue?.(property));
        return Number.isFinite(numeric) && numeric >= 0 ? numeric : fallback;
    }

    function getLayoutRect(element) {
        const rect = element?.getBoundingClientRect?.();
        return {
            width: Number(rect?.width) || Number(element?.clientWidth) || 0,
            height: Number(rect?.height) || Number(element?.clientHeight) || 0,
        };
    }

    function measureCardsPageSize() {
        const fallback = defaultCardsPageSize();
        const grid = document.getElementById('collection-cards-grid');
        const layout = CollectionLayout;
        const cardSize = Cards?.getCardPixelSize?.();
        const rect = getLayoutRect(grid);
        if (!grid || !layout?.fitCapacity || rect.width <= 1 || rect.height <= 1
            || !(Number(cardSize?.width) > 0) || !(Number(cardSize?.height) > 0)) {
            /* Nothing was measured, so `cardsGridColumns` still describes the
               PREVIOUS viewport. Say so, or the caller republishes a stale
               column count as though it were fresh. */
            cardsGridMeasured = false;
            return fallback;
        }

        const mobile = isMobileCollectionUi();
        const style = window.getComputedStyle?.(grid);
        const shell = grid.closest?.('.collection-container');
        const shellStyle = shell ? window.getComputedStyle?.(shell) : null;
        const shellZoom = readCssNumber(shellStyle, 'zoom', 1) || 1;
        const preferredScale = mobile
            ? MOBILE_CARDS_SCALE
            : (layout.fitReadableScale?.({
                itemWidth: Number(cardSize.width),
                zoom: shellZoom,
                readableWidth: uiPx(DESKTOP_READABLE_CARD_WIDTH),
                minScale: DESKTOP_CARDS_SCALE,
                maxScale: DESKTOP_MAX_CARDS_SCALE,
            }) || DESKTOP_CARDS_SCALE);
        const horizontalInset = readCssNumber(style, 'padding-left', 0)
            + readCssNumber(style, 'padding-right', 0);
        const verticalInset = readCssNumber(style, 'padding-top', 0)
            + readCssNumber(style, 'padding-bottom', 0);
        const scale = layout.fitItemScale?.({
            available: rect.height,
            item: Number(cardSize.height),
            preferredScale,
            inset: verticalInset,
            safetyInset: CARD_FIT_SAFETY_INSET,
            // A card that cannot be read is not a card. On a short landscape
            // phone the height fit alone drove this to 0.372, rendering 15x38px
            // slivers; hold a legible floor and let pagination show fewer.
            minScale: mobile ? MOBILE_MIN_CARDS_SCALE : undefined,
        }) || preferredScale;
        cardsGridScale = String(scale);
        const columnGap = readCssNumber(style, 'column-gap', mobile ? 12 : 14);
        const rowGap = readCssNumber(style, 'row-gap', mobile ? 12 : 16);
        const availableWidth = Math.max(1, rect.width - horizontalInset);
        const availableHeight = Math.max(1, rect.height - verticalInset - CARD_FIT_SAFETY_INSET);
        const itemWidth = Number(cardSize.width) * scale;
        const itemHeight = Number(cardSize.height) * scale;
        if (!mobile && layout.chooseBalancedGrid) {
            const balanced = layout.chooseBalancedGrid({
                width: availableWidth,
                height: availableHeight,
                itemWidth,
                itemHeight,
                columnGap,
                rowGap,
                maxItems: fallback,
            });
            if (balanced.pageSize > 0) {
                cardsGridColumns = balanced.columns;
                cardsGridMeasured = true;
                cardsGridCapacity = balanced.pageSize;
                return balanced.pageSize;
            }
        }
        cardsGridColumns = 0;
        cardsGridMeasured = true;
        cardsGridCapacity = layout.fitCapacity({
            width: availableWidth,
            height: availableHeight,
            itemWidth,
            itemHeight,
            columnGap,
            rowGap,
            maxRows: mobile ? 1 : undefined,
            maxItems: fallback,
        });
        return cardsGridCapacity;
    }

    /**
     * Publish a fit on the grid — its card scale and its columns, together —
     * for the page it was measured for. `fit` ({ scale, columns, pageSize })
     * defaults to the fit measured last.
     */
    function applyCardsGridColumns(entryCount = cardsPageSize, fit = null) {
        const grid = document.getElementById('collection-cards-grid');
        if (!grid) return;
        const scale = fit ? fit.scale : cardsGridScale;
        const gridColumns = fit ? fit.columns : cardsGridColumns;
        shownCardsPageSize = fit ? fit.pageSize : cardsPageSize;
        if (scale) grid.style.setProperty('--collection-fit-card-scale', scale);
        if (isMobileCollectionUi() || gridColumns <= 0) {
            grid.classList.remove('collection-cards-grid-balanced');
            grid.style.removeProperty('--collection-column-count');
            /* The phone grid's track count comes from CSS
               (`--deckbuilder-column-count`, shared with the Deck Builder's
               catalog) while the PAGE SIZE comes from measurement, and the two
               can disagree: a first page of 6 cards in a grid laying out 8
               tracks left two visibly empty slots. `--collection-column-count`
               cannot fix it — that variable only binds through
               `.collection-cards-grid-balanced`, which this branch never adds.
               Cap the variable the grid actually reads, and only downward, so a
               full page keeps the composition the media query chose.

               Clear the inline value BEFORE reading the computed one, or we
               measure our own previous cap instead of the CSS. */
            grid.style.removeProperty('--deckbuilder-column-count');
            const served = Math.max(0, Math.floor(Number(entryCount) || 0));
            const cssColumns = Math.floor(Number.parseFloat(
                window.getComputedStyle?.(grid)?.getPropertyValue('--deckbuilder-column-count'),
            ));
            if (served > 0 && Number.isFinite(cssColumns) && served < cssColumns) {
                grid.style.setProperty('--deckbuilder-column-count', String(served));
            }
            return;
        }
        const visibleColumns = Math.max(1, Math.min(
            gridColumns,
            Math.max(1, Number(entryCount) || 0),
        ));
        grid.classList.add('collection-cards-grid-balanced');
        grid.style.setProperty('--collection-column-count', String(visibleColumns));
    }

    /**
     * The fit measured last, on the page on screen, when that page holds as
     * many cards as the fit was measured for (and no page of another capacity
     * is on its way). A fit of another capacity waits for its own page:
     * renderCards builds that page at it (its paint stage carries it) and
     * publishes it with the page's columns at the commit.
     */
    function refitShownCardsPage() {
        if (!cardsGridMeasured || cardsGridCapacity !== shownCardsPageSize || cardsPageSize !== shownCardsPageSize) return;
        applyCardsGridColumns(renderedCardCount);
    }

    /** The card scale the grid's tiles are drawn at now. */
    function publishedCardsScale(container) {
        return container?.style?.getPropertyValue?.('--collection-fit-card-scale') || '';
    }

    function measureCardbackTrackWidth(grid) {
        if (!grid) return 0;
        const probe = document.createElement('span');
        probe.className = 'collection-cardback-layout-probe';
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:fixed;left:-9999px;top:0;visibility:hidden;pointer-events:none;'
            + 'width:var(--collection-cardback-track-width, 116px);height:1px;';
        grid.appendChild(probe);
        const width = Number(probe.getBoundingClientRect?.().width) || 0;
        probe.remove();
        if (width > 0) return width;
        if (!isMobileCollectionUi()) return 116;
        return Math.min(116, Math.max(84, Number(window.innerWidth || 0) * 0.26));
    }

    function measureCardbacksPageSize() {
        const fallback = defaultCardbacksPageSize();
        const grid = document.getElementById('collection-cardback-grid');
        const layout = CollectionLayout;
        const rect = getLayoutRect(grid);
        if (!grid || !layout?.fitCapacity || rect.width <= 1) return fallback;
        const style = window.getComputedStyle?.(grid);
        return layout.fitCapacity({
            width: rect.width,
            itemWidth: measureCardbackTrackWidth(grid),
            columnGap: readCssNumber(style, 'column-gap', 12),
            maxRows: 1,
            maxItems: CARDBACKS_PER_PAGE,
        });
    }

    function isCardbacksSectionVisible() {
        const modal = document.getElementById('collection-modal');
        const section = document.getElementById('collection-cardbacks-section');
        return isOpen
            && activeSection === 'cardbacks'
            && !!modal
            && modal.style.display !== 'none'
            && !!section
            && section.style.display !== 'none';
    }

    function preservePageForCapacity(page, previousPageSize, nextPageSize) {
        return CollectionLayout?.pageForLogicalOffset?.({
            page,
            pageSize: previousPageSize,
            nextPageSize,
        }) || 1;
    }

    function syncCardsPageSize({ preserveOffset = true } = {}) {
        /* Behind a card detail the grid has no box, and measured there it read
           as the default eight: a refresh made then (a craft, the Stellar poll
           seeing a print move, a print taken back) built eight cards for a
           phone grid of six or seven, and Back showed them in a second row
           (21 October 2026, 667x275 and 852x344). It keeps the size it was
           measured at until it is on screen again (closeCardDetail). */
        if (cardsGridMeasured && !cardsGridOnScreen()) return false;
        const nextPageSize = measureCardsPageSize();
        if (nextPageSize === cardsPageSize) return false;
        if (preserveOffset) cardsPage = preservePageForCapacity(cardsPage, cardsPageSize, nextPageSize);
        cardsPageSize = nextPageSize;
        return true;
    }

    function syncCardbacksPageSize({ preserveOffset = true } = {}) {
        // A display:none panel has no trustworthy width. Keep the last known
        // capacity until Cardbacks is visible instead of falling back to the
        // mobile three-item estimate and painting a transient short page.
        if (!isCardbacksSectionVisible()) return false;
        const nextPageSize = measureCardbacksPageSize();
        if (nextPageSize === cardbacksPageSize) return false;
        if (preserveOffset) cardbackPage = preservePageForCapacity(cardbackPage, cardbacksPageSize, nextPageSize);
        cardbacksPageSize = nextPageSize;
        return true;
    }

    /* The background is what the Collection covers, never a layer painted
       above it. Setting aside "everything else" made the welcome gift, which
       opens over everything (z-index 10020 over the Collection's 10000), inert
       while it was the one thing on screen: inert is skipped by hit-testing,
       so a new player's presses on "Enter the Lobby" went through it to the
       hidden Collection, fifteen of them, and they left (PostHog replay
       01a16462, 22 October 2026). The store and the rotate and consent
       overlays, above it too, keep their names here. */
    function paintsAboveCollection(element, modalLayer) {
        const layerOf = (node) => {
            const style = window.getComputedStyle?.(node);
            if (!style || style.position === 'static') return null;
            const z = Number.parseInt(style.zIndex, 10);
            return Number.isFinite(z) ? z : null;
        };
        const elementZ = layerOf(element);
        const modalZ = layerOf(modalLayer);
        if (elementZ === null || modalZ === null) return false;
        if (elementZ !== modalZ) return elementZ > modalZ;
        // One layer: the later in the document paints on top.
        return Boolean(modalLayer.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING);
    }

    function getCollectionBackgroundElements(modal) {
        if (!modal || !document.body) return [];
        const children = Array.from(document.body.children);
        const modalLayer = children.find((element) => element === modal || element.contains(modal)) || modal;
        return children.filter((element) => (
            element !== modalLayer
            && element.id !== 'store-modal'
            && element.id !== 'rotate-overlay'
            && !element.classList?.contains('consent-gate-overlay')
            && !['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE'].includes(element.tagName)
            && !paintsAboveCollection(element, modalLayer)
        ));
    }

    function hasActiveCollectionPrioritySurface() {
        if (Store?.isOpen?.()) return true;
        return Array.from(document.querySelectorAll('#rotate-overlay, .consent-gate-overlay')).some((element) => {
            if (element.inert || element.getAttribute('aria-hidden') === 'true') return false;
            const style = window.getComputedStyle?.(element);
            return style?.display !== 'none' && style?.visibility !== 'hidden';
        });
    }

    function dismissOpenCollectionFilterPopover(modal) {
        const popover = Array.from(modal?.querySelectorAll(
            '#collection-filter-popover.is-open, #deck-builder-filter-popover.is-open'
        ) || []).find((candidate) => {
            const controller = modal.querySelector(`[aria-controls="${candidate.id}"]`);
            return isVisibleCollectionControl(candidate, modal)
                && isVisibleCollectionControl(controller, modal);
        });
        if (!popover) return false;
        popover.classList.remove('is-open');
        const toggle = modal.querySelector(`[aria-controls="${popover.id}"]`);
        toggle?.setAttribute('aria-expanded', 'false');
        toggle?.focus();
        return true;
    }

    function isolateCollectionBackground(modal) {
        if (collectionBackgroundState !== null) return;
        collectionBackgroundState = getCollectionBackgroundElements(modal).map((element) => ({
            element,
            hadInertAttribute: element.hasAttribute('inert'),
            inertValue: 'inert' in element ? element.inert : null,
            ariaHidden: element.getAttribute('aria-hidden'),
        }));
        collectionBackgroundState.forEach(({ element }) => {
            if ('inert' in element) element.inert = true;
            element.setAttribute('inert', '');
            element.setAttribute('aria-hidden', 'true');
        });
    }

    function restoreCollectionBackground() {
        if (collectionBackgroundState === null) return;
        const states = collectionBackgroundState;
        collectionBackgroundState = null;
        states.forEach(({ element, hadInertAttribute, inertValue, ariaHidden }) => {
            if ('inert' in element && inertValue !== null) element.inert = inertValue;
            if (hadInertAttribute) element.setAttribute('inert', '');
            else element.removeAttribute('inert');
            if (ariaHidden === null) element.removeAttribute('aria-hidden');
            else element.setAttribute('aria-hidden', ariaHidden);
        });
    }

    function restoreCollectionFocus(returnFocus, modal) {
        if (hasActiveCollectionPrioritySurface()
            || document.body?.classList.contains('game-transition-pending')
            || document.body?.classList.contains('game-active')
            || !returnFocus?.isConnected
            || typeof returnFocus.focus !== 'function') return;

        returnFocus.focus({ preventScroll: true });
        const focusGeneration = openGeneration;
        collectionFocusFrame = window.requestAnimationFrame?.(() => {
            collectionFocusFrame = null;
            if (isOpen
                || focusGeneration !== openGeneration
                || hasActiveCollectionPrioritySurface()
                || document.body?.classList.contains('game-transition-pending')
                || document.body?.classList.contains('game-active')
                || !returnFocus.isConnected) return;
            const active = document.activeElement;
            const focusWasDeferredAway = !active
                || active === document.body
                || active === modal
                || modal?.contains(active);
            if (focusWasDeferredAway) returnFocus.focus({ preventScroll: true });
        });
    }

    function cancelCollectionFocusRestore() {
        if (collectionFocusFrame == null) return;
        window.cancelAnimationFrame?.(collectionFocusFrame);
        collectionFocusFrame = null;
    }

    function collectionSessionIsCurrent(generation, section = null) {
        return isOpen
            && generation === openGeneration
            && (!section || activeSection === section)
            && !document.body?.classList.contains('game-transition-pending')
            && !document.body?.classList.contains('game-active');
    }

    function isVisibleCollectionControl(element, modal) {
        if (!element || element.disabled || element.getAttribute('aria-hidden') === 'true') return false;
        let current = element;
        while (current && current !== modal) {
            if (current.hidden || current.getAttribute?.('aria-hidden') === 'true') return false;
            const style = window.getComputedStyle?.(current);
            if (style?.display === 'none' || style?.visibility === 'hidden') return false;
            current = current.parentElement;
        }
        return true;
    }

    function getCollectionFocusableElements(modal) {
        if (!modal) return [];
        return Array.from(modal.querySelectorAll(
            'button:not([disabled]):not([tabindex="-1"]), a[href]:not([tabindex="-1"]), '
            + 'input:not([disabled]):not([tabindex="-1"]), select:not([disabled]):not([tabindex="-1"]), '
            + 'textarea:not([disabled]):not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])'
        )).filter((element) => isVisibleCollectionControl(element, modal));
    }

    function removeCollectionKeyHandler() {
        if (!collectionKeyHandler) return;
        document.removeEventListener('keydown', collectionKeyHandler, true);
        collectionKeyHandler = null;
    }

    function installCollectionKeyHandler(modal) {
        removeCollectionKeyHandler();
        collectionKeyHandler = (event) => {
            if (!isOpen || hasActiveCollectionPrioritySurface()) return;
            if (event.key === 'Escape') {
                // Compact filter surfaces own the first Escape. Handle them in
                // capture phase because WebKit does not consistently focus a
                // pointer-clicked button; the event may originate at document
                // level and never reach the descendant popover listener.
                if (dismissOpenCollectionFilterPopover(modal)) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                }
                event.preventDefault();
                event.stopPropagation();
                close();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = getCollectionFocusableElements(modal);
            if (focusable.length === 0) {
                event.preventDefault();
                return;
            }
            const currentIndex = focusable.indexOf(document.activeElement);
            const nextIndex = event.shiftKey
                ? (currentIndex <= 0 ? focusable.length - 1 : currentIndex - 1)
                : (currentIndex < 0 || currentIndex === focusable.length - 1 ? 0 : currentIndex + 1);
            event.preventDefault();
            focusable[nextIndex].focus();
        };
        document.addEventListener('keydown', collectionKeyHandler, true);
    }

    function clamp(value, min, max) {
        if (!Number.isFinite(value)) return min;
        if (!Number.isFinite(min)) return value;
        if (!Number.isFinite(max)) return value;
        if (min > max) return min;
        return Math.min(Math.max(value, min), max);
    }

    /* The preview the pointer can still own: the one carrying the id. One
       handed to a neighbour (HoverHandoff) gives the id up and stays on
       screen only until the neighbour's own preview is revealed, so the id
       names the preview being built; a tear-down that must take the one being
       left too goes through `clearHoverPreview`, never the id alone. */
    function currentHoverPreview() {
        if (typeof HoverHandoff?.currentPreview === 'function') return HoverHandoff.currentPreview();
        return document.getElementById('card-hover-preview');
    }

    /* `keepHandedOff`: a hover starting or ending on the grid leaves a preview
       handed to a neighbour alone — it leaves in the task the neighbour's is
       revealed, or when no hover is left to reveal one. Everything else is a
       tear-down and takes it too. */
    function clearHoverPreview({ keepHandedOff = false } = {}) {
        if (!keepHandedOff) HoverHandoff?.releaseHandedOff?.();
        const preview = currentHoverPreview();
        if (!preview) return;
        const previewCard = preview.querySelector('.card');
        if (previewCard?.classList?.contains('stellar-card') && Cards?.setStellarHover) {
            Cards.setStellarHover(previewCard, false);
        }
        preview.remove();
    }

    function invalidateHoverSurface() {
        hoverSurfaceGeneration += 1;
        Cards?.clearHoverPreview?.();
        // Collection can initialize before Cards in isolated tests or partial
        // clients. Keep its own removal path as the authoritative fallback.
        clearHoverPreview();
    }

    function isScrollableHost(element) {
        if (!element || typeof element.scrollTop !== 'number') return false;
        return (element.scrollHeight - element.clientHeight) > 1;
    }

    function findScrollableAncestor(element) {
        let current = element;
        while (current && current !== document.body && current !== document.documentElement) {
            if (isScrollableHost(current)) return current;
            current = current.parentElement;
        }
        return null;
    }

    function findScrollHostBySelectors(target, selectors = []) {
        for (const selector of selectors) {
            if (!selector) continue;
            const fromTarget = target?.closest ? target.closest(selector) : null;
            if (fromTarget && typeof fromTarget.scrollTop === 'number') return fromTarget;
            const fromDocument = document.querySelector(selector);
            if (fromDocument && typeof fromDocument.scrollTop === 'number') return fromDocument;
        }
        return null;
    }

    function forwardWheelScroll(preview, event, fallbackSelectors = []) {
        if (!preview || !event) return;

        preview.style.pointerEvents = 'none';
        const target = typeof document.elementFromPoint === 'function'
            ? document.elementFromPoint(event.clientX, event.clientY)
            : null;
        preview.style.pointerEvents = 'auto';

        const scrollHost = findScrollableAncestor(target)
            || findScrollHostBySelectors(target, fallbackSelectors);
        if (scrollHost) {
            scrollHost.scrollTop += event.deltaY;
            if (event.cancelable) event.preventDefault();
            return;
        }

        if (typeof window.scrollBy === 'function') {
            window.scrollBy(0, event.deltaY);
            if (event.cancelable) event.preventDefault();
        }
    }

    /* Every caller builds the popup card with `Cards.createCardElement` and
       bails when it is missing, so the module that owns `applyPreviewScale` is
       always loaded by the time this runs. There used to be a local copy of the
       enlargement here as a partial-load fallback; it was unreachable, and it
       silently kept the transform model after the shared one moved to physical
       sizing — a second, blurrier definition of the same behaviour. One
       implementation, in one place; if it is genuinely absent the popup opens
       at rest size, which is visibly degraded rather than quietly wrong. */
    /* `sourceCard` is the card the popup enlarges: the popup adopts its
       description (size step + lines) so the text never re-wraps between the
       resting card and its enlargement (Cards: ENGRAVED DESCRIPTIONS). */
    function applyStableHoverPreviewScale(cardEl, variant, anchorRect, preview, sourceCard = null) {
        Cards?.applyPreviewScale?.(
            cardEl,
            variant,
            anchorRect,
            preview,
            sourceCard ? { ...HOVER_PREVIEW_OPTIONS, sourceCard } : HOVER_PREVIEW_OPTIONS,
        );
    }

    function getRecipeParentsForCard(cardId) {
        const card = getCachedCard(cardId);
        const parentsFromCard = Array.isArray(card?.recipeParents) ? card.recipeParents : null;
        if (parentsFromCard && parentsFromCard.length === 2) return parentsFromCard;
        const parentsFromRecipe = recipeByResultCardId.get(cardId) || null;
        if (parentsFromRecipe && parentsFromRecipe.length === 2) return parentsFromRecipe;
        return null;
    }

    function getOwnedNormalQty(cardId) {
        return ownedCardMap.get(cardId) || 0;
    }

    function getOwnedStellarQty(cardId) {
        return ownedCardStellarMap.get(cardId) || 0;
    }

    function getOwnedTotalQty(cardId) {
        return getOwnedNormalQty(cardId) + getOwnedStellarQty(cardId);
    }

    function isNonCraftableCard(card) {
        if (!card) return false;
        if (CardEligibility?.isNonCraftableCard) {
            return CardEligibility.isNonCraftableCard(card);
        }
        const type = String(card.type || '').toLowerCase();
        if (type !== 'character') return false;
        if (card.isForged === true) return false;
        if (String(card.collection || '') === 'Moon Foundry') return false;
        return true;
    }

    function getRecipeKey(parent1Id, parent2Id) {
        if (!parent1Id || !parent2Id) return '';
        return [parent1Id, parent2Id].sort().join('|');
    }

    async function loadRecipeDetails(parent1Id, parent2Id) {
        if (!parent1Id || !parent2Id) return null;
        if (!MoonfallCloud?.convex) return null;

        const key = getRecipeKey(parent1Id, parent2Id);
        if (!key) return null;

        if (recipeDetailsByKey.has(key)) {
            return await recipeDetailsByKey.get(key);
        }

        const request = MoonfallCloud.convex.query('foundry:getRecipeDetails', {
            parent1Id,
            parent2Id,
        }).catch((err) => {
            console.warn('[Collection] Failed to load recipe details:', err);
            return null;
        });

        recipeDetailsByKey.set(key, request);
        return await request;
    }

    function getStellarAsset(cardId) {
        if (!StellarAssets) return null;
        return StellarAssets.get(cardId);
    }

    async function loadStellarAssets(cardIds = null) {
        if (!StellarAssets) return;
        const ids = Array.isArray(cardIds) ? cardIds : getTrackedStellarCardIds();
        const requests = [];
        if (ids.length > 0 && StellarAssets.load) {
            requests.push(StellarAssets.load(ids));
        }
        if (StellarAssets.loadMyPendingClaim) {
            requests.push(StellarAssets.loadMyPendingClaim());
        }
        if (requests.length === 0) return;

        try {
            await Promise.all(requests);
        } catch (err) {
            console.warn('[Collection] Failed to load stellar assets:', err);
        }
        // The account's in-flight Stellar claim was just re-read: an open
        // detail's Stellar button may have been waiting on it.
        refreshDetailCraftRow();
        scheduleStellarPoll();
    }

    function attachStellarVideosToGrid() {
        if (!Cards?.createCardElement) return;
        const grid = document.getElementById('collection-cards-grid');
        if (!grid) return;
        // The committed page only: a tile settling or built ahead in a hidden
        // stage gets its loop when it is swapped in, not while nobody sees it.
        grid.querySelectorAll(':scope > .collection-card-item .stellar-card').forEach(cardEl => {
            if (cardEl.querySelector('.stellar-loop-video')) return;
            const cardId = cardEl.dataset.instanceId || cardEl.dataset.cardId;
            if (!cardId) return;
            // Its chip still says what it was built for: it is rebuilt, not decorated.
            if (stellarTileIsOutdated(cardEl.closest('.collection-card-item'))) return;
            const asset = getStellarAsset(cardId);
            if (!asset?.videoUrl && !asset?.videoWebmUrl) return;
            const imageEl = cardEl.querySelector('.card-image');
            if (!imageEl) return;
            // Build minimal card data for attachStellarVideo
            const cardData = { isStellar: true, type: cardEl.dataset.type || 'Character' };
            StellarAssets.applyMediaToCard(cardData, asset);
            if (Cards.attachStellarVideo) {
                Cards.attachStellarVideo(cardEl, imageEl, cardData);
            }
            if (Cards.autoPlayStellarVideo) {
                Cards.autoPlayStellarVideo(cardEl);
            }
            refreshTileSignature(cardEl.closest('.collection-card-item'));
        });
    }

    /*
     * THE PAGE BEING LEFT STOPS ITS STELLAR CLIPS (the owner, 28 September
     * 2026: "if there is a loading bar, it should always load just the
     * minimum"). Every Stellar tile on screen downloads its clips in full
     * (cards.js attachStellarVideo: the loop, and the summon and cast films
     * the grid never plays). A turn onto a page whose pictures must still
     * download stops the clips of the page it leaves that are not playing:
     * none of them is seen (a loop shows only while it plays, cards.css), and
     * the page asked for gets the link. On the WebKit phone seat (EU 4G) a
     * page committed at once from the look-ahead and left 100 ms later kept
     * six films downloading, and the page after it landed at 6.2-7.1 s
     * against 4.9-5.3 s where the page left had never been shown (round 17
     * journeys). Its tiles get their clips again when they are back on screen
     * (attachStellarVideosToGrid: at a commit, or a turn ended on the page on
     * screen).
     */
    function stopUnplayedStellarClips(container) {
        container?.querySelectorAll?.(':scope > .collection-card-item video.stellar-video').forEach((video) => {
            if (video.dataset.playing === 'true') return;
            try { video.pause(); } catch (_) { }
            // No source, then load(): the fetch in flight is aborted.
            video.removeAttribute('src');
            try { video.load(); } catch (_) { }
            video.remove();
        });
    }

    /**
     * A tile decorated in place (its Stellar loop attached, its ribbon added)
     * now looks like a tile built with that state, so it says so: otherwise
     * the next refresh would take the old signature for a change and rebuild
     * a tile that is already right, restarting its loop.
     */
    function refreshTileSignature(tile) {
        const entry = tile ? tileEntries.get(tile) : null;
        if (!entry) return;
        // Restated as current, a tile showing a print's old state would never be rebuilt.
        if (stellarTileIsOutdated(tile)) return;
        tile.dataset.tileSignature = cardTileSignature(entry, publishedCardsScale(document.getElementById('collection-cards-grid')));
    }

    /*
     * A STELLAR TILE SHOWS ITS PRINT AS IT WAS WHEN THE TILE WAS BUILT:
     * attuning, failed, or neither. Not its estimate, which drifts without
     * rebuilding anything (the detail patches its own), and not its clips: a
     * tile built before its print was read takes them in place
     * (attachStellarVideosToGrid). A print moves on whoever reads it — this
     * grid's poll, its open, a card detail, another tab — and the store says
     * so (stellar-assets.js, `moonfall:stellar-assets-changed`). A tile built
     * for a state the print has left is rebuilt: the owner's Sage Apprentice
     * kept "Attuning" on the grid after its print had finished (21 October
     * 2026, production), because only the poll's own read rebuilt a tile, and
     * only while the grid was on screen.
     */
    function stellarTileKey(stellarState) {
        return [stellarState.isGenerating, stellarState.isFailed, stellarState.canRetry];
    }

    function stellarTileIsOutdated(tile) {
        if (tile?.dataset?.variant !== 'stellar' || !tile.dataset.cardId) return false;
        return tile.dataset.stellarKey !== JSON.stringify(stellarTileKey(getStellarState(tile.dataset.cardId)));
    }

    function gridShowsOutdatedStellarTile(grid = document.getElementById('collection-cards-grid')) {
        return Array.from(grid?.children || []).some((child) => (
            child.classList?.contains('collection-card-item') && stellarTileIsOutdated(child)
        ));
    }

    /* Once per task, after the read or the commit that asked: a settled grid
       on screen rebuilds the tiles whose prints have moved. A grid off screen
       follows when it is shown again (closeCardDetail, the Cards tab's
       return, the next open); a render under way, when it settles. */
    let stellarFollowQueued = false;

    function followStellarPrints() {
        if (stellarFollowQueued) return;
        stellarFollowQueued = true;
        setTimeout(() => {
            stellarFollowQueued = false;
            if (!isOpen || !cardsGridOnScreen() || lastCardsRequestId !== settledCardsRequestId) return;
            if (gridShowsOutdatedStellarTile()) void renderCards();
        }, 0);
    }

    function createStellarAssetSnapshot(cardId) {
        const asset = getStellarAsset(cardId);
        if (!asset) return null;
        return JSON.stringify({
            status: asset.status || null,
            queueStatus: asset.queueStatus || null,
            videoUrl: asset.videoUrl || null,
            videoWebmUrl: asset.videoWebmUrl || null,
            audioUrl: asset.audioUrl || null,
            audioWebmUrl: asset.audioWebmUrl || null,
            error: asset.lastError || asset.error || null,
        });
    }

    function createStellarSnapshotMap(cardIds) {
        return new Map((cardIds || []).map((cardId) => [cardId, createStellarAssetSnapshot(cardId)]));
    }

    function hasStellarSnapshotChanges(beforeSnapshot, afterSnapshot) {
        const ids = new Set([
            ...Array.from(beforeSnapshot?.keys?.() || []),
            ...Array.from(afterSnapshot?.keys?.() || []),
        ]);
        for (const cardId of ids) {
            if ((beforeSnapshot?.get(cardId) || null) !== (afterSnapshot?.get(cardId) || null)) {
                return true;
            }
        }
        return false;
    }

    function scheduleStellarPoll() {
        if (stellarPollTimer) {
            clearTimeout(stellarPollTimer);
            stellarPollTimer = null;
        }
        if (!isOpen || !StellarAssets) return;

        const pendingIds = getTrackedStellarCardIds()
            .filter((id) => StellarAssets.isGenerating(id));
        if (pendingIds.length === 0) return;

        stellarPollTimer = setTimeout(async () => {
            const detailCardId = activeDetailCardId;
            const detailVariant = activeDetailVariant;
            const detailAssetBefore = detailCardId ? getStellarAsset(detailCardId) : null;
            const detailStatusSnapshotBefore = detailCardId ? createStellarDetailStatusSnapshot(detailAssetBefore) : null;
            const assetSnapshotBefore = createStellarSnapshotMap(pendingIds);

            await loadStellarAssets(pendingIds);

            const detailAssetAfter = detailCardId ? getStellarAsset(detailCardId) : null;
            const detailStatusSnapshotAfter = detailCardId ? createStellarDetailStatusSnapshot(detailAssetAfter) : null;
            const assetSnapshotAfter = createStellarSnapshotMap(pendingIds);
            const detailNeedsFullRender = detailCardId
                && detailVariant === 'stellar'
                && shouldFullyRerenderStellarDetail(detailAssetBefore, detailAssetAfter);
            const detailNeedsStatusPatch = detailCardId
                && detailVariant === 'stellar'
                && !detailNeedsFullRender
                && detailStatusSnapshotBefore !== detailStatusSnapshotAfter;
            const assetsChanged = hasStellarSnapshotChanges(assetSnapshotBefore, assetSnapshotAfter);

            if (detailNeedsFullRender && activeDetailCardId === detailCardId) {
                await renderCardDetail(detailCardId, detailVariant);
            } else if (detailNeedsStatusPatch && activeDetailCardId === detailCardId) {
                refreshActiveStellarDetailStatus(detailCardId);
            }
            if (assetsChanged) {
                void refreshFirstDiscoveryBadges().then(() => requestStellarFirstBanner());
                // A DATA change: the page cache would serve the page read
                // before it. The reconcile keeps every tile that is unchanged.
                // (A change another read saw first: followStellarPrints.)
                void renderCards(undefined, { refresh: true });
            }
        }, 5000);
    }

    function getStellarState(cardId) {
        if (!StellarAssets) {
            return { asset: null, isGenerating: false, isFailed: false, isReady: false, canRetry: false };
        }
        const asset = getStellarAsset(cardId);
        return {
            asset,
            isGenerating: asset ? StellarAssets.isGenerating(cardId) : false,
            isFailed: asset ? StellarAssets.isFailed(cardId) : false,
            isReady: asset ? StellarAssets.isReady(cardId) : false,
            canRetry: asset ? StellarAssets.canRetry(cardId) : false,
        };
    }

    /*
     * THE OPEN'S RIBBON READ STARTS WITH THE OPEN. The ribbons decide how the
     * first page's tiles are built (buildCardTile), which is why the lobby used
     * to read them at idle — on the server a walk of every card the player
     * owns, every lobby arrival (read audit R3, 26 September 2026). Read only
     * by the open, behind its profile read, they came a round trip later and
     * could land after the page, growing its ribbons on screen. So the open
     * asks in its first task, for the account this session signed in with
     * (GameState, main.js), beside its profile read; the profile then confirms
     * that account, and its own read joins this one instead of repeating it.
     */
    let firstDiscoveryRead = null; // { profileId, generation, startedAt, promise }
    // The open whose read the ribbons came from (a later open reads them again).
    let firstDiscoveryBadgesGeneration = 0;

    function startFirstDiscoveryRead(generation) {
        const account = Firebase?.getUser?.()?.uid || null;
        if (!account) return;
        const session = GameState?.getUser?.();
        const expected = profileId && profileFirebaseUid === account
            ? profileId
            : (session?.id && session.firebaseUid === account ? String(session.id) : null);
        if (!expected) return;
        void refreshFirstDiscoveryBadges({ generation, expectedProfileId: expected, provisional: true }).then((updated) => {
            if (updated && collectionSessionIsCurrent(generation)) applyFirstDiscoveryDecorations();
        });
    }

    /**
     * `join`: this open's read for the same account, if one is out or done, is
     * the answer. `provisional`: asked before the profile named the account,
     * so it is kept while none is named yet (readProfile confirms or drops it).
     */
    async function refreshFirstDiscoveryBadges({
        generation = openGeneration,
        expectedProfileId = profileId,
        join = false,
        provisional = false,
    } = {}) {
        if (!expectedProfileId || !MoonfallCloud?.convex?.query) return false;
        const shared = join
            && firstDiscoveryRead?.profileId === expectedProfileId
            && firstDiscoveryRead.generation === generation
            ? firstDiscoveryRead
            : null;
        if (!shared && join
            && firstDiscoveryBadgesProfileId === expectedProfileId
            && firstDiscoveryBadgesGeneration === generation) return false;
        let read = shared;
        if (!read) {
            read = {
                profileId: expectedProfileId,
                generation,
                startedAt: Date.now(),
                promise: Promise.resolve().then(() => MoonfallCloud.convex.query(
                    'foundry:getMyFirstDiscoveryBadges',
                    { userId: expectedProfileId },
                )),
            };
            firstDiscoveryRead = read;
            const clear = () => {
                if (firstDiscoveryRead === read) firstDiscoveryRead = null;
            };
            read.promise.then(clear, clear);
        }
        try {
            const result = await read.promise;
            if (
                !collectionSessionIsCurrent(generation)
                || (profileId !== expectedProfileId && !(provisional && profileId === null))
            ) return false;
            firstDiscoveryBadges = {
                fuse: new Set(result?.fuseCardIds || []),
                stellar: new Set(result?.stellarFirstCardIds || []),
            };
            firstDiscoveryBadgesProfileId = expectedProfileId;
            firstDiscoveryBadgesGeneration = generation;
            return true;
        } catch (err) {
            if (!collectionSessionIsCurrent(generation)) return false;
            // The open's first read failed: its profile's asks once more of its own.
            if (shared) return refreshFirstDiscoveryBadges({ generation, expectedProfileId });
            console.warn('[Collection] Failed to load first-discovery badges:', err);
            return false;
        }
    }

    /*
     * A FIRST OPEN'S PAGE WAITS FOR ITS RIBBONS. The read above starts with
     * the press and usually lands while the page is built; one that landed
     * after the build grew its ribbons on tiles already on screen (review of
     * read audit F9(b), 27 September 2026). So a page showing a card the
     * server can ribbon — one this player forged or dreamt, or a Stellar
     * print not crafted by another (convex/foundry.ts
     * getMyFirstDiscoveryBadges) — is committed once
     * that read has answered, capped from the read's start at the
     * doctrine's 1.5 s readiness gate. Only the account's first read
     * is waited for: a later one decorates in place whatever changed, and a
     * page no ribbon can be on waits for nothing.
     */
    const FIRST_DISCOVERY_JOIN_CAP_MS = 1500;

    function mayCarryFirstDiscovery(entry) {
        const card = entry?.card;
        if (!card) return false;
        if (entry.variant === 'stellar') return !stellarCraftedByAnother(card.id);
        // Any forged card: the page answer does not name its author.
        return card.isForged === true
            || card.collection === 'Moon Foundry'
            || String(card.origin || '').toLowerCase() === 'moon_dream'
            || String(card.collection || '').toLowerCase() === 'moon dream';
    }

    /* A Stellar ribbon is its crafter's: the server gives it only to the
       player whose Stellar it is. The print's asset, read with its page
       (warmStellarMediaFor), names that crafter, so a print whose Stellar
       another player crafted can carry none. Measured on the veteran account
       (27 September 2026, Firefox desktop and WebKit phone): its page 1 shows
       two such prints and no ribbon, and waiting for the read anyway made its
       first open 130-330 ms slower. An asset not read yet, or a name either
       side lacks, rules nothing out. */
    function stellarCraftedByAnother(cardId) {
        const crafter = String(getStellarAsset(cardId)?.crafterUsername || '').trim().toLowerCase();
        const player = String(profileUsername || GameState?.getUser?.()?.username || '').trim().toLowerCase();
        return Boolean(crafter && player && crafter !== player);
    }

    /** The ribbon read a page of `entries` is committed after (capped), or null when it waits for none. */
    function firstDiscoveryJoinFor(entries, generation) {
        const read = firstDiscoveryRead;
        if (!read || read.generation !== generation || firstDiscoveryBadgesProfileId !== null) return null;
        if (!(entries || []).some(mayCarryFirstDiscovery)) return null;
        // Past its cap it holds nothing (a page turn still swaps in the press's own task).
        const left = FIRST_DISCOVERY_JOIN_CAP_MS - (Date.now() - read.startedAt);
        if (left <= 0) return null;
        let capTimer = null;
        // Its answer is adopted (refreshFirstDiscoveryBadges) before this settles: that await came first.
        return Promise.race([
            read.promise.then(() => undefined, () => undefined),
            new Promise((resolve) => { capTimer = setTimeout(resolve, left); }),
        ]).then(() => clearTimeout(capTimer));
    }

    // Fetch-free decoration of already-rendered tiles: mirrors the badge
    // markup renderCards() produces, for when the badge sets resolve after
    // the grid painted.
    function applyFirstDiscoveryDecorations() {
        const grid = document.getElementById('collection-cards-grid');
        if (!grid) return;
        grid.querySelectorAll('[data-card-id]').forEach((tile) => {
            const cardId = tile.dataset.cardId;
            const isStellarVariant = tile.dataset.variant === 'stellar';
            const isFirst = isStellarVariant
                ? firstDiscoveryBadges.stellar.has(cardId)
                : firstDiscoveryBadges.fuse.has(cardId);
            tile.classList.toggle('collection-card-item-first', isFirst);
            const existingBadge = tile.querySelector('.collection-first-badge');
            if (!isFirst) {
                MoonfallDiscoveryPalette?.clearFrom(tile);
                if (existingBadge) existingBadge.remove();
                refreshTileSignature(tile);
                return;
            }
            MoonfallDiscoveryPalette?.applyTo(tile, cardId, isStellarVariant ? 'stellar' : undefined);
            if (!existingBadge) {
                const badge = document.createElement('div');
                badge.className = isStellarVariant
                    ? 'collection-first-badge collection-first-badge--stellar'
                    : 'collection-first-badge';
                badge.textContent = isStellarVariant
                    ? uiText('collection.badges.firstStellar', 'First Stellar')
                    : uiText('collection.badges.firstDiscovery', 'First Discovery');
                tile.appendChild(badge);
            }
            refreshTileSignature(tile);
        });
    }

    /**
     * Support data (character names) arrives after the grid has painted, and
     * the only thing the grid derives from it is the Spell owner badge's label.
     * Patch that text on the committed tiles — same contract as the first-
     * discovery decoration above. A full re-render would refetch the page and
     * destroy content the player is already reading.
     */
    function applyCardsSupportDecorations() {
        const grid = document.getElementById('collection-cards-grid');
        if (grid) {
            grid.querySelectorAll('.collection-card-owner-badge[data-character-key]').forEach((badge) => {
                const label = getCharacterLabel(badge.dataset.characterKey);
                if (!label || badge.textContent === label) return;
                badge.textContent = label;
                badge.title = uiText('collection.detail.ownerTitle', 'Summoner: {{owner}}', { owner: label });
            });
        }
        updateSpellOwnerFilterOptions();
    }

    function requestStellarFirstBanner({
        generation = openGeneration,
        expectedProfileId = profileId,
        expectedFirebaseUid = profileFirebaseUid,
    } = {}) {
        if (!expectedProfileId || !StellarFirstBannerDelivery) {
            return Promise.resolve(false);
        }
        const currentFirebaseUid = Firebase?.getUser?.()?.uid || null;
        if (expectedFirebaseUid && currentFirebaseUid !== expectedFirebaseUid) {
            return Promise.resolve(false);
        }
        return StellarFirstBannerDelivery.request({
            requesterId: 'collection',
            profileId: expectedProfileId,
            isIdentityCurrent: () => (
                profileId === expectedProfileId
                && (
                    !expectedFirebaseUid
                    || Firebase?.getUser?.()?.uid === expectedFirebaseUid
                )
            ),
            isActive: () => (
                collectionSessionIsCurrent(generation)
                && profileId === expectedProfileId
                && (
                    !expectedFirebaseUid
                    || Firebase?.getUser?.()?.uid === expectedFirebaseUid
                )
            ),
            resolveImageUrl: (cardId) => cardById.get(cardId)?.image || null,
            onPresented: (cardId) => {
                if (profileId !== expectedProfileId) return;
                firstDiscoveryBadges.stellar.add(cardId);
                if (isOpen && activeSection === 'cards') {
                    applyFirstDiscoveryDecorations();
                }
            },
        });
    }

    function getTrackedStellarCardIds() {
        const ids = new Set(Array.from(ownedCardStellarMap.keys()));
        const ownPendingCardId = StellarAssets?.getMyPendingClaimCardId?.();
        if (ownPendingCardId) ids.add(ownPendingCardId);
        if (StellarAssets?.listPendingCardIds) {
            StellarAssets.listPendingCardIds().forEach((cardId) => {
                if (cardId) ids.add(cardId);
            });
        }
        if (activeDetailVariant === 'stellar' && activeDetailCardId) {
            ids.add(activeDetailCardId);
        }
        return Array.from(ids).filter(Boolean);
    }

    // Returns the cardId of the user's currently in-flight stellar generation, if any.
    // Mirrors the server-side per-user serial gate (one stellar at a time per user).
    function getPendingStellarCardId() {
        return StellarAssets?.getMyPendingClaimCardId?.() || null;
    }

    function formatCountdown(msRemaining) {
        const totalSeconds = Math.max(0, Math.floor(msRemaining / 1000));
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        return `${minutes}:${String(seconds).padStart(2, '0')}`;
    }

    function formatEtaValue(msRemaining, { roundUp = false } = {}) {
        if (!Number.isFinite(msRemaining) || msRemaining <= 0) return null;
        const roundedMs = roundUp ? Math.ceil(msRemaining / 1000) * 1000 : msRemaining;
        if (roundedMs < 60 * 1000) {
            return roundUp
                ? uiText('common.oneMinute', '1 min')
                : uiText('common.underMinute', 'under a minute');
        }

        const totalMinutes = roundedMs / (60 * 1000);
        if (totalMinutes < 60) {
            const rounded = roundUp ? Math.ceil(totalMinutes) : Math.max(1, Math.floor(totalMinutes));
            return uiText('common.minutesShort', `${rounded} min`, { count: rounded });
        }

        const totalHours = totalMinutes / 60;
        const rounded = roundUp ? Math.ceil(totalHours) : Math.max(1, Math.floor(totalHours));
        return uiText('common.hoursShort', `${rounded}h`, { count: rounded });
    }

    function formatEtaWindowText(asset, { includeAbout = true } = {}) {
        if (!asset) return '';
        const minLabel = formatEtaValue(asset.estimatedWaitMinMs);
        const maxLabel = formatEtaValue(asset.estimatedWaitMaxMs, { roundUp: true });

        if (minLabel && maxLabel && minLabel !== maxLabel) {
            return uiText(
                includeAbout ? 'common.aboutRange' : 'common.durationRange',
                includeAbout ? 'about {{min}}–{{max}}' : '{{min}}–{{max}}',
                { min: minLabel, max: maxLabel }
            );
        }
        if (maxLabel) {
            return includeAbout
                ? uiText('common.aboutDuration', 'about {{duration}}', { duration: maxLabel })
                : maxLabel;
        }
        if (minLabel) {
            return includeAbout
                ? uiText('common.aboutDuration', 'about {{duration}}', { duration: minLabel })
                : minLabel;
        }
        return '';
    }

    function buildStellarFlavorStatus(stellarState) {
        const asset = stellarState?.asset || null;
        const etaText = formatEtaWindowText(asset);

        if (stellarState?.isReady) {
            return uiText('collection.stellar.ready', 'This card bears a stable stellar form.');
        }

        if (stellarState?.isFailed) {
            return uiText('collection.stellar.failed', 'The Stellar Crucible lost the thread. Stardust refunded.');
        }

        if (!asset) {
            return uiText('collection.stellar.quiet', 'The Stellar Crucible is quiet.');
        }

        const queueStatus = String(asset.queueStatus || asset.status || '').toLowerCase();
        if (queueStatus === 'queued') {
            return etaText
                ? uiText(
                    'collection.stellar.queuedWithEta',
                    'The Stellar Crucible has taken this card into the moonlit forge. {{eta}}',
                    { eta: etaText }
                )
                : uiText('collection.stellar.queued', 'The Stellar Crucible has taken this card into the moonlit forge.');
        }

        if (queueStatus === 'running' || queueStatus === 'generating') {
            return etaText
                ? uiText(
                    'collection.stellar.runningWithEta',
                    "The Stellar Crucible is weaving this card's lunar echo. {{eta}}",
                    { eta: etaText }
                )
                : uiText('collection.stellar.running', "The Stellar Crucible is weaving this card's lunar echo.");
        }

        return etaText
            ? uiText('collection.stellar.tendingWithEta', 'The Stellar Crucible is tending this card. {{eta}}', { eta: etaText })
            : uiText('collection.stellar.tending', 'The Stellar Crucible is tending this card.');
    }

    function normalizeStellarLifecycle(asset) {
        const status = String(asset?.status || asset?.queueStatus || '').trim().toLowerCase();
        if (!status) return null;
        if (status === 'complete' || status === 'failed' || status === 'expired') {
            return status;
        }
        if (status === 'running' || status === 'generating') {
            return 'running';
        }
        if (status === 'queued' || status === 'pending' || status === 'prompt_pending') {
            return 'queued';
        }
        return status;
    }

    function createStellarDetailStatusSnapshot(asset) {
        if (!asset) return null;
        return JSON.stringify({
            lifecycle: normalizeStellarLifecycle(asset),
            queueStatus: asset.queueStatus || null,
            estimatedWaitMinMs: asset.estimatedWaitMinMs || null,
            estimatedWaitMaxMs: asset.estimatedWaitMaxMs || null,
            error: asset.lastError || asset.error || null,
        });
    }

    function shouldFullyRerenderStellarDetail(beforeAsset, afterAsset) {
        const beforeLifecycle = normalizeStellarLifecycle(beforeAsset);
        const afterLifecycle = normalizeStellarLifecycle(afterAsset);
        if (beforeLifecycle !== afterLifecycle) {
            const terminal = new Set(['complete', 'failed', 'expired']);
            if (terminal.has(beforeLifecycle) || terminal.has(afterLifecycle)) {
                return true;
            }
        }
        return (beforeAsset?.videoUrl || null) !== (afterAsset?.videoUrl || null)
            || (beforeAsset?.videoWebmUrl || null) !== (afterAsset?.videoWebmUrl || null)
            || (beforeAsset?.audioUrl || null) !== (afterAsset?.audioUrl || null)
            || (beforeAsset?.audioWebmUrl || null) !== (afterAsset?.audioWebmUrl || null);
    }

    function buildStellarStatusSection(stellarState) {
        const status = document.createElement('div');
        status.className = 'collection-card-detail-status';
        const isAttuning = stellarState?.isGenerating === true;
        if (isAttuning) {
            status.classList.add('collection-card-detail-status-generating');
        } else if (stellarState?.isReady) {
            status.classList.add('collection-card-detail-status-ready');
        } else if (stellarState?.isFailed) {
            status.classList.add('collection-card-detail-status-failed');
        }

        const statusHeader = document.createElement('div');
        statusHeader.className = 'collection-card-detail-status-header';

        if (isAttuning) {
            const progress = document.createElement('div');
            progress.className = 'collection-card-detail-status-progress';
            progress.setAttribute('role', 'status');
            progress.setAttribute('aria-label', uiText('collection.detail.stellarInProgress', 'Stellar attunement in progress'));
            const spinner = document.createElement('div');
            spinner.className = 'collection-card-detail-status-spinner';
            progress.appendChild(spinner);
            statusHeader.appendChild(progress);
        }

        const statusTitle = document.createElement('div');
        statusTitle.className = 'collection-card-detail-status-title';
        statusTitle.textContent = uiText('collection.stellar.statusTitle', 'Stellar Status');
        statusHeader.appendChild(statusTitle);
        status.appendChild(statusHeader);

        const statusText = document.createElement('div');
        statusText.className = 'collection-card-detail-status-text';
        statusText.textContent = buildStellarFlavorStatus(stellarState);
        status.appendChild(statusText);

        if (isAttuning) {
            const etaText = formatEtaWindowText(stellarState?.asset);
            if (etaText) {
                const etaLine = document.createElement('div');
                etaLine.className = 'collection-card-detail-status-eta';
                etaLine.textContent = etaText;
                status.appendChild(etaLine);
            }
        }

        return status;
    }

    function refreshActiveStellarDetailStatus(cardId) {
        if (!cardId || activeDetailCardId !== cardId || activeDetailVariant !== 'stellar') return;
        const detail = document.getElementById('collection-card-detail');
        if (!detail || detail.style.display === 'none') return;
        const existing = detail.querySelector('.collection-card-detail-status');
        if (!existing) return;
        const next = buildStellarStatusSection(getStellarState(cardId));
        existing.className = next.className;
        existing.innerHTML = next.innerHTML;
    }

    function buildStellarCraftMessage(cardName, asset) {
        const etaText = formatEtaWindowText(asset);
        if (etaText) {
            return uiText(
                'collection.stellar.craftWithEta',
                '{{cardName}} is in your collection. The Stellar Crucible has claimed it. {{eta}}',
                { cardName, eta: etaText }
            );
        }
        return uiText(
            'collection.stellar.craft',
            '{{cardName}} is in your collection. The Stellar Crucible has claimed it.',
            { cardName }
        );
    }

    function getCardLabel(cardId) {
        const match = getCachedCard(cardId);
        return match?.name || uiText('collection.detail.fallbackTitle', 'Card');
    }

    function ensureToastHost() {
        if (toastHost && toastHost.isConnected) return toastHost;
        const host = document.createElement('div');
        host.className = 'collection-toast-host';
        document.body.appendChild(host);
        toastHost = host;
        return host;
    }

    function showToast(message, tone = 'info') {
        if (!message) return;
        const host = ensureToastHost();
        if (!host) {
            alert(message);
            return;
        }
        const toast = document.createElement('div');
        toast.className = `collection-toast collection-toast-${tone}`;
        toast.textContent = message;
        host.appendChild(toast);
        setTimeout(() => toast.classList.add('collection-toast-exit'), 3500);
        setTimeout(() => {
            if (toast.parentNode) toast.parentNode.removeChild(toast);
        }, 3900);
    }

    /**
     * What the committed grid is a picture OF. A tab return with an unchanged
     * signature has nothing to re-fetch and nothing to rebuild — and rebuilding
     * it anyway is what the player reads as "it reloads for no reason".
     */
    function cardsRenderSignature() {
        return JSON.stringify([
            activeCardTab,
            cardsPage,
            cardsPageSize,
            currentSearch,
            currentFilters,
            activeSpellOwnerFilter,
        ]);
    }

    /**
     * What the committed page is ABOUT, with capacity left out.
     *
     * A different tab, page, search, filter, spell owner or sort makes the
     * cards on screen the wrong cards (a sort puts other cards on page 1: it
     * was left out, so a sort's page took no loading state however long it
     * took, and the page kept on screen read as its answer). A different
     * `cardsPageSize` does not: they are still the right cards, there may
     * simply be room for more. That distinction is what stopped the grid
     * blinking back to skeletons on a tab return — `showSection` re-measures
     * the page size in the same tick it un-hides the section, and a one-off
     * wobble in that measurement invalidated the whole committed page.
     */
    function cardsContentSignature() {
        return JSON.stringify([
            activeCardTab,
            cardsPage,
            currentSearch,
            currentFilters,
            activeSpellOwnerFilter,
            currentSort,
        ]);
    }

    /**
     * The content signature of the page currently PAINTED, which survives a
     * data-freshness invalidation.
     *
     * The background support-data batch (recipes, forge costs, characters)
     * calls invalidateCardsCache(), which nulls `committedCardsSignature`. That
     * is correct for refetching, but it also told the skeleton step it had
     * nothing on screen worth keeping — so ~1.2s after the player returned to
     * the Cards tab, a correct page was replaced by eight placeholders and then
     * rebuilt. The DOM is the truth about what is on screen; the cache is the
     * truth about how fresh it is, and only the first one decides placeholders.
     */
    let paintedCardsContentSignature = null;

    function committedCardsGridIsCurrent() {
        if (cardsNeedRemoteRefresh) return false;
        if (!committedCardsSignature) return false;
        if (committedCardsSignature !== cardsRenderSignature()) return false;
        const grid = document.getElementById('collection-cards-grid');
        // A print that moved while another tab was up: its tile is rebuilt on the way back.
        if (gridShowsOutdatedStellarTile(grid)) return false;
        return Boolean(grid?.querySelector(':scope > .collection-card-item:not(.skeleton-card)'));
    }

    function invalidateCardsCache() {
        // Do NOT reset cardsPage here. Page resets must be explicit
        // (open, filter/sort/tab/search change) so that side-channel cache
        // invalidations like loadRecipes() or handleInventoryUpdate() —
        // which run after crafting or remote inventory updates — preserve
        // the user's current page position.
        currentPageEntries = [];
        committedCardsSignature = null;
        // The page on screen answers the data as it was: a press back to it reads it again.
        shownCardsTurn = null;
        // The data changed: no page read before it may be served again.
        cardsPages.invalidate();
        pageDataEpoch += 1;
    }

    function replaceCardCache(cards) {
        cardById = new Map();
        allCards = [];
        upsertCardCache(cards);
    }

    function upsertCardCache(cards) {
        (cards || []).forEach((card) => {
            if (!card || !card.id) return;
            const existing = cardById.get(card.id) || {};
            const next = {
                ...existing,
                ...card,
            };
            cardById.set(card.id, next);
        });
        allCards = Array.from(cardById.values());
    }

    function getCachedCard(cardId) {
        if (!cardId) return null;
        return cardById.get(cardId) || null;
    }

    function applyOwnedQuantitySnapshot(card) {
        if (!card?.id) return;
        const hasNormalQty = Object.prototype.hasOwnProperty.call(card, 'ownedNormalQty');
        const hasStellarQty = Object.prototype.hasOwnProperty.call(card, 'ownedStellarQty');
        const ownedNormalQty = hasNormalQty ? (Number(card.ownedNormalQty) || 0) : (ownedCardMap.get(card.id) || 0);
        const ownedStellarQty = hasStellarQty ? (Number(card.ownedStellarQty) || 0) : (ownedCardStellarMap.get(card.id) || 0);

        if (hasNormalQty) {
            if (ownedNormalQty > 0) {
                ownedCardMap.set(card.id, ownedNormalQty);
            } else {
                ownedCardMap.delete(card.id);
            }
        }
        if (hasStellarQty) {
            if (ownedStellarQty > 0) {
                ownedCardStellarMap.set(card.id, ownedStellarQty);
            } else {
                ownedCardStellarMap.delete(card.id);
            }
        }
        if ((ownedNormalQty + ownedStellarQty) > 0 && !ownedCards.includes(card.id)) {
            ownedCards.push(card.id);
        }
        if (card.isDiscovered) {
            discoveredRecipeCardIds.add(card.id);
        }
    }

    function normalizeSearchText(value) {
        return String(value || '').trim().toLowerCase();
    }

    function setCurrentSearch(value) {
        if (value !== undefined) {
            currentSearch = normalizeSearchText(value);
        }
        return currentSearch;
    }

    function paginateList(items, pageSize, page) {
        if (Pagination?.paginate) {
            return Pagination.paginate(items, pageSize, page);
        }
        const list = Array.isArray(items) ? items : [];
        const size = Number(pageSize) > 0 ? Number(pageSize) : 1;
        const totalItems = list.length;
        const totalPages = Math.max(1, Math.ceil(totalItems / size));
        const currentPage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
        const startIndex = (currentPage - 1) * size;
        const endIndex = startIndex + size;
        return {
            totalItems,
            totalPages,
            page: currentPage,
            pageSize: size,
            startIndex,
            endIndex: Math.min(endIndex, totalItems),
            pageItems: list.slice(startIndex, endIndex),
        };
    }

    async function fetchCardDetails(cardIds, { refresh = false } = {}) {
        const uniqueIds = Array.from(new Set((cardIds || []).map((id) => String(id || '').trim()).filter(Boolean)));
        if (uniqueIds.length === 0) return [];

        const headers = await buildAuthHeaders();
        const params = new URLSearchParams({ cardIds: uniqueIds.join(',') });
        params.set('lang', i18n?.getLanguage?.() || 'en');
        if (refresh) params.set('refresh', '1');
        const response = await fetch(`/api/cards/details?${params.toString()}`, { headers });
        if (!response.ok) {
            throw new Error('Failed to fetch card details');
        }
        const data = await response.json();
        const list = Array.isArray(data) ? data : [];
        upsertCardCache(list);
        list.forEach(applyOwnedQuantitySnapshot);
        return list;
    }

    /**
     * Everything that decides WHICH cards a page holds. The account is part of
     * it: a page read for one player is never served to another.
     */
    function cardsQuery({ spellOwner = activeSpellOwnerFilter } = {}) {
        return {
            account: Firebase?.getUser?.()?.uid || '',
            tab: activeCardTab,
            spellOwner,
            collection: currentFilters.collection || '',
            mana: currentFilters.mana || '',
            sort: currentSort || 'cost-asc',
            search: currentSearch || '',
            lang: i18n?.getLanguage?.() || 'en',
        };
    }

    /** The query the NEXT open will ask (an open always starts on every summoner). */
    function openingCardsQuery() {
        const search = /** @type {HTMLInputElement | null} */ (document.getElementById('collection-search'));
        setCurrentSearch(search?.value || '');
        return cardsQuery({ spellOwner: 'all' });
    }

    async function fetchCardsPage(query, page, pageSize, { refresh = false, prefetch = false } = {}) {
        const headers = await buildAuthHeaders();
        // The account keys the cache; the server knows it from the credential.
        const params = new URLSearchParams(Object.entries(query || {})
            .filter(([name]) => name !== 'account')
            .map(([name, value]) => [name, String(value)]));
        params.set('page', String(page));
        params.set('pageSize', String(pageSize));
        if (refresh) params.set('refresh', '1');
        // A look-ahead never waits on a very large collection's index; the
        // page the player asked for does, as long as the Collection is open.
        const response = await fetchPageAnswer(`/api/collection/cards-page?${params.toString()}`, { headers },
            prefetch ? { maxWaitMs: 0 } : { wanted: () => isOpen, onWaiting: showCardsIndexWait });
        // A page still not reached when the wait ended (202) is no page either.
        if (!response.ok || response.status === 202) {
            throw new Error('Failed to fetch collection page');
        }
        return normalizePageAnswer(await response.json());
    }

    /* A VERY LARGE COLLECTION NOT READY TO ANSWER (card-scale, 21 October
       2026): its index being prepared, or a search still walking it. Past
       the wait's grace the grid says so, in place of what it showed, as
       "Failed to load cards." does; the page replaces it as it replaces any
       loading state. */
    function showCardsIndexWait(state) {
        const container = document.getElementById('collection-cards-grid');
        if (!container || !state) return;
        const message = state === 'searching'
            ? uiText('collection.cards.searching', 'Still searching your collection...')
            : uiText('collection.cards.preparing', 'Preparing your collection. A very large one can take a while.');
        dropCardsPrebuild();
        dropAllLookAheads();
        disposeCollectionExactImages(container);
        const notice = document.createElement('div');
        notice.className = 'collection-empty';
        notice.dataset.indexWait = state;
        notice.setAttribute('role', 'status');
        notice.textContent = message;
        container.replaceChildren(notice);
        container.setAttribute('aria-busy', 'true');
    }

    /* The page after the one on screen is the flow's next view: its pictures
       wait for the page on screen like any background warm, but go AHEAD of
       other surfaces' low warms already waiting (asset-preloader.ts rank). In
       round 14 (26 September 2026, WebKit phone probe) page 2 queued behind
       eleven Moon Exchange row pictures the catalog read had declared a moment
       earlier, and none of it had downloaded when Next was pressed. */
    const NEXT_PAGE_ART_RANK = -1;

    const PAGE_ART_WARMS = {
        high: { priority: 'high' },
        low: { priority: 'low' },
    };

    /*
     * WHICH PICTURES ARE IN HAND. A picture a warm of this surface has settled
     * (its bytes in the HTTP cache, or decoded for the page on screen) is no
     * longer on the wire — nor is one that failed. The open's hold reads this
     * to tell a page whose pictures are still downloading from one whose build
     * alone is left (presentPastGrace). Bounded: it answers for the pages
     * around the one on screen, not for the session.
     */
    const PICTURES_IN_HAND_CAP = 128;
    const picturesInHand = new Set();

    function notePicturesInHand(urls) {
        (urls || []).forEach((url) => {
            if (!url) return;
            picturesInHand.delete(url);
            picturesInHand.add(url);
        });
        while (picturesInHand.size > PICTURES_IN_HAND_CAP) {
            picturesInHand.delete(picturesInHand.values().next().value);
        }
    }

    /** The URL a card's picture is fetched by (asset-preloader.ts `cardSource`), when it is one. */
    function pictureUrlOf(card) {
        const source = card?.imageUrl || card?.picture || card?.image || '';
        return typeof source === 'string' && /^(https?:|data:|blob:|\/)/.test(source) ? source : null;
    }

    function pictureUrlsOf(entries) {
        return Array.from(new Set((entries || []).map((entry) => pictureUrlOf(entry?.card)).filter(Boolean)));
    }

    /*
     * A LOW WARM IS THIS SURFACE'S OWN, one picture at a time, so what became
     * of each is known: only a picture that arrived (or failed) is in hand.
     * Through the shared warm (AssetPreloader.warmCards) it was not — that
     * warm resolves when its pictures are CANCELLED too (a match start closes
     * the lobby's scopes) — so the lobby's page-1 pictures that never came
     * were counted in hand, and the next open was held unseen past the grace
     * as if only its build were left, its wells a second late (review of read
     * audit F9(f), 27 September 2026). A high warm settles each picture in the
     * foreground, which no scope cancels: its end is its pictures' arrival.
     */
    let pageWarmScope = null;

    function pageWarmScheduler() {
        const scheduler = lookAheadScheduler();
        if (!scheduler) return null;
        if (!pageWarmScope || pageWarmScope.closed) {
            pageWarmScope = scheduler.scope('collection-page-warm', { runsOn: LOOK_AHEAD_SCREENS });
        }
        return pageWarmScope;
    }

    /**
     * `priority`: 'high' (on screen) or 'low' (possible later). The page after
     * the one on screen ('next') is the look-ahead's (readNextPageAhead), which
     * asks for its pictures only once this page's have settled.
     */
    function warmPageArt(entries, priority = 'low', signal = null) {
        const options = PAGE_ART_WARMS[priority];
        const cards = (entries || []).map((entry) => entry?.card).filter(Boolean);
        if (!options || cards.length === 0) return undefined;
        const scope = priority === 'low' ? pageWarmScheduler() : null;
        if (scope) {
            const warms = pictureUrlsOf(entries).map((url) => Promise.resolve(scope.soon([url])).then((result) => {
                if (Number(result?.ready) > 0 || Number(result?.failed) > 0) notePicturesInHand([url]);
            }, () => {}));
            // A card named by an asset id is resolved and warmed by the shared warm.
            const named = cards.filter((card) => !pictureUrlOf(card));
            if (named.length > 0 && typeof AssetPreloader?.warmCards === 'function') {
                warms.push(Promise.resolve(AssetPreloader.warmCards(named, { ...options })).catch(() => {}));
            }
            return Promise.all(warms).then(() => undefined);
        }
        if (typeof AssetPreloader?.warmCards !== 'function') return undefined;
        const warm = AssetPreloader.warmCards(cards, { ...options, ...(signal ? { signal } : {}) });
        if (priority === 'high') {
            const urls = pictureUrlsOf(entries);
            Promise.resolve(warm).then(() => { if (!signal?.aborted) notePicturesInHand(urls); }, () => {});
        }
        return warm;
    }

    // Likely-next pages (docs/asset-loading.md law 3): the lobby warms the
    // first pages at the largest size the grid can show, and the pages around
    // page N are read while page N is on screen, so an open or a page turn
    // paints from memory instead of a cold waterfall. See collection-page-cache.js.
    // Room for the window around the page on screen (the page behind, the
    // three ahead), the other card tab's first page and the lobby's answer,
    // with a question changed once: about 5 KB an answer.
    const PAGE_ANSWERS_KEPT = 12;
    const cardsPages = createCollectionPageCache({
        fetchPage: fetchCardsPage,
        warmEntries: warmPageArt,
        maxEntries: PAGE_ANSWERS_KEPT,
    });

    async function ensureAuxiliaryCardsLoaded({ refresh = false } = {}) {
        const lang = i18n?.getLanguage?.() || 'en';
        if ((auxiliaryAllCards.length > 0 || auxiliaryCatalogComplete === false) && !refresh && auxiliaryCatalogLanguage === lang) {
            return auxiliaryAllCards;
        }
        // One /api/cards read at a time: the Exchange warm and the tab's own
        // entry ask for the same catalog, and share it — while it is still for
        // the account, inventory revision and language it was started for.
        const uid = Firebase?.getUser?.()?.uid || null;
        if (
            !refresh
            && auxiliaryCardsRequest
            && auxiliaryCardsRequest.revision === auxiliaryInventoryRevision
            && auxiliaryCardsRequest.uid === uid
            && auxiliaryCardsRequest.lang === lang
        ) return auxiliaryCardsRequest.promise;
        const promise = readAuxiliaryCards({ fresh: refresh });
        if (!refresh) {
            const request = { promise, revision: auxiliaryInventoryRevision, uid, lang };
            auxiliaryCardsRequest = request;
            const clear = () => {
                if (auxiliaryCardsRequest === request) auxiliaryCardsRequest = null;
            };
            promise.then(clear, clear);
        }
        return promise;
    }

    async function readAuxiliaryCards({ fresh = false } = {}) {
        const owner = profileId, firebaseUid = Firebase?.getUser?.()?.uid || null;
        const revision = auxiliaryInventoryRevision;
        /* The first read after the holdings changed (a sale, a buy, a craft, a
           pack) asks for a fresh build. The server shares one catalog build
           between the readers that arrive while it runs (read audit F5), so a
           plain read right after the change could be served one that started
           before it: the sold card still owned. */
        const refresh = fresh || auxiliaryInventoryStale;
        const language = i18n?.getLanguage?.() || 'en';
        const headers = await buildAuthHeaders();
        const lang = encodeURIComponent(language);
        const response = await fetch(`/api/cards?v=2&lang=${lang}${refresh ? '&refresh=1' : ''}`, { headers });
        if (!response.ok) {
            throw new Error('Failed to load auxiliary cards');
        }
        const data = await response.json();
        // `{ complete, cards }` or nothing: any other answer (a server still
        // on the old card list during a release) fails the read, which the
        // screens show and retry, instead of passing for an empty collection.
        if (!data || typeof data.complete !== 'boolean' || !Array.isArray(data.cards)) {
            throw new Error('Failed to load auxiliary cards');
        }
        // The profile ID can become known while this authenticated request is
        // in flight. That is still the same owner; an actual Firebase change,
        // established profile change or inventory invalidation is not.
        if (firebaseUid !== (Firebase?.getUser?.()?.uid || null)
            || (owner !== null && owner !== profileId)
            || revision !== auxiliaryInventoryRevision) throw new Error('Inventory changed while loading');
        // An answer in a language the player has since left names every card
        // in words no longer on screen: it is never kept or handed over, and
        // whoever waits on it gets the catalog in the player's language.
        if (language !== (i18n?.getLanguage?.() || 'en')) return ensureAuxiliaryCardsLoaded({ refresh: fresh });
        auxiliaryAllCards = data.cards;
        auxiliaryCatalogComplete = data.complete;
        auxiliaryCatalogLanguage = language;
        auxiliaryAllCards.forEach(applyOwnedQuantitySnapshot);
        auxiliaryInventoryStale = false;
        return auxiliaryAllCards;
    }

    /**
     * Hold the pager label at the width of its widest page for this count.
     * Prev and Next are `flex: 1` beside it, so a label that grew with its
     * digits ("Page 9" → "Page 10") shoved both buttons sideways. The numerals
     * are tabular, so the last page is the widest label of its digit count.
     */
    function reservePageInfoWidth(pageInfo, totalPages, totalsUnknown = false) {
        const key = `${totalPages}|${totalsUnknown ? 'open' : 'counted'}|${i18n?.getLanguage?.() || 'en'}`;
        if (!pageInfo?.parentElement || pageInfo.dataset.reservedFor === key) return;
        const probe = document.createElement('div');
        probe.className = pageInfo.className;
        probe.setAttribute('aria-hidden', 'true');
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;'
            + 'pointer-events:none;white-space:nowrap;min-width:0;width:auto;';
        probe.textContent = pageLabelFor(uiText, { page: totalPages, totalPages, totalsUnknown });
        pageInfo.parentElement.appendChild(probe);
        const width = Math.ceil(Number(probe.getBoundingClientRect?.().width) || 0);
        probe.remove();
        if (width <= 0) return;
        pageInfo.dataset.reservedFor = key;
        /* NEVER NARROWER THAN IT HAS BEEN. On a phone the pager shares the
           search row, so a label that narrows (a capped open's reserve giving
           way to "Page 1 of 9", a filter shortening the count) grows the
           search box under the player, and one that widens squeezes it. */
        const held = Number.parseFloat(pageInfo.style.minWidth) || 0;
        if (width > held) pageInfo.style.minWidth = `${width}px`;
    }

    /* A capped open presents before the count is known: hold the room a
       two-digit count takes, so the page landing never moves the row
       (measured 25 September 2026, WebKit phone: 54 px). */
    const PENDING_PAGE_RESERVE = 99;

    function updateCardsPagination(pagination) {
        cardsTotalPages = Number(pagination?.totalPages) || 0;
        const prevBtn = document.getElementById('collection-cards-prev');
        const nextBtn = document.getElementById('collection-cards-next');
        const pageInfo = document.getElementById('collection-cards-page-info');
        if (prevBtn) prevBtn.disabled = pagination.page <= 1;
        if (nextBtn) nextBtn.disabled = pagination.page >= pagination.totalPages;
        if (pageInfo) {
            reservePageInfoWidth(pageInfo, pagination.totalPages, pagination.totalsUnknown === true);
            pageInfo.textContent = pageLabelFor(uiText, pagination);
        }
        // Held back while the count was unknown (a capped open); it is known now.
        document.getElementById('collection-cards-pagination')?.classList.remove('collection-pagination--pending');
    }

    /**
     * Answer a press on the element pressed. `aria-busy` at once; the visible
     * busy state only once the wait is long enough to see, so a result that
     * lands within a few frames IS the answer and nothing flashes before it.
     * Returns the release.
     */
    function acknowledgePress(element, busyClass = 'btn-pending') {
        if (!element?.classList) return () => {};
        element.setAttribute('aria-busy', 'true');
        const timer = setTimeout(() => element.classList.add(busyClass), PRESS_ACK_DELAY_MS);
        return () => {
            clearTimeout(timer);
            element.removeAttribute('aria-busy');
            element.classList.remove(busyClass);
        };
    }

    /**
     * The same answer for a pressed CARD: a ring drawn around it (its own
     * element, so it sits outside the card's frame and never touches the
     * card's box) while its detail is finished behind the grid.
     */
    function acknowledgeCardPress(tile) {
        if (!tile?.classList) return () => {};
        const release = acknowledgePress(tile, 'collection-card-item--opening');
        let ring = null;
        const timer = setTimeout(() => {
            ring = document.createElement('span');
            ring.className = 'collection-card-opening-ring';
            ring.setAttribute('aria-hidden', 'true');
            tile.appendChild(ring);
        }, PRESS_ACK_DELAY_MS);
        return () => {
            clearTimeout(timer);
            ring?.remove();
            release();
        };
    }

    function triggerCraftCelebration(button) {
        if (!button) return;
        // Shared radial spark celebration (FX.burst replaced the local
        // craft-burst clone this originated from).
        FX?.burst?.(button);

        button.classList.remove('crafting');
        void button.offsetWidth;
        button.classList.add('crafting');
    }

    async function requestStellarRetry(cardId) {
        // Retry disabled temporarily due to rate limiting.
        return false;
        /*
        try {
            const idToken = await Firebase.getIdToken();
            if (!idToken) {
                showStatus('Authentication required.');
                return false;
            }

            const res = await fetch('/api/foundry/stellar/retry', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`,
                },
                body: JSON.stringify({ cardId }),
            });

            const result = await res.json().catch(() => ({}));
            if (!res.ok || result?.error) {
                showStatus(result?.error || 'Failed to retry stellar generation.');
                return false;
            }

            await loadStellarAssets([cardId]);
            if (activeDetailCardId === cardId) {
                await renderCardDetail(cardId, 'stellar');
            }
            renderCards(document.getElementById('collection-search')?.value || '');
            return true;
        } catch (err) {
            console.error('[Collection] Stellar retry failed:', err);
            showStatus('Failed to retry stellar generation.');
            return false;
        }
        */
    }

    function setupStellarAssetListeners() {
        if (stellarAssetListenersReady) return;
        stellarAssetListenersReady = true;

        window.addEventListener('moonfall:stellar-asset-ready', (event) => {
            if (!isOpen) return;
            const cardId = event?.detail?.cardId;
            const name = cardId ? getCardLabel(cardId) : uiText('collection.stellar.fallbackCard', 'Stellar card');
            showToast(uiText(
                'collection.stellar.readyToast',
                '{{cardName}} is ready. The Stellar Crucible has awakened its stellar form.',
                { cardName: name }
            ), 'success');
            // The one-at-a-time Stellar lock has just lifted.
            refreshDetailCraftRow();
        });

        window.addEventListener('moonfall:stellar-assets-changed', () => {
            // A closed Collection follows at its next open (pageAlreadyAnswers).
            if (isOpen) followStellarPrints();
        });

        window.addEventListener('moonfall:stellar-asset-failed', (event) => {
            /* A forging that fails takes its Stellar print back (the queue
               revokes it and refunds its Stardust): the holdings changed
               out of this screen's sight, open or not. */
            markHoldingsChanged();
            if (!isOpen) return;
            const cardId = event?.detail?.cardId;
            const name = cardId ? getCardLabel(cardId) : uiText('collection.stellar.fallbackCard', 'Stellar card');
            showToast(uiText(
                'collection.stellar.failedToast',
                '{{cardName}} slipped from the Stellar Crucible. Stardust refunded.',
                { cardName: name }
            ), 'error');
            // The lock lifted and the refund moved the balance: read it again.
            refreshDetailCraftRow();
            void followProfileBalance();
        });
    }

    /* THE BALANCE MOVES ON OTHER SURFACES — a Stardust purchase confirmed on
       the way back from checkout, a spend in the Foundry or a Moon Dream, the
       Store opened from this Collection's chip. Whoever learns the new figure
       announces it (stardust-chip.js); while the Collection is open it
       follows, so its chip and an open card detail's craft row never argue
       with the rest of the game. An announcement without a figure asks for
       a re-read. Nothing here announces in turn. */
    function setupStardustFollow() {
        if (stardustFollowReady) return;
        stardustFollowReady = true;
        onStardustAnnounced((announced) => {
            if (!isOpen) return;
            if (announced === null) {
                void followProfileBalance();
                return;
            }
            if (announced !== stardust) {
                stardust = announced;
                updateStardustDisplay();
                void syncAuxiliaryContexts();
            }
            refreshDetailCraftRow();
            // A held open waiting for its balance has it now.
            markCollectionPresentable('balanceReady');
        });
    }

    /* HOLDINGS CHANGE ON OTHER SURFACES — a Moon Dream deletion or a finished
       run's spells, a card the Foundry grants. Found 25 September 2026: the
       page cache presents a page read in the last minute, so the next Cards
       view showed the old holdings and then morphed when its revalidation
       landed. Whoever changes them announces `moonfall:holdings-changed`
       (moon-dream.js, foundry.js); every page read before it is stale. */
    function setupHoldingsFollow() {
        if (holdingsFollowReady) return;
        holdingsFollowReady = true;
        window.addEventListener('moonfall:holdings-changed', () => markHoldingsChanged());
    }

    async function followProfileBalance() {
        const loaded = await loadProfile();
        if (!loaded || !isOpen) return;
        updateStardustDisplay();
        refreshDetailCraftRow();
        void syncAuxiliaryContexts();
    }

    async function buildAuthHeaders() {
        if (Firebase?.getIdToken) {
            try {
                const token = await Firebase.getIdToken();
                if (token) {
                    return { Authorization: `Bearer ${token}` };
                }
            } catch (_) {
                return {};
            }
        }
        return {};
    }

    // A crafted summoner's key is a generated identifier, not a name. Base
    // summoner keys ARE their names, so those stay their own fallback.
    const OPAQUE_CHARACTER_KEY_RE = /^dream_/i;

    function getCharacterLabel(characterKey) {
        if (!characterKey) return '';
        const served = spellOwnerNamesByKey.get(characterKey);
        if (served) return served;
        const known = characterNameByKey.get(characterKey);
        if (known) return known;
        // Nothing can name it yet. Showing the raw identifier is worse than
        // showing nothing: it is unreadable, it breaks the control's width, and
        // it puts an internal id in front of a player.
        return OPAQUE_CHARACTER_KEY_RE.test(characterKey)
            ? uiText('collection.filters.craftedSummoner', 'Crafted summoner')
            : characterKey;
    }

    function getCharacterPortraitUrl(characterKey) {
        if (!characterKey) return '/assets/art_placeholder.webp';
        const portraitUrl = sanitizeImageUrlForDirectClient(characterPortraitByKey.get(characterKey));
        return portraitUrl || '/assets/art_placeholder.webp';
    }

    function sanitizeImageUrlForDirectClient(raw) {
        if (!raw || typeof raw !== 'string') return null;
        const value = raw.trim();
        if (!value) return null;
        try {
            let pathname = new URL(
                value,
                window.location?.href || 'http://localhost/',
            ).pathname;
            for (let count = 0; count < 4; count += 1) {
                const decoded = decodeURIComponent(pathname);
                if (decoded === pathname) break;
                pathname = decoded;
            }
            if (pathname === '/assets/art_placeholder.webp') return null;
        } catch (_) {
            return null;
        }
        if (value.startsWith('/api/card-image/')) return null;
        if (value.startsWith('http') || value.startsWith('data:') || value.startsWith('blob:')) {
            return value;
        }
        if (value.startsWith('/assets/') || value.startsWith('/img/') || value.startsWith('assets/') || value.startsWith('img/')) {
            return value;
        }
        return null;
    }

    function sanitizeCardbackImageUrl(raw) {
        const sharedSanitizer = CardbackArt?.sanitizeUrl;
        return typeof sharedSanitizer === 'function'
            ? sharedSanitizer(raw)
            : (sanitizeImageUrlForDirectClient(raw) || '');
    }

    function loadCollectionExactImage(image, rawSource, profile = {}) {
        if (!image) return false;
        Cards?.disposeCardArtImage?.(image);
        window.MoonfallRetryingImage?.dispose?.(image, { clearSource: true });
        delete image.dataset.collectionExactSource;
        const source = sanitizeImageUrlForDirectClient(rawSource);
        if (!source) {
            image.removeAttribute('src');
            image.classList.add('card-art-unavailable');
            return false;
        }

        image.dataset.collectionExactSource = source;
        const cards = Cards;
        if (typeof cards?.loadCardArtImage === 'function') {
            void cards.loadCardArtImage(image, source, profile, { directArtOnly: true });
            return true;
        }
        if (typeof window.MoonfallRetryingImage?.load === 'function') {
            window.MoonfallRetryingImage.load(image, source);
            return true;
        }

        // Isolated/legacy shells can load Collection without the shared owner.
        // Keep the declared source truthful there; the normal application path
        // above owns indefinite exact-source recovery.
        image.setAttribute('src', source);
        if (typeof cards?.enhanceCardArt === 'function') {
            void cards.enhanceCardArt(image, source, profile);
        }
        return false;
    }

    function disposeCollectionExactImages(root) {
        root?.querySelectorAll?.('img[data-collection-exact-source]').forEach((image) => {
            Cards?.disposeCardArtImage?.(image);
            window.MoonfallRetryingImage?.dispose?.(image);
        });
    }

    async function hydrateCardImagesDirectFromConvex(cards) {
        if (!Array.isArray(cards)) return;

        cards.forEach((card) => {
            if (!card || typeof card !== 'object') return;
            card.image = sanitizeImageUrlForDirectClient(card.image);
        });
    }

    function setupI18nListeners() {
        if (i18nListenersReady) return;
        i18nListenersReady = true;

        window.addEventListener('moonfall:i18nready', () => {
            applyCollectionTranslations();
            updateSpellOwnerFilterOptions();
            reserveCollectionFilterWidth();
            updateCollectionFilterOptions({ force: true });
            renderCardbacks();
        });
        window.addEventListener('moonfall:languagechange', () => {
            applyCollectionTranslations();
            updateSpellOwnerFilterOptions();
            reserveCollectionFilterWidth();
            updateCollectionFilterOptions({ force: true });
            renderCardbacks();
            // A summoner's name is localized: read again in the new language when asked for.
            spellOwnerReads.clear();
            spellOwnersRead.clear();
            /* THE OWNED CATALOG IS READ IN ONE LANGUAGE (29 September 2026).
               Its names are the Moon Exchange's sell list and the words of its
               rows, and it was kept across a language change: the sell column
               named every card in the old language until a trade or a reload.
               It is invalidated here and read again in the new language when a tab
               needs it — at once when the Exchange is on screen; a read still
               on its way in the old language is never kept (readAuxiliaryCards). */
            // Keep a shown catalog until its replacement arrives, including
            // across balance updates that synchronize the Exchange context.
            if (!isOpen || activeSection !== 'exchange') auxiliaryAllCards = [];
            auxiliaryCatalogLanguage = null;
            auxiliaryCatalogComplete = null;
            auxiliaryCardsRequest = null;
            if (isOpen && activeSection === 'exchange') {
                handOverExchangeCatalog(openGeneration);
            }
            if (!isOpen) {
                // The lobby's pages were read in the old language.
                rewarmLobbyWindow();
                return;
            }
            void (async () => {
                await renderCards(undefined, { refresh: true });
                if (activeDetailCardId) {
                    await renderCardDetail(activeDetailCardId, activeDetailVariant);
                }
            })();
        });
    }

    function init() {
        createCollectionModal();
        if (MoonExchange?.init) {
            MoonExchange.init();
        }
        if (CardPacks?.init) {
            CardPacks.init();
        }
        setupEventListeners();
        setupStellarAssetListeners();
        setupStardustFollow();
        setupHoldingsFollow();
        setupI18nListeners();
    }

    function createCollectionModal() {
        if (document.getElementById('collection-modal')) return;

        const modal = document.createElement('div');
        modal.id = 'collection-modal';
        modal.className = 'deck-builder-modal collection-modal';
        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        modal.setAttribute('aria-labelledby', 'collection-title');
        modal.setAttribute('aria-hidden', 'true');
        modal.innerHTML = `
            <div class="deck-builder-container collection-container">
                <div class="deck-builder-header">
                    <div class="collection-heading">
                        <h2 id="collection-title" data-i18n="collection.title">Collection</h2>
                        <p class="collection-subtitle" data-i18n="collection.subtitle">Browse your cards and cardbacks</p>
                    </div>
                    <nav class="collection-tabs deck-builder-tabs" role="tablist" aria-orientation="horizontal" aria-label="Collection sections" data-i18n-aria-label="collection.sectionsLabel">
                        <button id="collection-tab-cards" class="deck-tab active" role="tab" aria-selected="true" aria-controls="collection-cards-section" tabindex="0" data-section="cards" data-i18n="collection.tabs.cards">Cards</button>
                        <button id="collection-tab-deckbuilder" class="deck-tab" role="tab" aria-selected="false" aria-controls="collection-deckbuilder-section" tabindex="-1" data-section="deckbuilder" data-i18n="collection.tabs.deckBuilder">Decks</button>
                        <button id="collection-tab-cardbacks" class="deck-tab" role="tab" aria-selected="false" aria-controls="collection-cardbacks-section" tabindex="-1" data-section="cardbacks" data-i18n="collection.tabs.cardbacks">Cardbacks</button>
                        <button id="collection-tab-exchange" class="deck-tab" role="tab" aria-selected="false" aria-controls="collection-exchange-section" tabindex="-1" data-section="exchange" data-i18n="collection.tabs.exchange">Moon Exchange</button>
                        <button id="collection-tab-packs" class="deck-tab" role="tab" aria-selected="false" aria-controls="collection-packs-section" tabindex="-1" data-section="packs" data-i18n="collection.tabs.packs">Card Packs</button>
                    </nav>
                    <div class="collection-header-actions">
                        <button class="collection-stardust" id="collection-stardust" type="button" data-open-store aria-label="Moon Store" data-i18n-aria-label="store.open">
                            <span class="stardust-glyph" aria-hidden="true"></span>
                            <span class="collection-stardust-label" data-i18n="common.stardust">Stardust</span>
                            <span class="collection-stardust-value" id="collection-stardust-value">—</span>
                        </button>
                        <button type="button" class="deck-builder-close" id="collection-close" aria-label="Close" data-i18n-aria-label="store.close">&times;</button>
                    </div>
                </div>

                <div class="collection-content">
                    <section class="collection-section collection-section-cards" id="collection-cards-section" role="tabpanel" aria-labelledby="collection-tab-cards">
                        <div id="collection-cards-browser">
                            <div class="collection-controls-row">
                                <div class="collection-card-tabs deck-builder-tabs" role="group" aria-label="Card type" data-i18n-aria-label="collection.cardTypesLabel">
                                    <button type="button" class="deck-tab active" data-card-tab="characters" aria-pressed="true" data-i18n="collection.cardTabs.characters">Characters</button>
                                    <button type="button" class="deck-tab" data-card-tab="spells" aria-pressed="false" data-i18n="collection.cardTabs.spells">Spells</button>
                                </div>
                                <div class="collection-controls">
                                    <div class="deck-builder-controls">
                                        <label class="collection-visually-hidden" for="collection-search" data-i18n="collection.filters.searchLabel">Search cards</label>
                                        <input type="text" id="collection-search" placeholder="Search cards..." data-i18n-placeholder="collection.searchPlaceholder" class="deck-builder-search">
                                        <button type="button" class="collection-mobile-filters-toggle" id="collection-mobile-filters-toggle" aria-expanded="false" aria-controls="collection-filter-popover" data-i18n="collection.filters.mobileToggle">Filters</button>
                                        <div class="collection-filter-popover" id="collection-filter-popover" role="group" aria-label="Card filters" data-i18n-aria-label="collection.filters.mobileGroup">
                                            <label class="collection-visually-hidden" for="collection-filter-spell-owner" data-i18n="collection.filters.spellOwnerLabel">Spell owner</label>
                                            <select id="collection-filter-spell-owner" class="deck-filter" style="display:none;">
                                                <option value="all" data-i18n="collection.filters.spellsAll">Spells: All</option>
                                            </select>
                                            <label class="collection-visually-hidden" for="collection-filter-collection" data-i18n="collection.filters.collectionLabel">Collection</label>
                                            <!-- Collections are filled in from the
                                                 player's own holdings; only the two
                                                 constants ship in the markup. -->
                                            <select id="collection-filter-collection" class="deck-filter">
                                                <option value="" data-i18n="collection.filters.allCollections">All Collections</option>
                                                <option value="Stellar" data-i18n="collection.filters.stellar">Stellar</option>
                                            </select>
                                            <label class="collection-visually-hidden" for="collection-filter-mana" data-i18n="collection.filters.manaLabel">Mana cost</label>
                                            <select id="collection-filter-mana" class="deck-filter">
                                                <option value="" data-i18n="collection.filters.manaAll">Mana: All</option>
                                                <option value="0">0</option>
                                                <option value="1">1</option>
                                                <option value="2">2</option>
                                                <option value="3">3</option>
                                                <option value="4">4</option>
                                                <option value="5">5</option>
                                                <option value="6">6</option>
                                                <option value="7+">7+</option>
                                            </select>
                                            <label class="collection-visually-hidden" for="collection-sort-cards" data-i18n="collection.filters.sortLabel">Sort cards</label>
                                            <select id="collection-sort-cards" class="deck-filter">
                                                <option value="name" data-i18n="collection.sort.nameAsc">Name (A-Z)</option>
                                                <option value="cost-asc" selected data-i18n="collection.sort.manaAsc">Mana ↑</option>
                                                <option value="cost-desc" data-i18n="collection.sort.manaDesc">Mana ↓</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="deck-builder-cards collection-cards-grid" id="collection-cards-grid"></div>
                            <div class="collection-pagination" id="collection-cards-pagination">
                                <button type="button" class="btn deck-btn" id="collection-cards-prev" disabled data-i18n="common.prev">Prev</button>
                                <div class="collection-page-info" id="collection-cards-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
                                <button type="button" class="btn deck-btn" id="collection-cards-next" disabled data-i18n="common.next">Next</button>
                            </div>
                        </div>

                        <div class="collection-card-detail" id="collection-card-detail" style="display:none;"></div>
                    </section>

                    <section class="collection-section collection-section-cardbacks" id="collection-cardbacks-section" role="tabpanel" aria-labelledby="collection-tab-cardbacks" style="display: none;">
                        <div class="collection-cardbacks-header">
                            <div>
                                <h3 data-i18n="collection.cardbacks.title">Cardbacks</h3>
                                <p class="collection-cardbacks-subtitle" data-i18n="collection.cardbacks.subtitle">Select the art on your deck backs</p>
                            </div>
                            <div class="collection-cardbacks-count" id="collection-cardbacks-count"></div>
                        </div>
                        <div class="cardback-grid" id="collection-cardback-grid"></div>
                        <div class="cardback-pagination" id="cardback-pagination">
                            <button type="button" class="btn deck-btn" id="cardback-prev" disabled data-i18n="common.prev">Prev</button>
                            <div class="cardback-page-info" id="cardback-page-info" data-i18n="common.pageOne" data-i18n-runtime="true">Page 1</div>
                            <button type="button" class="btn deck-btn" id="cardback-next" disabled data-i18n="common.next">Next</button>
                        </div>
                    </section>

                    <section class="collection-section collection-section-deckbuilder" id="collection-deckbuilder-section" role="tabpanel" aria-labelledby="collection-tab-deckbuilder" style="display: none;"></section>

                    <section class="collection-section collection-section-exchange" id="collection-exchange-section" role="tabpanel" aria-labelledby="collection-tab-exchange" style="display: none;"></section>
                    <section class="collection-section collection-section-packs" id="collection-packs-section" role="tabpanel" aria-labelledby="collection-tab-packs" style="display: none;"></section>
                </div>
            </div>
        `;
        document.body.appendChild(modal);
        applyCollectionTranslations();
    }

    function setupEventListeners() {
        if (collectionEventListenersReady) return;
        const modal = document.getElementById('collection-modal');
        if (!modal) return;
        collectionEventListenersReady = true;
        // First: it decides whether a press reaches anything below it.
        installOpeningGestureGuard(modal);

        document.getElementById('collection-close')?.addEventListener('click', close);
        // Backdrop click-to-close, but only for a gesture that BEGAN on the
        // backdrop. A drag that starts inside the panel (pulling the pack
        // ribbon, dragging a card) and is released over the backdrop gets its
        // click dispatched at the common ancestor of the two targets — the
        // modal itself — which used to close the whole Collection mid-gesture.
        let backdropGestureOrigin = false;
        modal.addEventListener('pointerdown', (e) => {
            backdropGestureOrigin = e.target?.id === 'collection-modal';
        });
        modal.addEventListener('click', (e) => {
            const startedOnBackdrop = backdropGestureOrigin;
            backdropGestureOrigin = false;
            // Keyboard/synthetic clicks have no preceding pointerdown; those
            // only reach the backdrop when it is genuinely the target.
            if (e.target?.id === 'collection-modal' && (startedOnBackdrop || e.detail === 0)) {
                close();
            }
        });

        document.querySelectorAll('#collection-modal .collection-tabs .deck-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                const picked = getValidCollectionSection(tab.dataset.section || 'cards');
                // The tab picked is on its way (held, the tab being left kept
                // on screen for it): a second press has nothing to start again.
                if (picked === activeSection && sectionHandover) return;
                switchSection(picked);
            });
            if (tab.getAttribute('data-section') === 'cardbacks') {
                // Intent: the backs this tab opens on start decoding now.
                const warmBacks = () => warmCardbackArt('high');
                tab.addEventListener('pointerenter', warmBacks);
                tab.addEventListener('focus', warmBacks);
                tab.addEventListener('touchstart', warmBacks, { passive: true });
            }
            tab.addEventListener('keydown', (event) => {
                const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
                if (!keys.includes(event.key)) return;
                event.preventDefault();
                const tabs = Array.from(document.querySelectorAll('#collection-modal .collection-tabs .deck-tab'));
                const current = tabs.indexOf(tab);
                const nextIndex = event.key === 'Home'
                    ? 0
                    : event.key === 'End'
                        ? tabs.length - 1
                        : (current + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
                tabs[nextIndex]?.focus();
                tabs[nextIndex]?.click();
            });
        });

        document.querySelectorAll('#collection-modal .collection-card-tabs .deck-tab').forEach(tab => {
            tab.addEventListener('click', () => {
                switchCardTab(tab.dataset.cardTab || 'characters');
            });
        });

        document.getElementById('collection-search')?.addEventListener('input', (e) => {
            cardsPage = 1;
            setCurrentSearch(e.target.value || '');
            renderCards(undefined, { resetScroll: true });
        });

        document.getElementById('collection-mobile-filters-toggle')?.addEventListener('click', () => {
            const popover = document.getElementById('collection-filter-popover');
            setMobileFiltersOpen(!popover?.classList.contains('is-open'));
        });

        modal.addEventListener('pointerdown', (event) => {
            const popover = document.getElementById('collection-filter-popover');
            const toggle = document.getElementById('collection-mobile-filters-toggle');
            if (!popover?.classList.contains('is-open')) return;
            if (popover.contains(event.target) || toggle?.contains(event.target)) return;
            setMobileFiltersOpen(false);
        });

        modal.addEventListener('keydown', (event) => {
            const popover = document.getElementById('collection-filter-popover');
            if (event.key !== 'Escape' || !popover?.classList.contains('is-open')) return;
            event.stopPropagation();
            setMobileFiltersOpen(false, { returnFocus: true });
        });

        document.getElementById('collection-filter-collection')?.addEventListener('change', (e) => {
            currentFilters.collection = e.target.value;
            cardsPage = 1;
            renderCards(undefined, { resetScroll: true });
        });

        document.getElementById('collection-filter-mana')?.addEventListener('change', (e) => {
            currentFilters.mana = e.target.value;
            cardsPage = 1;
            renderCards(undefined, { resetScroll: true });
        });

        document.getElementById('collection-filter-spell-owner')?.addEventListener('change', (e) => {
            activeSpellOwnerFilter = e.target.value || 'all';
            cardsPage = 1;
            renderCards(undefined, { resetScroll: true });
        });

        document.getElementById('collection-sort-cards')?.addEventListener('change', (e) => {
            currentSort = e.target.value;
            cardsPage = 1;
            renderCards(undefined, { resetScroll: true });
        });

        document.addEventListener('pointermove', hoverIntent.observeMove, { capture: true, passive: true });
        // An input takes the main thread back from the page being built ahead.
        ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((type) => {
            document.addEventListener(type, yieldCardsPrebuild, { capture: true, passive: true });
        });

        /* A page turn keeps the page on screen until the next one is built and
           settled, then swaps it in whole; the button answers the press in the
           meantime. Hover, focus and a first touch read the page it leads to.
           A press while a turn is on its way asks for the page after the one
           asked (fast hands): the last page asked for is the one painted, and
           a page superseded while it was built is never painted (renderCards).
           Pressed again, a button already answering keeps its answer (released
           and re-acknowledged, its pending look blinked off for 60 ms). A
           press back to the page on screen ends the turn at once
           (endCardsTurnOnShownPage). */
        const turnPage = (button, delta) => {
            const last = cardsTotalPages > 0 ? cardsTotalPages : Infinity;
            const asked = Math.min(Math.max(1, cardsPage + delta), last);
            if (asked === cardsPage) return;
            cardsPage = asked;
            if (endCardsTurnOnShownPage()) return;
            if (cardsPagerAck?.button !== button) {
                releaseCardsPagerAck();
                cardsPagerAck = { button, release: acknowledgePagerPress(button) };
            }
            renderCards(undefined, { resetScroll: true });
        };
        [{ id: 'collection-cards-prev', delta: -1 }, { id: 'collection-cards-next', delta: 1 }].forEach(({ id, delta }) => {
            const button = /** @type {HTMLButtonElement | null} */ (document.getElementById(id));
            if (!button) return;
            button.addEventListener('click', () => turnPage(button, delta));
            const warmTarget = () => {
                if (!button.disabled) prefetchCardsPage(cardsPage + delta, 'high');
            };
            button.addEventListener('pointerenter', warmTarget);
            button.addEventListener('focus', warmTarget);
            button.addEventListener('touchstart', warmTarget, { passive: true });
        });

        document.getElementById('cardback-prev')?.addEventListener('click', () => {
            cardbackPage = Math.max(1, cardbackPage - 1);
            renderCardbacks();
        });
        document.getElementById('cardback-next')?.addEventListener('click', () => {
            cardbackPage += 1;
            renderCardbacks();
        });

        document.getElementById('collection-cardback-grid')?.addEventListener('click', async (e) => {
            const item = e.target.closest('.cardback-item[data-cardback-action="equip"]');
            if (!item) return;
            const slug = item.dataset.slug;
            if (!slug) return;

            try {
                if (MoonfallCloud?.cardbacks) {
                    await MoonfallCloud.cardbacks.select(slug);
                    selectedCardback = slug;
                    /* The lobby's starts warm the back the session user names,
                       in the press task, without re-reading the profile: this
                       equip is the only in-tab change to it. */
                    const sessionUser = GameState?.getUser?.();
                    if (sessionUser) GameState.setUser({ ...sessionUser, selectedCardback: slug });
                    renderCardbacks();
                    showStatus(uiText('collection.cardbacks.updated', 'Cardback updated!'));
                }
            } catch (err) {
                console.error('[Collection] Failed to select cardback:', err);
                showStatus(uiText('collection.cardbacks.updateFailed', 'Failed to select cardback'), 'error');
            }
        });

        /*
         * THE GRID REFITS IN THE FRAME OF THE RESIZE. The cards are sized in
         * viewport units times the grid's fit scale, and every size a card
         * passes through is a full re-raster of its picture (cards.js; on the
         * Chrome phone seat each is a 100-300 ms task). Refit only on the
         * debounced viewport change below (platform.ts, 150 ms, and far later
         * under a busy main thread), each resize re-rastered the page at the
         * new viewport's size with the OLD scale, then again at the new scale:
         * three sizes per viewport change on the phone (round 15 profile, 28
         * September 2026: 229x333, 233x311, 261x311). The scale and columns
         * are measured here, in the resize event, so the first frame at the
         * new viewport is laid out at its final size. A change of capacity (a
         * page of another size) still waits for the debounced change, which
         * re-reads and rebuilds the page — and so does its scale: the page on
         * screen keeps the scale of its own columns (refitShownCardsPage).
         */
        window.addEventListener('resize', () => {
            if (!isOpen || activeSection !== 'cards' || !cardsGridOnScreen()) return;
            measureCardsPageSize();
            refitShownCardsPage();
        }, { passive: true });

        window.addEventListener('moonfall:viewport-change', () => {
            if (!isOpen) return;
            clearHoverPreview();
            if (activeSection === 'cards') {
                if (syncCardsPageSize({ preserveOffset: true })) {
                    void renderCards(undefined, { resetScroll: true });
                } else {
                    /* The measurement just ran and may have changed the grid's
                       SHAPE — balanced vs auto-fill, and the column count —
                       without changing its CAPACITY. Only `renderCards`
                       publishes that shape, and it is skipped here, so the grid
                       keeps the shape it had at the other viewport: leave a
                       1920x1080 window for a short one and come back, and the
                       balanced six-column grid returns as four columns packed
                       to one side of a 1274px shell. Republish instead; it is a
                       class and a custom property, no re-render, no scroll
                       change. A page of another capacity still on its way
                       takes it at its own commit. */
                    refitShownCardsPage();
                }
            } else if (activeSection === 'cardbacks' && syncCardbacksPageSize({ preserveOffset: true })) {
                renderCardbacks();
            }
        });
    }

    /*
     * THE NEXT TAB, AS FAR AS AN OPEN MAY READ IT. The Moon Exchange's config
     * and ONE listings page — a page of data is cheap — at idle after the
     * open's first paint, so the page the player is waiting for gets the
     * network and the main thread first. The tab's owned catalog (the whole
     * card list) is read only on intent on the tab (MoonExchange.warm), with
     * the catalog loader handed over here; the entry rows' art (the rows
     * carry it) and their cards (page-scoped, for their faces: the tab opens
     * before the catalog) are read with the page.
     * The Decks tab warms itself on intent (deck-builder.js): warmed from here
     * it read every saved deck, every starter deck and every deck summoner's
     * details on each open, pressed or not (read audit R6, 26 September 2026).
     */
    function scheduleNeighbourTabWarm(generation) {
        const warmExchange = () => {
            if (!collectionSessionIsCurrent(generation)) return;
            // The tab opens before the catalog (openAuxiliarySection): the
            // warm reads the entry rows' cards with the page, for their faces.
            void MoonExchange?.warm?.({ loadCards: ensureAuxiliaryCardsLoaded, withoutCatalog: true });
        };
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(warmExchange, { timeout: 2000 });
        } else {
            setTimeout(warmExchange, 200);
        }
    }

    function getValidCollectionSection(section) {
        return ['cards', 'deckbuilder', 'cardbacks', 'exchange', 'packs'].includes(section) ? section : 'cards';
    }

    function ensureDeckBuilderSection() {
        if (!DeckBuilder?.open) return null;
        if (deckBuilderSectionReady && DeckBuilder.isOpen?.()) return null;
        if (deckBuilderSectionOpening) return deckBuilderSectionOpening;

        const host = document.getElementById('collection-deckbuilder-section');
        if (!host) return null;

        deckBuilderSectionOpening = Promise.resolve(DeckBuilder.open({
            source: 'collection',
            embeddedHost: host,
        })).catch((err) => {
            console.error('[Collection] Failed to open embedded deck builder:', err);
        }).finally(() => {
            deckBuilderSectionOpening = null;
        });

        return deckBuilderSectionOpening.then(() => {
            deckBuilderSectionReady = true;
        });
    }

    /*
     * PRESENT A FINISHED COLLECTION (docs/asset-loading.md, the Collection row).
     *
     * Measured on the 110 ms RTT desktop profile, 24 September 2026: the modal
     * appeared over eight skeleton cards on a warm AND a cold cache, the page
     * replaced them about a second later, the Stardust chip went "—" → "5K"
     * (moving the tabs 3 px and the search box 7 px) and the pager went
     * "Page 1" → "Page 1 of 16" (resizing Prev and Next by 22 px). Now the
     * page is read and its art decoded ahead of the press (`warmForLobby`,
     * `prefetchFirstPage`), the modal is laid out but held until the first
     * page is committed with its pager and balance, and then presented whole.
     */
    let presentation = null;

    function gridHasCommittedPage(container = document.getElementById('collection-cards-grid')) {
        return Boolean(container?.querySelector(':scope > .collection-card-item:not(.skeleton-card)'));
    }

    function collectionPresentationHeld() {
        return Boolean(presentation && !presentation.released);
    }

    /*
     * THE PRESSED TILE'S RING. A lobby tile answers its press with the shared
     * busy style (acknowledgePress), and on a tile that style is a ring turning
     * in the chevron's place (hub.css `.hub-tile-busy`), which the Foundry tile
     * carries in its markup. The Collection tile carried none, so its press
     * hid the chevron and showed nothing where it had been. The open gives the
     * tile its ring. Past the grace, when only the build is left (nothing on
     * the wire, so no wells need show), the ring is that wait's ONE organized
     * loading state and is marked as one (`data-loading`, as the Foundry
     * tile's is in lobby.js) until the Collection is presented whole.
     */
    function openerRing(opener) {
        if (!opener?.classList?.contains('hub-tile')) return null;
        let ring = opener.querySelector('.hub-tile-busy');
        if (!ring) {
            ring = document.createElement('span');
            ring.className = 'hub-tile-busy';
            ring.setAttribute('aria-hidden', 'true');
            opener.appendChild(ring);
        }
        return ring;
    }

    function holdCollectionPresentation(modal, generation, opener) {
        // Replacing a hold never uncovers the modal in between.
        releaseCollectionPresentation({ uncover: false });
        const ring = openerRing(opener);
        const releasePress = acknowledgePress(opener);
        const hold = {
            generation,
            released: false,
            gridReady: false,
            balanceReady: false,
            // Past the grace, a committed page is presented without its balance read.
            graceOver: false,
            deferredEntrance: false,
            // The page's pictures: 'unknown' until its build starts, then
            // 'pending' while its art warm is out, or 'in-hand' (renderCards).
            art: 'unknown',
            // The page's picture URLs, once its build asks for them.
            pictures: [],
            ring,
            startedAt: Date.now(),
            timer: null,
            releaseOpener: () => {
                releasePress();
                if (ring) delete ring.dataset.loading;
            },
        };
        presentation = hold;
        modal.classList.add('collection-modal-preparing');
        modal.setAttribute('aria-busy', 'true');
        hold.timer = setTimeout(() => presentPastGrace(hold), COLLECTION_OPEN_HOLD_CAP_MS);
        return hold;
    }

    /* The grace is over (see COLLECTION_OPEN_BUILD_CAP_MS): a committed page
       is presented now; with only the build left, the open finishes unseen
       and is presented when its page commits (capped), the pressed tile's
       ring its loading state; anything still on the wire gets the one loading
       state now. */
    function presentPastGrace(hold) {
        if (hold.released) return;
        hold.graceOver = true;
        if (!hold.gridReady && presentationArtInHand(hold)) {
            const left = COLLECTION_OPEN_BUILD_CAP_MS - (Date.now() - hold.startedAt);
            if (left > 0) {
                if (hold.ring) hold.ring.dataset.loading = 'true';
                hold.timer = setTimeout(() => presentCollection(hold), left);
                return;
            }
        }
        presentCollection(hold);
    }

    /** Nothing of the held page's is on the wire: its warm landed, or every picture's bytes are in hand. */
    function presentationArtInHand(hold) {
        if (hold.art === 'in-hand') return true;
        return hold.art === 'pending'
            && hold.pictures.length > 0
            && hold.pictures.every((url) => picturesInHand.has(url));
    }

    /** The open's page art warm, while the open is held: in hand once it lands. */
    function notePresentationArt(hold, warm, entries = []) {
        if (!hold || hold.released) return;
        if (!warm || typeof warm.then !== 'function') {
            hold.art = 'in-hand';
            return;
        }
        hold.art = 'pending';
        hold.pictures = pictureUrlsOf(entries);
        Promise.resolve(warm).then(() => {
            if (hold.art === 'pending') hold.art = 'in-hand';
        }, () => {});
    }

    /** One of the two things the open waits for has arrived: 'gridReady' or 'balanceReady'. */
    function markCollectionPresentable(part) {
        const hold = presentation;
        if (!hold || hold.released) return;
        hold[part] = true;
        if (hold.gridReady && (hold.balanceReady || hold.graceOver)) presentCollection(hold);
    }

    function releaseCollectionPresentation({ uncover = true } = {}) {
        const hold = presentation;
        presentation = null;
        if (hold && !hold.released) {
            hold.released = true;
            clearTimeout(hold.timer);
            hold.releaseOpener();
        }
        if (!uncover) return;
        const modal = document.getElementById('collection-modal');
        modal?.classList.remove('collection-modal-preparing');
        modal?.removeAttribute('aria-busy');
    }

    function presentCollection(hold) {
        if (hold.released) return;
        hold.released = true;
        clearTimeout(hold.timer);
        hold.releaseOpener();
        if (presentation === hold) presentation = null;
        const modal = document.getElementById('collection-modal');
        if (!modal || hold.generation !== openGeneration || !isOpen) return;
        /* Presented before this open's own profile read: the chip carries the
           figure the lobby chip is showing, which may have been learnt since
           the press (open() printed it if it was known then). Only a balance
           nobody knows yet is shown as "—", in the width the chip reserves for
           the widest figure (pills.css), so its arrival moves nothing. */
        if (!Number.isFinite(Number(stardust))) {
            const lobbyBalance = lastKnownStardust();
            if (lobbyBalance !== null) {
                stardust = lobbyBalance;
                updateStardustDisplay();
            }
        }
        /* Past the grace with the page not committed yet: ONE organized
           loading state — the whole page of wells at once — and the page
           replaces it whole when it lands. The pager is final with the wells
           when the page's answer is in (usually: it was read at lobby idle, and
           what is still out is its pictures); only a count nobody knows yet is
           held back rather than printed provisionally. A reopen owed only a
           re-read (the holdings changed while it was closed) over the very
           page it asks for presents that page under a re-query's loading
           state (the grace is already past), so its old holdings never read
           as current; the re-read reconciles it tile by tile and ends that
           state (renderCards). Wells over it would be a placeholder over a
           correct page. */
        const grid = document.getElementById('collection-cards-grid');
        const showsAskedPage = gridHasCommittedPage(grid)
            && paintedCardsContentSignature === cardsContentSignature();
        if (activeSection === 'cards' && !hold.gridReady && showsAskedPage) {
            markCardsGridStale(grid, false);
            showCardsGridLoading(grid, true);
        } else if (activeSection === 'cards' && !hold.gridReady) {
            markCardsGridStale(grid, false);
            renderCardsSkeletons({ replaceStale: true });
            const answered = cardsPages.peek(cardsQuery(), cardsPage, cardsPageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS });
            if (answered?.page?.totalItems > 0) {
                updateCardsPagination(answered.page);
            } else {
                reservePageInfoWidth(document.getElementById('collection-cards-page-info'), PENDING_PAGE_RESERVE);
                document.getElementById('collection-cards-pagination')?.classList.add('collection-pagination--pending');
            }
        }
        presentCollectionNow(modal);
        if (hold.deferredEntrance) dealInCardsGrid(document.getElementById('collection-cards-grid'));
    }

    function presentCollectionNow(modal) {
        modal.classList.remove('collection-modal-preparing');
        modal.removeAttribute('aria-busy');
        landRevealTransitions(modal);
        Effects?.viewEnter?.(modal);
        hoverIntent.disarm();
        /* The Collection takes over from the lobby in the frame it is first
           SEEN: the modal is announced (aria-hidden false) and takes focus,
           and the lobby is set aside (inert, aria-hidden), all at once.
           Not while it is held invisible: isolating at the start of a hold
           made the lobby inert and aria-hidden while it was still what the
           player saw, for the whole hold.
           Not in this task either. Its view-enter fade starts at opacity 0,
           and the first frame or two of it can still paint nothing (measured
           with the real stylesheets: Firefox's first frame, headless
           Chromium's first two; WebKit already paints some on its first).
           The chrome menu counts the Collection as open once nothing hides it
           — display, visibility, aria-hidden, never opacity — and checks it
           before the lobby, so lifting the hold withdrew the menu's button in
           this task: on the WebKit phone the button went at 53132 and the
           modal first painted at 53711, the lobby on screen without it in
           between (round 13 reopen; the snappy meter's "withdrawn"). So the
           modal stays aria-hidden, as the unpainted thing it is, until the
           first frame that paints it, read from its fade as each frame
           begins, capped; the focus waits with it (a browser refuses
           aria-hidden on an ancestor that holds focus). Without the fade
           (reduced motion), or re-presented while already seen, it is seen
           now. */
        if (collectionBackgroundState !== null || !entersFromTransparent(modal)) {
            showCollectionSeen(modal);
            return;
        }
        modal.setAttribute('aria-hidden', 'true');
        const generation = openGeneration;
        let framesLeft = LOBBY_ASIDE_MAX_FRAMES;
        const nextFrame = (callback) => (typeof window.requestAnimationFrame === 'function'
            ? window.requestAnimationFrame(callback)
            : setTimeout(callback, 16));
        // Frame callbacks run after the frame's animations are updated and
        // before it paints: what the modal computes now is what it paints,
        // and the menu's observer answers in this frame's microtasks.
        const onceSeen = () => {
            if (!isOpen || generation !== openGeneration) return;
            framesLeft -= 1;
            if (framesLeft > 0 && entersFromTransparent(modal) && !paintsVisibly(modal)) {
                nextFrame(onceSeen);
                return;
            }
            showCollectionSeen(modal, { inFrame: true });
        };
        nextFrame(onceSeen);
    }

    /** The frame the Collection is first seen: announced, focused, and the lobby set aside. */
    function showCollectionSeen(modal, { inFrame = false } = {}) {
        modal.setAttribute('aria-hidden', 'false');
        const active = document.activeElement;
        if (!active || !modal.contains(active)) document.getElementById('collection-close')?.focus();
        isolateCollectionBackground(modal);
        noteOpeningGestureSeen({ inFrame });
    }

    /*
     * THE PRESS THAT OPENED THE COLLECTION NEVER ACTS ON IT.
     *
     * A reopen is presented in the task of the tile's press, over the very
     * spot that was pressed, so the second press of a double click or a
     * double tap landed on the grid: a click (detail 2) opened a card's detail
     * on Chrome and Firefox desktop, and a second finger enlarged a card on
     * the Chrome and WebKit phones, in every pass of round 15 (28 September
     * 2026, base15 and int15, `collection-tile-double-press`). The phones'
     * second finger went down at +126-137 ms, before the Collection's first
     * frame (+160-194 ms): the player could not have seen the card.
     *
     * A press belongs to the opening gesture, and does nothing in the
     * Collection, when
     *   - its pointer went down before the Collection was first painted;
     *   - it continues a mouse multi-click that did not begin in the
     *     Collection (the browser counts it: click detail >= 2);
     *   - it lands where the opening press did, within the multi-press
     *     interval: a double tap (a touch press carries no click count).
     * Its whole sequence is swallowed (down, up and the click that follows),
     * in the capture phase on the modal, so no control or card under it sees
     * any of it, and the selection a double click would start is not made.
     * Every other press acts at once: nothing is delayed or held, so the
     * first real press on the grid is answered as ever.
     */
    // The common double-click interval, and a finger's spread for "the same spot".
    const OPENING_GESTURE_MS = 500;
    const OPENING_GESTURE_SLOP_PX = 16;
    // A swallowed sequence's click follows its release at once (a mouse) or
    // within a double-tap delay (a touch); a stale swallow never outlives this.
    const OPENING_GESTURE_CLICK_MS = 1000;
    /** @type {{ at: number, x: number, y: number, target: EventTarget | null } | null} */
    let lastPressOutside = null;
    /** @type {any} the open's gesture: its press, when it was first seen, what it swallows */
    let openingGesture = null;
    // The browser's current multi-click chain began in the Collection.
    let clickChainInCollection = false;

    /**
     * When an event happened, on the page's clock. Every engine the game runs
     * in stamps input on it (high resolution, from the time origin), which is
     * when the finger went down even when a long task delayed its dispatch;
     * a stamp on another clock (an epoch time) is read as now.
     */
    function eventTime(event) {
        const now = performance.now();
        const stamp = Number(event?.timeStamp);
        return Number.isFinite(stamp) && stamp > 0 && stamp <= now + 1000 ? stamp : now;
    }

    function collectionModalContains(target) {
        const modal = document.getElementById('collection-modal');
        return Boolean(modal && target instanceof Node && modal.contains(target));
    }

    /** Every press outside the Collection: the one that opens it is its gesture's first. */
    function notePressOutside(event) {
        if (collectionModalContains(event.target)) return;
        lastPressOutside = {
            at: eventTime(event),
            x: Number(event.clientX) || 0,
            y: Number(event.clientY) || 0,
            target: event.target,
        };
    }

    /** A click chain that begins outside the Collection is not the Collection's. */
    function noteClickOutside(event) {
        if (!collectionModalContains(event.target) && Number(event.detail) <= 1) clickChainInCollection = false;
    }

    function beginOpeningGesture(opener) {
        const now = performance.now();
        const press = lastPressOutside;
        const pressOpened = Boolean(press)
            && Boolean(opener?.contains)
            && press.target instanceof Node
            && opener.contains(press.target)
            && now - press.at <= OPENING_GESTURE_MS;
        openingGesture = {
            at: now,
            press: pressOpened ? press : null,
            // Opened without a press (a key, a link in code): no finger or
            // button is still on its way down, so nothing waits to be seen.
            seenAt: pressOpened ? null : now,
            pointers: new Set(),
            swallowClickUntil: 0,
        };
        clickChainInCollection = false;
    }

    /**
     * The Collection's first painted frame. Called from that frame's own
     * callback, or in the task that presents a Collection with no fade to
     * wait for, which the next frame paints.
     */
    function noteOpeningGestureSeen({ inFrame = false } = {}) {
        const gesture = openingGesture;
        if (!gesture || gesture.seenAt !== null || gesture.seenPending) return;
        const mark = () => {
            gesture.seenPending = false;
            if (gesture.seenAt === null) gesture.seenAt = performance.now();
        };
        if (inFrame) {
            mark();
            return;
        }
        gesture.seenPending = true;
        if (typeof window.requestAnimationFrame === 'function') window.requestAnimationFrame(mark);
        else setTimeout(mark, 16);
    }

    function pressBelongsToOpeningGesture(gesture, event) {
        const at = eventTime(event);
        if (gesture.seenAt === null || at < gesture.seenAt) return true;
        const press = gesture.press;
        if (!press || at - press.at > OPENING_GESTURE_MS) return false;
        const dx = (Number(event.clientX) || 0) - press.x;
        const dy = (Number(event.clientY) || 0) - press.y;
        return Math.sqrt(dx * dx + dy * dy) <= OPENING_GESTURE_SLOP_PX;
    }

    function swallowGesturePress(event) {
        event.stopPropagation();
        // Not the pointerdown: cancelling it would also cancel what the
        // engine does for the pointer (its compatibility events, capture).
        if (event.cancelable && event.type !== 'pointerdown') event.preventDefault();
    }

    /** The modal's capture-phase guard: a press of the opening gesture reaches nothing inside it. */
    function guardOpeningGesture(event) {
        const gesture = openingGesture;
        const now = performance.now();
        switch (event.type) {
            case 'pointerdown':
                if (!gesture) return;
                if (pressBelongsToOpeningGesture(gesture, event)) {
                    gesture.pointers.add(event.pointerId);
                    swallowGesturePress(event);
                } else {
                    // A new gesture of the player's own: nothing swallowed
                    // before may eat its click.
                    gesture.swallowClickUntil = 0;
                }
                return;
            case 'pointerup':
            case 'pointercancel':
                if (!gesture?.pointers.has(event.pointerId)) return;
                gesture.pointers.delete(event.pointerId);
                if (event.type === 'pointerup') gesture.swallowClickUntil = now + OPENING_GESTURE_CLICK_MS;
                swallowGesturePress(event);
                return;
            case 'mousedown':
            case 'mouseup':
                if (!gesture) return;
                if (gesture.pointers.size > 0
                    || now < gesture.swallowClickUntil
                    || (Number(event.detail) >= 2 && !clickChainInCollection)) {
                    swallowGesturePress(event);
                }
                return;
            case 'click': {
                const detail = Number(event.detail) || 0;
                // A keyboard's click (detail 0) is never a pointer's gesture.
                const armed = Boolean(gesture) && detail >= 1 && now < gesture.swallowClickUntil;
                const continued = Boolean(gesture) && detail >= 2 && !clickChainInCollection;
                if (armed || continued) {
                    gesture.swallowClickUntil = 0;
                    swallowGesturePress(event);
                    return;
                }
                if (detail === 1) clickChainInCollection = true;
                return;
            }
            default:
        }
    }

    function installOpeningGestureGuard(modal) {
        document.addEventListener('pointerdown', notePressOutside, { capture: true, passive: true });
        document.addEventListener('click', noteClickOutside, { capture: true, passive: true });
        ['pointerdown', 'pointerup', 'pointercancel', 'mousedown', 'mouseup', 'click'].forEach((type) => {
            modal.addEventListener(type, guardOpeningGesture, true);
        });
    }

    /* The first frame a fade-in can be told apart from what is under it:
       about 5 of 255 levels over the lobby. And a cap on the wait, for an
       engine whose fade never reports progress: past the 200 ms view-enter
       fade at 60 Hz. */
    const FIRST_VISIBLE_OPACITY = 0.02;
    const LOBBY_ASIDE_MAX_FRAMES = 15;

    function paintsVisibly(element) {
        try {
            return Number(window.getComputedStyle(element).opacity) >= FIRST_VISIBLE_OPACITY;
        } catch (_) {
            return true;
        }
    }

    /**
     * Whether an entrance animation on `element` starts it fully transparent,
     * so the frame it begins in shows nothing of it.
     */
    function entersFromTransparent(element) {
        if (typeof element?.getAnimations !== 'function') return false;
        try {
            return element.getAnimations().some((animation) => {
                if (animation.playState === 'finished') return false;
                const first = animation.effect?.getKeyframes?.()?.[0];
                return first?.opacity != null && Number(first.opacity) === 0;
            });
        } catch (_) {
            return false;
        }
    }

    /*
     * A SURFACE REVEALED BY LIFTING ITS HOLD IS WHOLE IN ITS FIRST FRAME.
     *
     * The open's hold (`.collection-modal-preparing`) and the card detail's
     * stage hide what they build with `visibility: hidden`, which every
     * descendant inherits, and the tabs, buttons, tiles and card faces
     * transition `all` (deck-builder.css), visibility included. Lifting the
     * hold starts a visibility transition on each of them, and a transition
     * paints its start value, hidden, on its first frame; a card face
     * transitions inside its transitioning tile, and its own only starts once
     * the tile's has moved, a frame later. Measured 25-26 September 2026 in
     * WebKit, Chromium and Firefox, sampling what each frame paints with the
     * real stylesheets: the tabs, pager and tiles one frame after the hold
     * lifted, each card face two, the detail's Back, Craft and Stellar
     * buttons one — on the WebKit phone, the reopened Collection's 42 card
     * elements 437 ms after its modal, the warm open's 40.
     *
     * Reading the animations computes the reveal's style, so those
     * transitions exist now: each visibility transition ending visible lands
     * on its end, pass after pass until a nested one has nothing left to
     * start. Every other transition — a colour, a designed delayed hide —
     * keeps running. (surface-entry-hold.js does the same for the tabs'
     * bodies.)
     */
    const REVEAL_TRANSITION_PASSES = 4;

    function revealsVisibility(animation) {
        if (animation?.transitionProperty !== 'visibility' || animation.playState === 'finished') return false;
        try {
            const frames = animation.effect?.getKeyframes?.();
            const last = Array.isArray(frames) && frames.length > 0 ? frames[frames.length - 1] : null;
            return !last?.visibility || last.visibility === 'visible';
        } catch (_) {
            return true;
        }
    }

    function landRevealTransitions(root) {
        if (!root?.isConnected || typeof root.getAnimations !== 'function') return;
        for (let pass = 0; pass < REVEAL_TRANSITION_PASSES; pass += 1) {
            let pending = [];
            try {
                pending = root.getAnimations({ subtree: true }).filter(revealsVisibility);
            } catch (_) {
                return;
            }
            if (pending.length === 0) return;
            pending.forEach((animation) => {
                try {
                    animation.finish();
                } catch (_) {
                    // An engine may refuse one; the next frame still shows it.
                }
            });
        }
    }

    /*
     * WARMED BEFORE THE PRESS. The lobby calls this at idle once the page has
     * loaded (and on hover, focus or the first touch of the Collection tile):
     * the first page is read at the largest page the grid can show — the grid
     * cuts whatever it measures out of it (collection-page-cache.js) — and its
     * art is fetched at low priority, with the forge price its tiles are built
     * from (one read the Foundry's own price warm shares) and the media of the
     * owned Stellar cards on it. One page: never the catalog, and nothing
     * about the whole library. The recipe discoveries (up to 4,000 rows) and
     * the first-discovery ribbons (on the server, a walk of every card the
     * player owns) rode this warm too, every lobby arrival — and the open read
     * both again (read audit R3/R4, 26 September 2026; the owner: "it's fine
     * to load things, but not a players entire library"). The open reads them
     * now, the ribbons from its first task (startFirstDiscoveryRead).
     */
    function warmForLobby({ priority = 'low', maxAgeMs = PAGE_PRESENT_MAX_AGE_MS } = {}) {
        const account = Firebase?.getUser?.()?.uid || '';
        if (isOpen || !account) return;
        void loadForgeCosts();
        const query = openingCardsQuery();
        if (priority === 'low') {
            // After a change the server's caches may still hold the holdings from before it.
            warmLobbyWindow(query, account, maxAgeMs, { refresh: cardsNeedRemoteRefresh });
            return;
        }
        // An open intent: the first page, fresh, its pictures at the press's priority.
        void cardsPages.request(query, 1, CARDS_PAGE_SIZE, { prefetch: true, priority, maxAgeMs })
            .then((served) => {
                if ((Firebase?.getUser?.()?.uid || '') === account) warmStellarMediaFor(served?.page?.items);
            }, () => {});
    }

    /*
     * THE LOBBY'S WINDOW (the owner, 28 September 2026: "from the lobby, we
     * could preload like three pages of anything"). The idle warm reads the
     * first LOBBY_WARM_PAGES pages of the tab the Collection opens on in ONE
     * answer (the grid cuts its pages out of it, collection-page-cache.js),
     * the other card tab's first page, and the card backs' list: three small
     * reads. Their pictures come after the lobby's own, as bytes only: the
     * first page's as before (the open is held for them), then the second
     * page, the other tab's first page and the third page, ranked behind the
     * other lobby doors' warms, and every card back once the first page's are
     * in (warmCardbackBytes). A match
     * owns the screen and the link: while one is starting, on or being
     * resumed, nothing new starts, and the rest goes on in the lobby after
     * it. A constrained link carries the first page only. The Collection's
     * own window takes these pictures over when it opens (declareFarLookAhead
     * retires this scope once its own has asked for them).
     */
    const LOBBY_WARM_PAGES = 3;
    // Longer than a match: the window waits the match out, and goes on after it.
    const LOBBY_WARM_MATCH_WAIT_CAP_MS = 60 * 60 * 1000;
    const MATCH_BODY_CLASSES = ['game-active', 'game-transition-pending', 'game-resuming'];
    /** @type {any} the lobby window's pictures beyond the first page */
    let lobbyAhead = null;

    function matchOwnsScreen() {
        const body = document.body;
        return Boolean(body) && MATCH_BODY_CLASSES.some((name) => body.classList.contains(name));
    }

    function lobbyWarmIsCurrent(account) {
        return !isOpen && account === (Firebase?.getUser?.()?.uid || '');
    }

    function warmLobbyWindow(query, account, maxAgeMs, { refresh = false } = {}) {
        lobbyWarmAccount = account;
        const epoch = pageDataEpoch;
        const pages = cardsPages.request(query, 1, CARDS_PAGE_SIZE * LOBBY_WARM_PAGES, { maxAgeMs, refresh });
        // After a refreshed read the server's build is the new one: the other tab needs no second rebuild.
        const readOtherTab = () => cardsPages.request(otherCardTabQuery(query), 1, CARDS_PAGE_SIZE, { maxAgeMs });
        const otherTab = refresh ? pages.then(readOtherTab, readOtherTab) : readOtherTab();
        void pages.then((served) => {
            if (refresh && served && pageDataEpoch === epoch) freshAnswersEpoch = epoch;
            if (!served || !lobbyWarmIsCurrent(account)) return;
            const items = Array.isArray(served.page?.items) ? served.page.items : [];
            const first = items.slice(0, CARDS_PAGE_SIZE);
            warmStellarMediaFor(first);
            const firstPictures = warmPageArt(first, 'low');
            void otherTab.then((other) => {
                if (!lobbyWarmIsCurrent(account)) return;
                // The second page, the other tab's first, then the third.
                declareLobbyLookAhead([
                    pictureUrlsOf(items.slice(CARDS_PAGE_SIZE, CARDS_PAGE_SIZE * 2)),
                    pictureUrlsOf(other?.page?.items),
                    pictureUrlsOf(items.slice(CARDS_PAGE_SIZE * 2)),
                ].map((urls) => urls.filter((url) => !pictureUrlsOf(first).includes(url))));
            });
            // The backs are not the scheduler's (no-CORS, as their tiles load
            // them): they start once the first page's pictures are in.
            void Promise.all([Promise.resolve(firstPictures), loadCardbacks()]).then(() => {
                if (lobbyWarmIsCurrent(account) && !matchOwnsScreen()) warmCardbackBytes();
            }, () => {});
        }, () => {});
    }

    function declareLobbyLookAhead(groups) {
        retireLobbyLookAhead();
        const scheduler = lookAheadScheduler();
        const pictures = [];
        groups.forEach((urls, index) => urls.forEach((url) => pictures.push({ url, rank: FAR_AHEAD_RANK + index })));
        if (!scheduler || pictures.length === 0) return;
        // The lobby's own pace (background-soon): it yields to the lobby's presses and pictures.
        const scope = scheduler.scope('collection-lobby-look-ahead', {
            runsOn: LOOK_AHEAD_SCREENS,
            maxInFlight: LOOK_AHEAD_AT_ONCE,
            admit: admitFarAhead,
        });
        scope.waitWhile?.('a match owns the screen', matchOwnsScreen, LOBBY_WARM_MATCH_WAIT_CAP_MS);
        lobbyAhead = scope;
        pictures.forEach((picture) => {
            Promise.resolve(scope.soon([picture])).then((result) => {
                if (Number(result?.ready) > 0 || Number(result?.failed) > 0) notePicturesInHand([picture.url]);
            }, () => {});
        });
    }

    /** The lobby window's pictures let go (planned again, or the window ended). */
    function retireLobbyLookAhead() {
        const scope = lobbyAhead;
        lobbyAhead = null;
        scope?.close?.();
    }

    /* The lobby's window is over: the Collection's own window has taken its
       pictures over, or the Collection closed or reset (another account).
       Nothing keeps it current after that; the Collection's answers are the
       ones a change drops, and its next open reads its page again. */
    function endLobbyWindow() {
        retireLobbyLookAhead();
        lobbyWarmAccount = null;
    }

    /*
     * AND KEPT CURRENT (the owner, 28 September 2026: "the entire game is
     * dynamic ... If something changes, you should update the preloaded
     * things"). A change to the holdings (a fusion, a Moon Dream run, a
     * Stellar print revoked) or to the language while the Collection is
     * closed has dropped every answer read before it (markHoldingsChanged,
     * invalidateCardsCache; answers are asked for per language). The lobby's
     * window, planned from those answers, is planned again at the next idle
     * moment from what the server answers now: once for any number of
     * changes that came together, for the account it was warmed for, and
     * never under a match.
     */
    // The account the lobby's window was warmed for, if any.
    let lobbyWarmAccount = null;
    let lobbyRewarmPending = false;

    function rewarmLobbyWindow() {
        retireLobbyLookAhead();
        if (!lobbyWarmAccount || lobbyRewarmPending) return;
        lobbyRewarmPending = true;
        const run = () => {
            lobbyRewarmPending = false;
            if (isOpen || matchOwnsScreen() || lobbyWarmAccount !== (Firebase?.getUser?.()?.uid || '')) return;
            warmForLobby();
        };
        if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(run, { timeout: 2000 });
        else setTimeout(run, 200);
    }

    /*
     * AND ITS STELLAR LOOPS. A Stellar card animates at rest, and its loop
     * layer shows only once the loop is playing (cards.css). The page's
     * Stellar media used to be read after the page committed, so each loop
     * was attached, fetched and started after the Collection appeared: on
     * the Chrome phone seat the first page's two loops arrived 540-630 ms
     * after it, two pop-ins on every warm and revisit open (round 13). Read
     * with the warmed page — one small read, for the owned Stellar cards on
     * it and no other — the open builds those tiles with their loops, which
     * load and start in the hidden stage while the pictures settle. An open
     * that finds the read still out waits for it (renderCards), capped.
     */
    let stellarMediaWarm = null;
    // Each account's cards read once: the tile's hover and focus warm again.
    const stellarMediaWarmed = new Set();
    const STELLAR_WARM_JOIN_CAP_MS = 1500;

    function warmStellarMediaFor(items) {
        if (typeof StellarAssets?.load !== 'function' || !Array.isArray(items)) return;
        const account = Firebase?.getUser?.()?.uid || '';
        const ids = Array.from(new Set(items
            .filter((entry) => entry?.variant === 'stellar' && Number(entry.ownedQty) > 0 && entry.card?.id)
            .map((entry) => entry.card.id)
            // A print last read attuning has moved on with nobody watching: it is read again.
            .filter((id) => printLastReadInFlux(id)
                || (!getStellarAsset(id) && !stellarMediaWarmed.has(`${account}:${id}`)))));
        if (ids.length === 0) return;
        ids.forEach((id) => stellarMediaWarmed.add(`${account}:${id}`));
        const warm = { ids: new Set(ids), promise: null };
        warm.promise = Promise.resolve()
            .then(() => StellarAssets.load(ids))
            .catch(() => {})
            .finally(() => {
                if (stellarMediaWarm === warm) stellarMediaWarm = null;
            });
        stellarMediaWarm = warm;
    }

    /** The warm's read, when it is still out for a Stellar card among `entries`. */
    function stellarMediaWarmFor(entries) {
        const warm = stellarMediaWarm;
        if (!warm) return null;
        const pending = (entries || []).some((entry) => (
            entry?.variant === 'stellar' && warm.ids.has(entry.card?.id)
                && (!getStellarAsset(entry.card.id) || printLastReadInFlux(entry.card.id))
        ));
        return pending ? warm.promise : null;
    }

    function printLastReadInFlux(cardId) {
        return StellarAssets?.isGenerating?.(cardId) === true;
    }

    /* AND A REOPEN SHOWS WHERE ITS PRINTS ARE NOW. While the Collection is
       closed nothing reads its prints, so one that finished meanwhile is still
       "Attuning" in the store and on the page kept from the last open. The
       lobby's warm (hover, focus or first touch of its tile) reads such prints
       again, so does the open itself when no warm did, and an open that finds
       that read out holds for it, as a first open does for its loops: the page
       it presents is already rebuilt. */
    function committedTileEntries() {
        const grid = document.getElementById('collection-cards-grid');
        return Array.from(grid?.children || []).map((tile) => tileEntries.get(tile)).filter(Boolean);
    }

    function rereadKeptPrintsInFlux() {
        const entries = committedTileEntries();
        if (!stellarMediaWarmFor(entries)) warmStellarMediaFor(entries);
    }

    function committedPageAwaitsStellarRead() {
        return Boolean(stellarMediaWarmFor(committedTileEntries()));
    }

    /*
     * AN OPEN STILL BEING FINISHED OFF-SCREEN IS NOT STARTED AGAIN. The held
     * modal is unseen (visibility: hidden), so the second press of a double
     * press lands on the lobby tile again; it re-ran the whole open and
     * restarted the grace from the second press. The same open asked for
     * again is the one already under way.
     */
    /** @type {{ generation: number, section: string, promise: Promise<boolean> } | null} */
    let openInFlight = null;

    function open(section = 'cards', options = {}) {
        const requestedSection = getValidCollectionSection(section);
        const inFlight = openInFlight;
        if (inFlight
            && isOpen
            && inFlight.generation === openGeneration
            && inFlight.section === requestedSection
            && collectionPresentationHeld()) {
            return inFlight.promise;
        }
        const promise = openCollection(section, options);
        openInFlight = { generation: openGeneration, section: requestedSection, promise };
        return promise;
    }

    async function openCollection(section = 'cards', { opener = null } = {}) {
        if (Store?.isOpen?.()) return false;
        if (document.body?.classList.contains('game-transition-pending')
            || document.body?.classList.contains('game-active')) {
            return false;
        }
        const generation = ++openGeneration;
        const requestedSection = getValidCollectionSection(section);
        cancelCollectionFocusRestore();

        const modal = document.getElementById('collection-modal');
        if (!modal || !claimLobbySurface()) return false;
        // Stellar generation can complete while this surface is open. Warm
        // its completion cue on entry so that first completion is timely too.
        void SoundEffects?.preload?.(['magic']);
        // The Exchange tab is one click away: read its config now so the sell
        // column's answer is on the first frame of that tab (MoonExchange.prepare).
        void MoonExchange?.warmConfig?.();
        // Likewise the Packs tab: its odds, counts and any open pack are read
        // now and painted before its first frame (CardPacks.prepare). Its
        // wrapper follows the visible page's pictures, unless Packs is opened.
        void CardPacks?.warm?.({ wrapperAfter: packWrapperAfterPage(generation, requestedSection) });
        // The Exchange tab's config and first listings page, at idle after the
        // first paint (scheduleNeighbourTabWarm); the Decks tab warms itself
        // on intent.
        scheduleNeighbourTabWarm(generation);

        if (!isOpen) {
            const activeElement = document.activeElement;
            collectionReturnFocus = activeElement && activeElement !== document.body
                ? activeElement
                : document.getElementById('open-collection-btn');
            beginOpeningGesture(opener);
        }
        isOpen = true;
        cardsPage = 1;
        activeSpellOwnerFilter = 'all';
        const searchInput = /** @type {HTMLInputElement | null} */ (document.getElementById('collection-search'));
        setCurrentSearch(searchInput?.value || '');
        /* Covered BEFORE it is displayed. Marked preparing only after
           display:flex, a layout read in between flushed an uncovered style;
           the tabs and pager (`transition: all`) then kept `visibility:
           visible` through their transition and flashed over the lobby for
           ~0.45 s (Chrome, 24 September 2026). An open that needs no hold
           uncovers it again below, in this same task. */
        modal.classList.add('collection-modal-preparing');
        modal.style.display = 'flex';
        modal.setAttribute('aria-hidden', 'false');
        installCollectionKeyHandler(modal);

        /* Unknown must look unknown — but the figure the lobby chip is showing
           underneath this modal IS known: it came from the lobby's own profile
           read and follows every announced spend. Printing it on the first
           frame is what keeps the chip from going "—" → "5K" and shoving the
           tabs and the search box sideways; this open's own read confirms it. */
        if (!Number.isFinite(Number(stardust))) {
            const lobbyBalance = lastKnownStardust();
            if (lobbyBalance !== null) stardust = lobbyBalance;
        }
        updateStardustDisplay();
        startFirstDiscoveryRead(generation);

        recipeDetailsByKey = new Map();
        if (stellarPollTimer) {
            clearTimeout(stellarPollTimer);
            stellarPollTimer = null;
        }

        // switchSection records the request synchronously, so later bootstrap
        // work can observe the user's chosen surface without pre-mutating the
        // previous-section value used for hover lifecycle invalidation.
        const deferSectionOpen = requestedSection === 'exchange' || requestedSection === 'packs';
        if (deferSectionOpen) {
            suspendedSectionOpenGeneration = generation;
        }
        switchSection(requestedSection, { skipSectionOpen: true });

        /* The Cards grid opens FINISHED: the modal is laid out but held
           invisible until its first page is committed (data, decoded art,
           settled rasters, pager and balance), then presented in one frame.
           A page the grid already shows that still answers the question (a
           reopen on page 1) needs no hold at all. The press is answered on
           the opener meanwhile, and the hold is capped. */
        // ...unless the holdings changed since it was painted
        // (markHoldingsChanged): then it is held until the new page is in.
        // ...or a print on it moved since, or is being read again (its tile
        // is rebuilt before it is shown: committedPageAwaitsStellarRead).
        rereadKeptPrintsInFlux();
        const pageAlreadyAnswers = !cardsNeedRemoteRefresh
            && paintedCardsContentSignature === cardsContentSignature()
            && gridHasCommittedPage()
            && !gridShowsOutdatedStellarTile()
            && !committedPageAwaitsStellarRead();
        if (activeSection === 'cards' && !deferSectionOpen && !pageAlreadyAnswers) {
            document.getElementById('collection-cards-pagination')
                ?.classList.toggle('collection-pagination--pending', !gridHasCommittedPage());
            const hold = holdCollectionPresentation(modal, generation, opener);
            if (Number.isFinite(Number(stardust))) hold.balanceReady = true;
        } else {
            releaseCollectionPresentation();
            presentCollectionNow(modal);
        }

        try {
            await loadData({ lightweight: deferSectionOpen, generation });
        } finally {
            if (suspendedSectionOpenGeneration === generation) {
                suspendedSectionOpenGeneration = 0;
            }
        }
        if (!collectionSessionIsCurrent(generation)) return false;
        if (activeSection === requestedSection) {
            const exchangePromise = switchSection(activeSection, {
                skipCardsRender: activeSection === 'cards',
                ownerGeneration: generation,
            });
            if (exchangePromise && typeof exchangePromise.then === 'function') {
                await exchangePromise;
            }
        }
        return collectionSessionIsCurrent(generation, requestedSection);
    }

    function close({ restoreFocus = true } = {}) {
        const wasOpen = isOpen;
        invalidateHoverSurface();
        isOpen = false;
        openGeneration += 1;
        openInFlight = null;
        openingGesture = null;
        suspendedSectionOpenGeneration = 0;
        for (const controller of cardsWarmOwners) controller.abort();
        cardsWarmController = null;
        packWrapperGate = null;
        lastCardsRequestId += 1;
        shownCardsTurn = null;
        cancelCollectionFocusRestore();
        collectionRevealTimers.forEach((timer) => clearTimeout(timer));
        collectionRevealTimers.clear();
        // The page built ahead has no reader either; a reopen starts on page 1.
        dropCardsPrebuild();
        // Nor do the pictures read ahead for it: the player moved on.
        dropAllLookAheads();
        document.querySelectorAll('.collection-card-paint-stage').forEach((stage) => stage.remove());
        // A queued prefetch has no reader once the surface closes; the pages
        // already read stay (they serve the next open).
        clearTimeout(cardsPrefetchTimer);
        cardsPrefetchTimer = null;
        releaseCollectionPresentation();
        releaseCardsPagerAck();
        // A tab kept on screen for a held one goes as it was.
        finishSectionHandover();
        markCardsGridStale(document.getElementById('collection-cards-grid'), false);
        removeCollectionKeyHandler();
        closeCardDetail();
        disposeCollectionExactImages(document.getElementById('collection-cards-grid'));
        disposeRetryingCardbackImages(document.getElementById('collection-cardback-grid'));
        if (MoonExchange?.close) {
            MoonExchange.close();
        }
        if (CardPacks?.close) {
            CardPacks.close();
        }
        if (stellarPollTimer) {
            clearTimeout(stellarPollTimer);
            stellarPollTimer = null;
        }
        if (DeckBuilder?.close && DeckBuilder.isOpen?.()) {
            DeckBuilder.close();
        }
        // Its tab's pictures read ahead for this open, still loading, go with it.
        DeckBuilder?.dropWarm?.();
        deckBuilderSectionReady = false;
        deckBuilderSectionOpening = null;
        const modal = document.getElementById('collection-modal');
        if (modal) {
            modal.style.display = 'none';
            modal.setAttribute('aria-hidden', 'true');
        }
        restoreCollectionBackground();
        releaseLobbySurface();
        const returnFocus = collectionReturnFocus;
        collectionReturnFocus = null;
        if (restoreFocus) restoreCollectionFocus(returnFocus, modal);
        if (toastHost && toastHost.parentNode) {
            toastHost.parentNode.removeChild(toastHost);
            toastHost = null;
        }
        return wasOpen;
    }

    function loadCollectionSupportData() {
        if (!collectionSupportDataPromise) {
            collectionSupportDataPromise = Promise
                .all([loadForgeCosts(), loadRecipes(), loadCardbacks()])
                .finally(() => {
                    collectionSupportDataPromise = null;
                });
        }
        return collectionSupportDataPromise;
    }

    async function loadData({ lightweight = false, generation = openGeneration } = {}) {
        // loadProfile's sync prelude (state reset) runs immediately; the profile
        // fetch races against the cards-page fetch instead of blocking it.
        const profilePromise = loadProfile(generation);
        /* THE CHIP DOES NOT WAIT FOR THE CARDS. The profile is a ~160ms read
           and the cards page a ~2s one, and the balance sat on the finished
           read for the whole difference (measured on the real backend: first
           digits 2.3s after open). It paints the moment the profile lands;
           the later calls below are the same idempotent paint. */
        void profilePromise.then((loaded) => {
            if (!loaded || !collectionSessionIsCurrent(generation)) return;
            updateStardustDisplay();
            // A detail presented at its cap before the balance was known says so now.
            refreshDetailCraftRow();
            /* NOR DOES THE TILL. The Packs tab's Buy button and the Exchange
               price their actions against this balance; handed over only
               after the cards page, the Buy button stayed disabled for
               seconds after the vault was ready on a phone (measured 25
               September 2026). The later syncs below repeat it with the page. */
            void syncAuxiliaryContexts();
        }).catch(() => {}).finally(() => {
            // Answered or failed, the chip now says what is true.
            if (collectionSessionIsCurrent(generation)) markCollectionPresentable('balanceReady');
        });
        setCurrentSearch(document.getElementById('collection-search')?.value || '');
        if (lightweight) {
            await Promise.all([
                profilePromise,
                activeSection === 'packs' ? loadCardbacks() : Promise.resolve(),
            ]);
            if (!collectionSessionIsCurrent(generation)) return false;
            updateStardustDisplay();
            await syncAuxiliaryContexts();
            return collectionSessionIsCurrent(generation);
        }

        // Foundry-discovery repair is a one-time idempotent backfill. Chain on the
        // profile so we have profileId for caching, but otherwise run in parallel.
        const repairPromise = profilePromise
            .then((loaded) => (
                loaded && collectionSessionIsCurrent(generation)
                    ? repairOwnedFoundryDiscoveries()
                    : false
            ))
            .catch((err) => {
                console.warn('[Collection] background discovery repair failed:', err);
                return false;
            });

        const supportDataPromise = loadCollectionSupportData();
        /* The page does not wait for the recipe read. The server decides which
           cards the page holds; the client's recipe map only ever decorated
           the detail, and the first read waited for here so that its arrival
           would not count as a CHANGE and invalidate the committed page. The
           first read is a baseline now (`commitRecipes`), so the open no
           longer spends a round trip on it before the grid can commit. */
        await renderCards(undefined, { resetScroll: true, revalidateAfterMs: PAGE_REVALIDATE_AFTER_MS });
        if (!collectionSessionIsCurrent(generation)) return false;
        releasePackWrapperAfterPage(generation);
        await profilePromise;
        if (!collectionSessionIsCurrent(generation)) return false;
        updateStardustDisplay();
        await syncAuxiliaryContexts();
        if (!collectionSessionIsCurrent(generation)) return false;
        await supportDataPromise;
        if (!collectionSessionIsCurrent(generation)) return false;
        applyCardsSupportDecorations();
        await loadStellarAssets();
        if (!collectionSessionIsCurrent(generation)) return false;
        attachStellarVideosToGrid();
        await syncAuxiliaryContexts();
        if (!collectionSessionIsCurrent(generation)) return false;
        /* Rendered behind the Cards grid, with EAGER images: the backs the
           Cardbacks tab opens on are fetched and decoded while the player reads
           their cards, so entering the tab presents every back at once — once
           the next page's pictures are in (renderCardbacksAfterNextPage). */
        renderCardbacksAfterNextPage(generation);
        warmDecksTab(generation);

        const didRepair = await repairPromise;
        if (didRepair && collectionSessionIsCurrent(generation, 'cards')) {
            cardsNeedRemoteRefresh = true;
            await renderCards(undefined, { refresh: true });
        }
        return collectionSessionIsCurrent(generation);
    }

    /**
     * Everything the profile owns, back to "nothing is known". Called when the
     * account changes or the read fails — never speculatively, because doing it
     * up front made a real balance display 0 for the whole load and then jump,
     * and made every re-open a cold start with nothing to show.
     */
    function resetProfileState() {
        stardust = undefined;
        profileId = null;
        profileFirebaseUid = null;
        profileUsername = '';
        unlockedCardbacks = ['default'];
        selectedCardback = 'default';
        ownedCardMap = new Map();
        ownedCardStellarMap = new Map();
        recipeByResultCardId = new Map();
        discoveredRecipeCardIds = new Set();
        recipesBaselineRead = false;
        firstDiscoveryBadges = { fuse: new Set(), stellar: new Set() };
        firstDiscoveryBadgesProfileId = null;
        firstDiscoveryBadgesGeneration = 0;
        firstDiscoveryRead = null;
        forgetRetiredTiles();
        dropCardsPrebuild();
        dropAllLookAheads();
        replaceCardCache([]);
        availableSpellOwners = [];
        paintedCardsContentSignature = null;
        spellOwnerNamesByKey = new Map();
        availableCollections = [];
        collectionsResolved = false;
        renderedCollectionOptionsKey = null;
        auxiliaryAllCards = [];
        auxiliaryCatalogLanguage = null;
        auxiliaryCatalogComplete = null;
        auxiliaryInventoryStale = false;
        auxiliaryInventoryRevision += 1;
        if (StellarAssets?.reset) {
            StellarAssets.reset();
        }
        cardsNeedRemoteRefresh = false;
        invalidateCardsCache();
    }

    function loadProfile(generation = openGeneration) {
        const read = readProfile(generation);
        pendingProfileRead = read;
        const settle = () => {
            if (pendingProfileRead === read) pendingProfileRead = null;
        };
        read.then(settle, settle);
        return read;
    }

    async function readProfile(generation) {
        const previousProfileId = profileId;

        if (!MoonfallCloud?.isReady?.() || !MoonfallCloud?.users) {
            if (collectionSessionIsCurrent(generation)) resetProfileState();
            return false;
        }

        try {
            const profile = await MoonfallCloud.users.getProfile();
            if (!collectionSessionIsCurrent(generation)) return false;
            if (!profile) {
                resetProfileState();
                return false;
            }
            // A different account may never inherit the previous one's cached
            // inventory, recipes or balance.
            //
            // Only a DIFFERENT one. `!previousProfileId` used to reset here
            // too, which fires on every first open — where there is no previous
            // account to protect against, because `resetProfileState` already
            // ran at close/sign-out and leaves `profileId` null only alongside
            // empty maps. That reset landed in the middle of its own open: the
            // recipe read and the profile read resolve within a few ms of each
            // other, so whichever finished second won. Measured on the real dev
            // backend in Firefox, it threw away a complete 14-recipe read about
            // 20% of the time, the page then committed with none, and the
            // support batch's second read reported a genuine change that
            // rebuilt the whole grid on the next tab return.
            if (previousProfileId && profile.id !== previousProfileId) {
                resetProfileState();
            }
            profileId = profile.id || null;
            // Ribbons read in the open's first task for an account this is not.
            if (firstDiscoveryBadgesProfileId !== null && firstDiscoveryBadgesProfileId !== profileId) {
                firstDiscoveryBadges = { fuse: new Set(), stellar: new Set() };
                firstDiscoveryBadgesProfileId = null;
                applyFirstDiscoveryDecorations();
            }
            profileFirebaseUid = typeof profile.firebaseUid === 'string' && profile.firebaseUid
                ? profile.firebaseUid
                : null;
            profileUsername = typeof profile.username === 'string' ? profile.username.trim() : '';
            stardust = profile.stardust ?? 0;
            // The lobby chip under this surface is painted once at sign-in;
            // a fresh profile read is the authoritative figure, so it heals a
            // chip another surface (Foundry, Moon Dream) left stale.
            announceStardust(stardust);
            unlockedCardbacks = profile.unlockedCardbacks || ['default'];
            selectedCardback = profile.selectedCardback || 'default';
            const committedProfileId = profileId;
            const committedFirebaseUid = profileFirebaseUid;
            // Decorate in place once badges land: the grid usually
            // paints before this fetch resolves, and a full renderCards()
            // would refetch the page (breaking the one-round-trip open).
            // The open's own first-task read, when it asked for this account, is the answer.
            void refreshFirstDiscoveryBadges({
                generation,
                expectedProfileId: committedProfileId,
                join: true,
            }).then((updated) => {
                if (updated && collectionSessionIsCurrent(generation)) {
                    applyFirstDiscoveryDecorations();
                }
            });
            void requestStellarFirstBanner({
                generation,
                expectedProfileId: committedProfileId,
                expectedFirebaseUid: committedFirebaseUid,
            });
            return true;
        } catch (err) {
            if (!collectionSessionIsCurrent(generation)) return false;
            console.warn('[Collection] Failed to load profile:', err);
            const currentFirebaseUid = Firebase?.getUser?.()?.uid || null;
            const definitelyDifferentAccount = Boolean(
                profileFirebaseUid
                && currentFirebaseUid
                && profileFirebaseUid !== currentFirebaseUid
            );
            if (!previousProfileId || definitelyDifferentAccount) resetProfileState();
            return false;
        }
    }

    async function loadForgeCosts() {
        if (forgeCosts) return;
        // One read of the price table for the Collection and the Foundry's
        // warm, which used to ask for it side by side on every boot.
        const table = await FoundryInventoryWarm.costTable();
        if (table) forgeCosts = table;
        else console.warn('[Collection] Failed to load forge costs.');
    }

    /*
     * THE SUMMONERS OF THE SPELLS ON SCREEN, AND NO OTHERS. Every open
     * downloaded the whole roster (`/api/characters`: every public summoner
     * and every one the player dreamt, each localized, each with a portrait
     * URL) to name the owner of a Spell, and every language change downloaded
     * it again (read audit R18, 26 September 2026). The page answer names every
     * owner it offers (`spellOwnerNames`), which labels the tiles and the
     * Spells filter; only a Spell's detail shows more, its summoner's portrait.
     * So the owners of the Spells a committed page shows are read — one small
     * read for those not read yet (the server takes up to 20) — as are a
     * tile's on intent (warmCardDetail) and a detail's as it is built, within
     * its cap. A failed read keeps the names the player already had, and may
     * be asked again.
     */
    const SPELL_OWNER_READ_CAP = 20;
    const spellOwnerReads = new Map(); // `${language}:${key}` -> Promise<void>
    const spellOwnersRead = new Set(); // `${language}:${key}` answered (found or not)

    function spellOwnerMemo(key) {
        return `${i18n?.getLanguage?.() || 'en'}:${key}`;
    }

    function spellOwnerKnown(key) {
        return spellOwnersRead.has(spellOwnerMemo(key));
    }

    function readSpellOwners(keys) {
        const wanted = Array.from(new Set((keys || []).filter(Boolean)));
        const missing = wanted.filter((key) => !spellOwnerReads.has(spellOwnerMemo(key))).slice(0, SPELL_OWNER_READ_CAP);
        if (missing.length > 0) {
            const lang = i18n?.getLanguage?.() || 'en';
            const memos = missing.map(spellOwnerMemo);
            const read = (async () => {
                const headers = await buildAuthHeaders();
                const params = new URLSearchParams({ characterIds: missing.join(','), lang });
                const response = await fetch(`/api/characters/details?${params.toString()}`, { headers });
                if (!response.ok) throw new Error('Failed to fetch summoners');
                const data = await response.json();
                const list = Array.isArray(data) ? data : [];
                list.forEach((char) => {
                    const portraitUrl = sanitizeImageUrlForDirectClient(char?.imageUrl);
                    // The keys a Spell names its summoner by: its deck key, or its id.
                    const keysOf = [char?.deckKey, char?.characterId];
                    if (missing.length === 1 && list.length === 1) keysOf.push(missing[0]);
                    keysOf.filter(Boolean).forEach((key) => {
                        if (char?.name) characterNameByKey.set(key, char.name);
                        characterPortraitByKey.set(key, portraitUrl);
                    });
                });
                memos.forEach((memo) => spellOwnersRead.add(memo));
                // An owner the page could not name is named now (a no-op otherwise).
                if (isOpen && list.length > 0) applyCardsSupportDecorations();
            })().catch((err) => {
                memos.forEach((memo) => {
                    if (spellOwnerReads.get(memo) === read) spellOwnerReads.delete(memo);
                });
                console.warn('[Collection] Failed to load the Spells\' summoners:', err);
            });
            memos.forEach((memo) => spellOwnerReads.set(memo, read));
        }
        const reads = wanted.map((key) => spellOwnerReads.get(spellOwnerMemo(key))).filter(Boolean);
        return Promise.all(reads).then(() => undefined);
    }

    function readSpellOwner(characterKey) {
        return readSpellOwners([characterKey]);
    }

    function spellOwnerOf(card) {
        return String(card?.type || '').toLowerCase() === 'spell' ? card.character || null : null;
    }

    // Label lookup for the Collection dropdown. A collection the catalog grows
    // later still appears — under its own name until it earns a translation.
    const COLLECTION_FILTER_LABELS = {
        'Classic': ['collection.filters.classic', 'Classic'],
        'Mythos': ['collection.filters.mythology', 'Mythology'],
        'Anime Archetypes': ['collection.filters.anime', 'Anime'],
        'Public Domain': ['collection.filters.publicDomain', 'Public Domain'],
        'Brainrot': ['collection.filters.brainrot', 'Brainrot'],
        'Cute Creatures': ['collection.filters.cuteCreatures', 'Cute Creatures'],
        'Fun': ['collection.filters.fun', 'Fun'],
        'Moon Foundry': ['collection.filters.crafted', 'Crafted'],
    };

    function getCollectionFilterLabel(name) {
        const entry = COLLECTION_FILTER_LABELS[name];
        return entry ? uiText(entry[0], entry[1]) : name;
    }

    /**
     * How wide the engine would make this control if it held every label.
     *
     * Measured with a real, off-screen <select> rather than canvas text plus a
     * constant for the dropdown arrow: WebKit reserves more chrome than Blink,
     * so a canvas estimate under-shot it by 6px and the toolbar still moved.
     * The clone carries the same class and lives in the same parent, so it
     * inherits the identical computed font, padding and arrow.
     */
    function measureWidestSelectWidth(select, labels) {
        const parent = select.parentElement;
        if (!parent) return 0;
        const probe = document.createElement('select');
        probe.className = select.className;
        probe.setAttribute('aria-hidden', 'true');
        probe.tabIndex = -1;
        probe.style.cssText = 'position:absolute;left:-9999px;top:0;'
            + 'visibility:hidden;pointer-events:none;width:auto;min-width:0;max-width:none;';
        probe.innerHTML = labels
            .map((label) => `<option>${escapeHtml(label)}</option>`)
            .join('');
        parent.appendChild(probe);
        const width = Math.ceil(probe.getBoundingClientRect().width);
        probe.remove();
        return Number.isFinite(width) && width > 0 ? width : 0;
    }

    /**
     * Hold the collection filter at the width its widest POSSIBLE label needs.
     * A <select> sizes to its widest option, so the control grew when the real
     * list replaced the two bootstrap options and moved the toolbar after first
     * paint. Measured rather than constant: labels are localized and the font is
     * viewport derived. The phone popover sizes its selects to 100%, so the
     * reservation is dropped there.
     */
    function reserveCollectionFilterWidth() {
        const select = document.getElementById('collection-filter-collection');
        if (!select) return;
        const popover = document.getElementById('collection-filter-popover');
        const stacked = popover && window.getComputedStyle(popover).display !== 'contents';
        if (stacked) {
            select.style.removeProperty('min-width');
            return;
        }
        const labels = [
            uiText('collection.filters.allCollections', 'All Collections'),
            uiText('collection.filters.stellar', 'Stellar'),
            uiText('collection.filters.collectionsLoading', 'Loading…'),
            ...Object.keys(COLLECTION_FILTER_LABELS).map((name) => getCollectionFilterLabel(name)),
        ];
        const reserved = measureWidestSelectWidth(select, labels);
        if (reserved > 0) select.style.minWidth = `${reserved}px`;
    }

    let renderedCollectionOptionsKey = null;

    /**
     * The collection list only ever arrives with a card page, so until one
     * lands the two options in the markup are a BOOTSTRAP, not the player's
     * holdings. Behind the phone's "Filters" button that bootstrap reads as the
     * whole truth. Same defect, same fix as the Deck Builder's own dropdown.
     */
    let collectionsResolved = false;

    function updateCollectionFilterOptions({ force = false } = {}) {
        const select = document.getElementById('collection-filter-collection');
        if (!select) return;

        const names = Array.isArray(availableCollections) ? availableCollections : [];
        // Rebuilding the option list on every page turn would reset a dropdown
        // the player may have open, so only redraw when it actually changed
        // (or when a language switch invalidated every label). The resolved
        // flag joins the key so the pending marker comes off as soon as the
        // real list lands, even when that list is empty.
        const optionsKey = `${collectionsResolved ? 'r' : 'p'}\u0001${names.join('\u0000')}`;
        if (!force && optionsKey === renderedCollectionOptionsKey) {
            select.value = currentFilters.collection || '';
            return;
        }
        renderedCollectionOptionsKey = optionsKey;

        select.innerHTML = [
            `<option value="">${escapeHtml(uiText('collection.filters.allCollections', 'All Collections'))}</option>`,
            `<option value="Stellar">${escapeHtml(uiText('collection.filters.stellar', 'Stellar'))}</option>`,
            ...names.map((name) => (
                `<option value="${escapeHtml(name)}">${escapeHtml(getCollectionFilterLabel(name))}</option>`
            )),
            collectionsResolved
                ? ''
                : `<option value="" disabled data-collections-pending="true">${escapeHtml(uiText('collection.filters.collectionsLoading', 'Loading…'))}</option>`,
        ].join('');
        if (collectionsResolved) select.removeAttribute('aria-busy');
        else select.setAttribute('aria-busy', 'true');

        const selected = currentFilters.collection || '';
        select.value = currentFilters.collection || '';
        // Only a RESOLVED list can say a collection is gone. An unresolved one
        // is empty for want of an answer, not because the account holds
        // nothing — clearing on that would silently drop the player's active
        // filter every time a page render starts.
        if (collectionsResolved && selected && selected !== 'Stellar' && !names.includes(selected)) {
            // The active filter's collection just left this account's holdings.
            // Clear it through the control's own change path so the grid and the
            // dropdown cannot disagree about what is being filtered.
            select.value = '';
            select.dispatchEvent(new Event('change', { bubbles: true }));
        }
    }

    function updateSpellOwnerFilterOptions() {
        const spellOwnerFilter = document.getElementById('collection-filter-spell-owner');
        if (!spellOwnerFilter) return;

        const owners = Array.isArray(availableSpellOwners) ? availableSpellOwners : [];

        const ownerOptions = owners
            .map((owner) => ({ key: owner, label: getCharacterLabel(owner) }))
            .sort((a, b) => a.label.localeCompare(b.label));

        spellOwnerFilter.innerHTML = [
            `<option value="all">${escapeHtml(uiText('collection.filters.spellsAll', 'Spells: All'))}</option>`,
            ...ownerOptions.map(owner => `<option value="${escapeHtml(owner.key)}">${escapeHtml(owner.label)}</option>`)
        ].join('');

        if (!owners.includes(activeSpellOwnerFilter)) {
            activeSpellOwnerFilter = 'all';
        }
        spellOwnerFilter.value = activeSpellOwnerFilter;
    }

    /** The card-detail view, if it is currently layered over the grid. */
    function openCardDetailSurface() {
        const detail = document.getElementById('collection-card-detail');
        if (!detail) return null;
        const open = detail.style.display !== 'none' && detail.getClientRects().length > 0;
        return open ? detail : null;
    }

    /**
     * Is this hover source SEALED UNDER the card-detail view?
     *
     * Only a card BEHIND the detail is: the preview it spawns lands on top of
     * the detail's chrome and eats its clicks, and “← Back” becomes a dead
     * button. The detail's OWN cards — the hero and both recipe parents — are
     * the card viewing screen itself, and enlarging them on hover is what that
     * screen is for. Containment is the whole distinction; an unscoped “is the
     * detail open” killed all three.
     */
    function isHoverSourceBehindCardDetail(sourceEl) {
        const detail = openCardDetailSurface();
        if (!detail) return false;
        return !detail.contains(sourceEl);
    }

    async function openCardDetail(cardId, variant = 'normal', { sourceTile = null } = {}) {
        if (!cardId) return;

        const browser = document.getElementById('collection-cards-browser');
        const detail = document.getElementById('collection-card-detail');
        if (!browser || !detail) return;

        // Only while the grid is on screen: a detail opened from a detail (a
        // recipe parent) would otherwise record the hidden grid's zero.
        const grid = document.getElementById('collection-cards-grid');
        if (grid && browser.style.display !== 'none') lastCardsGridScrollTop = grid.scrollTop;

        // The whole hover goes, including a preview handed to a neighbour on
        // a glide, which gave the id up (hover-handoff.js): the id alone took
        // the hidden one being built and left the enlarged card over the
        // detail until the neighbour's hover ended.
        clearHoverPreview();

        activeDetailCardId = cardId;
        activeDetailVariant = variant === 'stellar' ? 'stellar' : 'normal';
        /* What the player is looking at stays on screen while the detail is
           built and settled behind it (renderCardDetail); the card they pressed
           answers the press meanwhile. */
        const release = acknowledgeCardPress(sourceTile);
        try {
            await renderCardDetail(cardId, activeDetailVariant, {
                sourceCard: sourceTile?.querySelector?.(':scope > .card') || null,
            });
        } finally {
            release();
        }
    }

    function discardCardDetailStages() {
        document.querySelectorAll('.collection-card-detail-stage').forEach((stage) => {
            disposeCollectionExactImages(stage);
            stage.remove();
        });
    }

    function closeCardDetail() {
        lastDetailRequestId += 1;
        activeDetailCardId = null;
        activeDetailVariant = 'normal';
        activeDetailCraftRow = null;
        discardCardDetailStages();

        const browser = document.getElementById('collection-cards-browser');
        const detail = document.getElementById('collection-card-detail');
        const detailCoveredGrid = browser?.style.display === 'none';

        if (detail) {
            disposeCollectionExactImages(detail);
            detail.style.display = 'none';
            detail.innerHTML = '';
            detail.setAttribute('aria-busy', 'false');
        }
        if (browser) {
            if (detailCoveredGrid) hoverIntent.disarm();
            browser.style.display = '';
        }

        const grid = document.getElementById('collection-cards-grid');
        if (grid) {
            grid.scrollTop = lastCardsGridScrollTop;
        }
        resumeCardsPrebuild();
        if (detailCoveredGrid) refitGridShownAgain();
        // The grid is on screen again: a print its detail (or anything) saw move is followed now.
        followStellarPrints();
    }

    /* The grid kept its page size behind the detail (syncCardsPageSize); a
       window resized meanwhile gets its own page now that it can be measured.
       After the task, so a tab switch or a close that closed the detail has
       already taken the grid off screen. */
    function refitGridShownAgain() {
        setTimeout(() => {
            if (!isOpen || !cardsGridOnScreen()) return;
            // As the viewport handler does: a new size, or the same size at a new scale.
            if (syncCardsPageSize({ preserveOffset: true })) void renderCards();
            else refitShownCardsPage();
        }, 0);
    }

    /**
     * The finished detail replaces what the player was looking at in ONE step:
     * the grid (or the previous detail) goes in the same task the new one shows.
     */
    function presentCardDetail(stage, craftRow = null) {
        const previous = document.getElementById('collection-card-detail');
        const browser = document.getElementById('collection-cards-browser');
        // A preview opened over the grid while this was prepared must not outlive it.
        invalidateHoverSurface();
        if (previous && previous !== stage) {
            disposeCollectionExactImages(previous);
            previous.remove();
        }
        stage.id = 'collection-card-detail';
        stage.classList.remove('collection-card-detail-stage');
        stage.removeAttribute('aria-hidden');
        stage.setAttribute('aria-busy', 'false');
        stage.style.display = 'flex';
        if (browser) browser.style.display = 'none';
        landStagedEntrance(stage);
        landRevealTransitions(stage);
        activeDetailCraftRow = craftRow;
        // The balance or the Stellar lock may have moved while it was built.
        applyCraftRowState(craftRow);
        hoverIntent.disarm();
    }

    /**
     * The detail's entrance — its body and card column fade in, its Stellar
     * badge slides (deck-builder.css; the very elements a refresh plays none
     * on) — starts when the stage is built and runs there unseen. A detail
     * built faster than its fade was presented mid-fade: its header and
     * buttons whole, its card and facts still near transparent, arriving a
     * frame later (round 13, 25 September 2026: Firefox desktop, the card
     * 360 ms after the title; the WebKit phone, the card and its facts
     * 300 ms after). One built slower, the usual case, has already arrived
     * whole. The detail replaces the grid in ONE step, so an entrance still
     * running when it is presented lands on its end; a loop keeps running.
     */
    function landStagedEntrance(stage) {
        stage.querySelectorAll(
            '.collection-card-detail-body, .collection-card-detail-left, .collection-card-detail-variant'
        ).forEach((element) => {
            if (typeof element.getAnimations !== 'function') return;
            let animations = [];
            try {
                animations = element.getAnimations();
            } catch (_) {
                return;
            }
            animations.forEach((animation) => {
                if (!animation?.animationName || animation.playState === 'finished') return;
                if (!Number.isFinite(animation.effect?.getComputedTiming?.().endTime)) return;
                try {
                    animation.finish();
                } catch (_) {
                    // Left to run: the next frames still bring it in.
                }
            });
        });
    }

    function presentCardDetailMessage(stage, message) {
        disposeCollectionExactImages(stage);
        const note = document.createElement('div');
        note.className = 'collection-empty';
        note.textContent = message;
        stage.replaceChildren(note);
        presentCardDetail(stage);
    }

    function buildCardElementData(card, variant = 'normal', stellarAsset = null) {
        const isStellar = variant === 'stellar';
        const data = {
            id: card.id,
            instanceId: card.id,
            name: card.name,
            type: card.type,
            manaCost: card.cost || 0,
            picture: card.image,
            imageUrl: card.image,
            description: card.description || '',
            targetingType: card.targetingType || 'None',
            character: card.character,
            collection: card.collection,
            origin: card.origin,
            forgeStatus: card.forgeStatus,
            isStellar,
        };

        if (isStellar && stellarAsset?.status === 'complete') {
            StellarAssets?.applyMediaToCard?.(data, stellarAsset);
        }

        return data;
    }

    /**
     * What a card's detail needs beyond the tile the player is pointing at:
     * its recipe parents (their data and art) and, for a forged card, who
     * discovered it. Started when a pointer enters the tile (a mouse hovering,
     * or a finger's first contact — the touch latch's enlarging tap) or it
     * takes focus, so the detail that press opens has it all and appears whole.
     */
    function warmCardDetail(cardId) {
        if (!isOpen || !cardId) return;
        // A Spell's detail shows its summoner: that one summoner is read now.
        const owner = spellOwnerOf(getCachedCard(cardId));
        if (owner && !spellOwnerKnown(owner)) void readSpellOwner(owner);
        const parents = getRecipeParentsForCard(cardId);
        if (!parents?.length) return;
        const missing = parents.filter((pid) => !getCachedCard(pid) && !detailParentReads.has(pid));
        if (missing.length > 0) {
            const read = fetchCardDetails(missing).catch(() => [])
                .finally(() => missing.forEach((pid) => detailParentReads.delete(pid)));
            missing.forEach((pid) => detailParentReads.set(pid, read));
            void read.then(() => warmPageArt(parents.map((pid) => ({ card: getCachedCard(pid) })), 'high'));
        } else {
            warmPageArt(parents.map((pid) => ({ card: getCachedCard(pid) })), 'high');
        }
        const card = getCachedCard(cardId);
        const isFoundryCard = card?.isForged === true || card?.collection === 'Moon Foundry';
        if (isFoundryCard && parents.length === 2 && MoonfallCloud?.convex) {
            void loadRecipeDetails(parents[0], parents[1]);
        }
    }
    const detailParentReads = new Map();

    /**
     * Built where it will be shown — a stage laid over the cards section at the
     * detail's own size, invisible — so its rasters settle at their real
     * geometry, then presented in one step (presentCardDetail). It used to take
     * the screen at once over a skeleton, with no cap on the wait, and then
     * pop its header, buttons and discoverer in one after another.
     */
    async function renderCardDetail(cardId, variant = 'normal', { sourceCard = null } = {}) {
        const current = document.getElementById('collection-card-detail');
        const section = current?.parentElement;
        if (!current || !section) return;

        const requestId = ++lastDetailRequestId;
        discardCardDetailStages();
        const stage = document.createElement('div');
        stage.className = 'collection-card-detail collection-card-detail-stage';
        stage.dataset.cardId = String(cardId);
        stage.dataset.variant = String(variant);
        /* A re-render of the detail on screen (a craft, a finished Stellar, a
           language change) is a REFRESH: its entrance fades, run unseen while
           the stage was hidden, restarted the moment it replaced the opaque
           detail — the whole detail blinked to low opacity and faded back. */
        if (
            current.style.display === 'flex'
            && current.dataset.cardId === String(cardId)
            && current.dataset.variant === String(variant)
        ) {
            stage.classList.add('collection-card-detail--refresh');
        }
        stage.setAttribute('aria-hidden', 'true');
        stage.setAttribute('aria-busy', 'true');
        section.appendChild(stage);
        const isCurrent = () => requestId === lastDetailRequestId && stage.isConnected;
        const abandon = () => {
            disposeCollectionExactImages(stage);
            stage.remove();
        };
        // Everything optional this detail waits for shares ONE cap, from here.
        let capTimer = null;
        const cap = new Promise((resolve) => {
            capTimer = setTimeout(() => resolve('cap'), DETAIL_PRESENT_CAP_MS);
        });
        const withinCap = (work) => Promise.race([Promise.resolve(work).catch(() => undefined), cap]);

        let card = getCachedCard(cardId);
        if (!card) {
            const details = await fetchCardDetails([cardId]).catch(() => []);
            card = Array.isArray(details) ? details[0] : null;
        }
        if (!isCurrent()) {
            clearTimeout(capTimer);
            abandon();
            return;
        }
        if (!card) {
            clearTimeout(capTimer);
            presentCardDetailMessage(stage, uiText('collection.detail.cardNotFound', 'Card not found.'));
            return;
        }

        if (variant === 'stellar') {
            await withinCap(loadStellarAssets([cardId]));
        }

        /* The Craft and Stellar buttons are decided by the balance, so a
           detail opened while the profile read is still out (a slow link, a
           tap on the first grid paint) waits for it, within the cap. It used
           to paint at once: an unknown balance reads as "cannot afford", so
           both buttons painted grey and nothing re-stated them when the
           balance landed a moment later. */
        if (!Number.isFinite(Number(stardust)) && pendingProfileRead) {
            await withinCap(pendingProfileRead);
        }

        const isFoundryCard = card.isForged === true || card.collection === 'Moon Foundry';
        // A forged card's lineage comes from the recipes, whose first read may still be out.
        if (isFoundryCard && !recipesBaselineRead) {
            await withinCap(loadRecipes());
        }
        // A Spell names its summoner with their portrait (readSpellOwners).
        const spellOwner = spellOwnerOf(card);
        if (spellOwner && !spellOwnerKnown(spellOwner)) {
            await withinCap(readSpellOwner(spellOwner));
        }

        if (!isCurrent()) {
            clearTimeout(capTimer);
            abandon();
            return;
        }

        const detailRasters = [];
        let detailCraftRow = null;

        const ownedNormalQty = getOwnedNormalQty(cardId);
        const ownedStellarQty = getOwnedStellarQty(cardId);
        const showAsStellar = variant === 'stellar';
        const isOwned = (ownedNormalQty + ownedStellarQty) > 0;
        const isRecipeDiscovered = !isFoundryCard || recipeByResultCardId.has(cardId) || discoveredRecipeCardIds.has(cardId);
        const isCollectionActionable = !isFoundryCard || isOwned || isRecipeDiscovered;
        /* The card's lineage. A forged card whose recipe the player has not
           discovered shows the recipe as hidden rather than revealing it. */
        const parentIdsForDetail = getRecipeParentsForCard(cardId);
        if (parentIdsForDetail?.length) {
            const missingParentIds = parentIdsForDetail.filter((pid) => !getCachedCard(pid));
            if (missingParentIds.length > 0) {
                // Joins a hover warm already reading them; reads the rest in one request.
                const unread = missingParentIds.filter((pid) => !detailParentReads.has(pid));
                const parentsRead = Promise.all([
                    ...missingParentIds.map((pid) => detailParentReads.get(pid)).filter(Boolean),
                    unread.length > 0 ? fetchCardDetails(unread).catch(() => []) : null,
                ]);
                const arrived = await withinCap(parentsRead);
                if (!isCurrent()) {
                    clearTimeout(capTimer);
                    abandon();
                    return;
                }
                if (arrived === 'cap') {
                    // Present what is known now; the lineage re-renders whole when it lands.
                    void parentsRead.then(() => {
                        if (activeDetailCardId === cardId && activeDetailVariant === variant) {
                            void renderCardDetail(cardId, variant);
                        }
                    });
                }
            }
        }
        const visibleParents = isRecipeDiscovered ? getRecipeParentsForCard(cardId) : null;
        const parentsHidden = isFoundryCard && !isRecipeDiscovered;
        const stellarState = showAsStellar ? getStellarState(cardId) : null;
        const isSpell = (card.type || '').toLowerCase() === 'spell';
        const isMoonDreamCard = (card.origin || '').toLowerCase() === 'moon_dream'
            || (card.collection || '').toLowerCase() === 'moon dream';
        const isNonCraftable = isNonCraftableCard(card);
        // Base and pack cards are granted, never forged — the server refuses to
        // craft them, so the button must not be offered.
        const canCraftNormalCopy = !isSpell && !isMoonDreamCard && !isNonCraftable;
        // A Spell is one entitlement per face: once the Stellar print exists,
        // "craft another" is an offer the player could never spend.
        const stellarPrintCapped = isSpell && ownedStellarQty > 0;

        // --- Header ---
        const header = document.createElement('div');
        header.className = 'collection-card-detail-header';

        const backBtn = document.createElement('button');
        backBtn.className = 'btn deck-btn deck-btn-secondary collection-card-detail-back';
        backBtn.type = 'button';
        backBtn.textContent = uiText('collection.detail.back', '← Back');
        backBtn.addEventListener('click', closeCardDetail);

        const title = document.createElement('div');
        title.className = 'collection-card-detail-title';
        title.textContent = card.name || uiText('collection.detail.fallbackTitle', 'Card');

        header.appendChild(backBtn);
        header.appendChild(title);

        if (showAsStellar) {
            const variantBadge = document.createElement('span');
            variantBadge.className = 'collection-card-detail-variant';
            variantBadge.textContent = uiText('common.stellar', 'Stellar');
            header.appendChild(variantBadge);
        }

        // --- Body (side-by-side: card left, info right) ---
        const body = document.createElement('div');
        body.className = 'collection-card-detail-body';

        // Left: card display
        const left = document.createElement('div');
        left.className = 'collection-card-detail-left';
        const cardWrap = document.createElement('div');
        cardWrap.className = 'collection-card-detail-card';

        if (Cards?.createCardElement) {
            const asset = showAsStellar ? stellarState?.asset : null;
            const cardData = buildCardElementData(card, variant, asset);
            const cardEl = Cards.createCardElement(
                cardData,
                `collection-detail-${card.id}-${variant}`
            );
            cardEl.classList.add('deck-builder-card');
            if (showAsStellar) {
                cardEl.dataset.stellarPreview = 'collection';
            }
            cardWrap.appendChild(cardEl);
            if (cardEl.querySelector('.card-art-paint')) {
                /* The pressed tile already holds a settled raster of this very
                   face; start from it, as the hover popup does, rather than
                   resolving the art again (measured in Firefox: 5.2 s to open,
                   the picture still loading 10 s later). Settlement still
                   re-rasters it at the detail's own size. */
                const heroSource = sourceCard?.isConnected
                    && sourceCard.id === `collection-${card.id}-${variant}`
                    ? sourceCard
                    : null;
                detailRasters.push({ cardEl, cardData, sourceCard: heroSource });
            }
            if (CardTilt?.register) {
                CardTilt.register(cardEl);
            }

            if (showAsStellar && Cards?.setStellarAudioHover) {
                // Touch: tap enlarges, tap again acts. The guard above already admits the
                // synthetic (untrusted) mouseenter this dispatches while rejecting the
                // compatibility one a real tap produces.
                TouchPreview?.attach?.(cardWrap);
                cardWrap.addEventListener('mouseenter', (event) => {
                    if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                    Cards.setStellarAudioHover(cardEl, true);
                });
                cardWrap.addEventListener('mouseleave', () => Cards.setStellarAudioHover(cardEl, false));
            }
        } else {
            const fallbackImg = document.createElement('img');
            fallbackImg.alt = escapeHtml(card.name);
            fallbackImg.loading = 'lazy';
            fallbackImg.style.maxHeight = uiCssPx(240);
            fallbackImg.style.borderRadius = uiCssPx(12);
            cardWrap.appendChild(fallbackImg);
            if (card.image) {
                loadCollectionExactImage(fallbackImg, card.image, {
                    fit: card.type === 'Character' ? 'cover' : 'contain',
                });
            } else {
                fallbackImg.classList.add('card-art-unavailable');
            }
        }

        left.appendChild(cardWrap);

        // Attach hover-to-enlarge preview (same popup pattern as grid cards)
        attachCardHoverPreview(cardWrap, card, variant);

        body.appendChild(left);

        // Right: info panel
        const right = document.createElement('div');
        right.className = 'collection-card-detail-right';
        const information = document.createElement('div');
        information.className = 'collection-card-detail-info';
        right.appendChild(information);

        // Stellar status (if applicable)
        if (showAsStellar) {
            information.appendChild(buildStellarStatusSection(stellarState));
        }

        // --- Action buttons ---
        const actions = document.createElement('div');
        actions.className = 'collection-card-detail-actions';

        if (isCollectionActionable) {
            const craftBtn = document.createElement('button');
            craftBtn.className = 'btn deck-btn collection-craft-btn';
            craftBtn.type = 'button';
            craftBtn.dataset.craftMode = 'normal';
            const craftIcon = document.createElement('span');
            craftIcon.className = 'craft-icon';
            craftIcon.textContent = '\u2728';
            craftBtn.appendChild(craftIcon);
            const craftingCost = forgeCosts?.CRAFTING ?? 10;
            const craftLabel = document.createTextNode(` ${uiText('collection.detail.craftCost', 'Craft {{cost}} ✦', {
                cost: formatStardust(craftingCost),
            })}`);
            craftBtn.appendChild(craftLabel);
            craftBtn.setAttribute('aria-label', uiText('collection.detail.craftCost', 'Craft {{cost}} ✦', {
                cost: craftingCost,
            }));
            craftBtn.addEventListener('click', () => {
                const action = async () => {
                    triggerCraftCelebration(craftBtn);
                    const runner = () => Promise.resolve(craftCard(cardId, 'normal', craftBtn));
                    if (MoonfallCloudGuard && typeof MoonfallCloudGuard.run === 'function') {
                        await MoonfallCloudGuard.run(`collection.craft:${cardId}:normal`, runner);
                    } else {
                        await runner();
                    }
                };
                const guard = MoonfallClickGuard;
                if (guard && typeof guard.guard === 'function') {
                    // The guard's release re-enables the button whatever the
                    // balance now says; the row re-states it after.
                    guard.guard(craftBtn, action)
                        .catch((err) => console.error('[Collection] craft guard failed:', err))
                        .finally(refreshDetailCraftRow);
                } else {
                    action().catch((err) => console.error('[Collection] craft failed:', err));
                }
            });

            const stellarBtn = document.createElement('button');
            stellarBtn.className = 'btn deck-btn deck-btn-primary collection-craft-btn';
            stellarBtn.type = 'button';
            stellarBtn.dataset.craftMode = 'stellar';
            const stellarIcon = document.createElement('span');
            stellarIcon.className = 'craft-icon';
            stellarIcon.textContent = '\u2726';
            stellarBtn.appendChild(stellarIcon);
            const stellarCost = forgeCosts?.STELLAR ?? 200;
            const stellarLabel = document.createTextNode(` ${uiText('collection.detail.stellarCost', 'Stellar {{cost}} ✦', {
                cost: formatStardust(stellarCost),
            })}`);
            stellarBtn.appendChild(stellarLabel);
            stellarBtn.setAttribute('aria-label', uiText('collection.detail.stellarCost', 'Stellar {{cost}} ✦', {
                cost: stellarCost,
            }));
            stellarBtn.addEventListener('click', () => {
                const action = async () => {
                    triggerCraftCelebration(stellarBtn);
                    if (MoonfallAnalytics) MoonfallAnalytics.capture('stellar_claim');
                    const runner = () => Promise.resolve(craftCard(cardId, 'stellar', stellarBtn));
                    if (MoonfallCloudGuard && typeof MoonfallCloudGuard.run === 'function') {
                        await MoonfallCloudGuard.run(`collection.craft:${cardId}:stellar`, runner);
                    } else {
                        await runner();
                    }
                };
                const guard = MoonfallClickGuard;
                if (guard && typeof guard.guard === 'function') {
                    guard.guard(stellarBtn, action)
                        .catch((err) => console.error('[Collection] stellar craft guard failed:', err))
                        .finally(refreshDetailCraftRow);
                } else {
                    action().catch((err) => console.error('[Collection] stellar craft failed:', err));
                }
            });

            const offersStellar = isOwned && !stellarPrintCapped;
            const canSpendHere = canCraftNormalCopy || offersStellar;
            const craftRow = canSpendHere
                ? buildCraftRow({
                    craftBtn: canCraftNormalCopy ? craftBtn : null,
                    stellarBtn: offersStellar ? stellarBtn : null,
                    craftingCost,
                    stellarCost,
                })
                : null;
            // The anchor button (Stellar when offered) sits in its slot, with
            // the line that says why it is dead right underneath it.
            if (canCraftNormalCopy) actions.appendChild(craftRow.anchor === craftBtn ? craftRow.slot : craftBtn);
            if (offersStellar) actions.appendChild(craftRow.slot);
            if (craftRow) {
                // Live once presented (presentCardDetail); stated now so the
                // first frame already says it.
                detailCraftRow = craftRow;
                applyCraftRowState(craftRow);
            }

            // Real-money equivalent at the point of spending. Kept OFF the
            // buttons themselves: a label like "Craft 10 ✦ (≈ $1.00)" outgrows
            // its control on a phone, and these buttons already sit in a
            // wrapping action row.
            const moneyNote = document.createElement('p');
            moneyNote.className = 'collection-craft-money-note';
            /** Fill the note from whatever rate is known now. Never invents one. */
            const paintMoneyNote = () => {
                const parts = [];
                const craftMoney = canCraftNormalCopy
                    ? Stardust?.moneyEquivalent?.(craftingCost) : null;
                const stellarMoney = offersStellar
                    ? Stardust?.moneyEquivalent?.(stellarCost) : null;
                if (craftMoney) {
                    parts.push(uiText('collection.detail.craftMoney', 'Craft {{money}}', { money: craftMoney }));
                }
                if (stellarMoney) {
                    parts.push(uiText('collection.detail.stellarMoney', 'Stellar {{money}}', { money: stellarMoney }));
                }
                moneyNote.textContent = parts.join(' · ');
                moneyNote.hidden = parts.length === 0;
                return parts.length > 0;
            };
            if (!paintMoneyNote() && canSpendHere && Stardust) {
                /* The rate lands late. Patch this ONE element when it does —
                   re-rendering the whole detail threw away the hero card, its
                   settled raster, its tilt registration, and any hover already
                   in progress (the pointer never left, so no fresh `mouseenter`
                   followed and the enlarged card simply never appeared). */
                Stardust.loadReferenceRate?.().then(() => {
                    if (requestId === lastDetailRequestId) paintMoneyNote();
                });
            }

            if (!isSpell && !isNonCraftable) {
                const listBtn = document.createElement('button');
                listBtn.className = 'btn deck-btn deck-btn-secondary collection-list-exchange-btn';
                listBtn.type = 'button';
                listBtn.textContent = uiText('collection.detail.listOnExchange', 'List on Exchange');
                const listableQty = showAsStellar ? ownedStellarQty : ownedNormalQty;
                listBtn.disabled = listableQty <= 0;
                listBtn.addEventListener('click', () => {
                    if (MoonExchange?.prefillSellForm) {
                        MoonExchange.prefillSellForm({
                            cardId,
                            variant: showAsStellar ? 'stellar' : 'normal',
                            quantity: 1,
                        });
                    }
                    switchSection('exchange');
                });
                actions.appendChild(listBtn);
            }

            // Only where there is something to spend on. `actions` is dropped
            // entirely when it has no children, and a card with no craft
            // buttons must not gain an actions row just to hold an empty note.
            if (canSpendHere) actions.appendChild(moneyNote);
        }

        /* RESTORED. A "mobile polish" pass deleted this whole block and left a
           note claiming ownership, parentage, collection and discoverer were
           "already represented elsewhere". They were not represented anywhere:
           opening a card stopped telling the player which collection it came
           from, who discovered it, or what it was forged from. It is the point
           of the detail view. If it ever needs to yield space on a phone, it
           yields by LAYOUT, never by deletion. */
        // --- Meta info (appended first, actions deferred to bottom) ---
        const meta = document.createElement('div');
        meta.className = 'collection-card-detail-meta';

        if (showAsStellar) {
            const ownedItem = document.createElement('div');
            ownedItem.className = 'collection-card-detail-meta-item';
            const ownedLabel = document.createElement('span');
            ownedLabel.className = 'meta-label';
            ownedLabel.textContent = uiText('collection.detail.ownedStellar', 'In collection (Stellar)');
            const ownedValue = document.createElement('span');
            ownedValue.textContent = String(ownedStellarQty);
            ownedItem.appendChild(ownedLabel);
            ownedItem.appendChild(ownedValue);
            meta.appendChild(ownedItem);
        } else {
            const ownedItem = document.createElement('div');
            ownedItem.className = 'collection-card-detail-meta-item';
            const ownedLabel = document.createElement('span');
            ownedLabel.className = 'meta-label';
            ownedLabel.textContent = uiText('collection.detail.owned', 'In collection');
            const ownedValue = document.createElement('span');
            ownedValue.textContent = String(ownedNormalQty);
            ownedItem.appendChild(ownedLabel);
            ownedItem.appendChild(ownedValue);
            meta.appendChild(ownedItem);
        }

        if (isSpell && card.character) {
            const ownerLine = document.createElement('div');
            ownerLine.className = 'collection-card-detail-owner';

            const ownerImg = document.createElement('img');
            ownerImg.src = getCharacterPortraitUrl(card.character);
            ownerImg.alt = getCharacterLabel(card.character);
            ownerImg.loading = 'lazy';
            ownerImg.className = 'collection-card-detail-owner-image';
            ownerLine.appendChild(ownerImg);

            const ownerText = document.createElement('span');
            ownerText.textContent = getCharacterLabel(card.character);
            ownerLine.appendChild(ownerText);
            meta.appendChild(ownerLine);
        }

        const discovererLine = document.createElement('div');
        discovererLine.className = 'collection-card-detail-meta-item';

        if (isFoundryCard && visibleParents) {
            const discLabel = document.createElement('span');
            discLabel.className = 'meta-label';
            discLabel.textContent = uiText('collection.detail.discoveredBy', 'Discovered by');
            const discValue = document.createElement('span');
            discValue.textContent = uiText('status.loading', 'Loading') + '...';
            discValue.className = 'discoverer-value';
            discovererLine.appendChild(discLabel);
            discovererLine.appendChild(discValue);
            meta.appendChild(discovererLine);
        } else if (isFoundryCard) {
            const discLabel = document.createElement('span');
            discLabel.className = 'meta-label';
            discLabel.textContent = uiText('collection.detail.discoveredBy', 'Discovered by');
            const discValue = document.createElement('span');
            discValue.textContent = '\u2014';
            discovererLine.appendChild(discLabel);
            discovererLine.appendChild(discValue);
            meta.appendChild(discovererLine);
        } else if (showAsStellar && stellarState?.asset?.crafterUsername) {
            // A Stellar print names whoever first forged it, on any card —
            // base spells included. The normal face keeps its own attribution.
            const discLabel = document.createElement('span');
            discLabel.className = 'meta-label';
            discLabel.textContent = uiText('collection.detail.stellarCraftedBy', 'Stellar forged by');
            const discValue = document.createElement('span');
            discValue.className = 'discoverer-value';
            discValue.textContent = stellarState.asset.crafterUsername;
            discovererLine.appendChild(discLabel);
            discovererLine.appendChild(discValue);
            meta.appendChild(discovererLine);
        } else if (isMoonDreamCard) {
            // A Moon Dream card exists only inside its dreamer's collection, so
            // the viewer is always its discoverer \u2014 no lookup required.
            const discLabel = document.createElement('span');
            discLabel.className = 'meta-label';
            discLabel.textContent = uiText('collection.detail.discoveredBy', 'Discovered by');
            const discValue = document.createElement('span');
            discValue.className = 'discoverer-value';
            discValue.textContent = profileUsername || uiText('collection.detail.unknown', 'Unknown');
            discovererLine.appendChild(discLabel);
            discovererLine.appendChild(discValue);
            meta.appendChild(discovererLine);
        } else {
            const collItem = document.createElement('div');
            collItem.className = 'collection-card-detail-meta-item';
            const collLabel = document.createElement('span');
            collLabel.className = 'meta-label';
            collLabel.textContent = uiText('collection.detail.collection', 'Collection');
            const collValue = document.createElement('span');
            collValue.textContent = card.collection || 'Classic';
            collItem.appendChild(collLabel);
            collItem.appendChild(collValue);
            meta.appendChild(collItem);
        }

        // A fusion this player discovered first: one door to #recipes
        // (ui/discord-links.js). Decided from the first-discovery set the grid
        // already read, so it paints with the detail and never arrives late.
        // That set also holds every Moon Dream card, which has no recipe.
        if (isFoundryCard && !isMoonDreamCard && !showAsStellar && firstDiscoveryBadges.fuse.has(cardId)) {
            const recipeLink = DiscordLinks.createLink(
                'discovery',
                uiText('collection.detail.postRecipeOnDiscord', 'Post the recipe in #recipes'),
            );
            if (recipeLink) {
                const discordLine = document.createElement('div');
                discordLine.className = 'collection-card-detail-meta-item collection-card-detail-discord';
                discordLine.appendChild(recipeLink);
                meta.appendChild(discordLine);
            }
        }

        information.appendChild(meta);

        // --- Recipe section ---
        const hasRecipe = visibleParents && visibleParents.length === 2;

        if (hasRecipe || parentsHidden) {
            const recipeSection = document.createElement('div');
            recipeSection.className = 'collection-recipe-section';

            const recipeTitle = document.createElement('div');
            recipeTitle.className = 'collection-recipe-title';
            recipeTitle.textContent = uiText('collection.detail.recipe', 'Recipe');
            recipeSection.appendChild(recipeTitle);

            if (hasRecipe) {
                const formula = document.createElement('div');
                formula.className = 'collection-recipe-formula';

                visibleParents.forEach((pid, idx) => {
                    const parent = getCachedCard(pid);
                    if (!parent) return;

                    if (idx > 0) {
                        const plusOp = document.createElement('div');
                        plusOp.className = 'collection-recipe-operator';
                        plusOp.textContent = '+';
                        formula.appendChild(plusOp);
                    }

                    const parentWrap = document.createElement('div');
                    parentWrap.className = 'collection-recipe-parent';
                    parentWrap.dataset.cardId = pid;

                    const parentStellarQty = ownedCardStellarMap.get(pid) || 0;
                    const parentIsStellar = parentStellarQty > 0;
                    const parentAsset = parentIsStellar ? getStellarAsset(pid) : null;

                    if (Cards?.createCardElement) {
                        const parentCardData = buildCardElementData(
                            parent,
                            parentIsStellar ? 'stellar' : 'normal',
                            parentAsset,
                        );
                        const parentEl = Cards.createCardElement(
                            parentCardData,
                            `collection-detail-parent-${pid}`
                        );
                        parentEl.classList.add('deck-builder-card');
                        if (parentIsStellar) {
                            parentEl.dataset.stellarPreview = 'collection';
                        }
                        parentWrap.appendChild(parentEl);
                        if (parentEl.querySelector('.card-art-paint')) {
                            detailRasters.push({ cardEl: parentEl, cardData: parentCardData });
                        }
                        if (CardTilt?.bindHost) {
                            CardTilt.bindHost(parentWrap, parentEl);
                        }

                        if (parentIsStellar && Cards?.setStellarAudioHover) {
                            // Touch: tap enlarges, tap again acts. The guard above already admits the
                            // synthetic (untrusted) mouseenter this dispatches while rejecting the
                            // compatibility one a real tap produces.
                            TouchPreview?.attach?.(parentWrap);
                            parentWrap.addEventListener('mouseenter', (event) => {
                                if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
                                Cards.setStellarAudioHover(parentEl, true);
                            });
                            parentWrap.addEventListener('mouseleave', () => Cards.setStellarAudioHover(parentEl, false));
                        }
                    } else {
                        const fallbackImg = document.createElement('img');
                        fallbackImg.src = parent.image || '/assets/images/cardback.webp';
                        fallbackImg.alt = escapeHtml(parent.name);
                        fallbackImg.loading = 'lazy';
                        fallbackImg.onerror = function () { this.src = '/assets/images/cardback.webp'; };
                        fallbackImg.style.maxHeight = uiCssPx(120);
                        fallbackImg.style.borderRadius = uiCssPx(8);
                        parentWrap.appendChild(fallbackImg);
                        if (parent.image && Cards?.enhanceCardArt) {
                            void Cards.enhanceCardArt(fallbackImg, parent.image, {
                                fit: parent.type === 'Character' ? 'cover' : 'contain',
                            });
                        }
                    }

                    const parentName = document.createElement('div');
                    parentName.className = 'collection-recipe-parent-name';
                    parentName.textContent = parent.name;
                    parentName.title = parent.name;
                    parentWrap.appendChild(parentName);

                    parentWrap.addEventListener('click', () => openCardDetail(pid, 'normal', { sourceTile: parentWrap }));
                    attachCardHoverPreview(parentWrap, parent, parentIsStellar ? 'stellar' : 'normal');
                    formula.appendChild(parentWrap);
                });

                recipeSection.appendChild(formula);
            } else {
                const hidden = document.createElement('div');
                hidden.className = 'collection-recipe-hidden';
                hidden.textContent = uiText('collection.detail.recipeHidden', 'Recipe hidden until you discover this card.');
                recipeSection.appendChild(hidden);
            }

            information.appendChild(recipeSection);
        }

        if (actions.children.length > 0) {
            right.appendChild(actions);
        }

        body.appendChild(right);

        stage.appendChild(header);
        stage.appendChild(body);

        /* Presented once its card rasters have settled AND the discoverer has
           answered — or at the cap, whichever is first. Past the cap the detail
           shows text-first: an unsettled art well keeps its own loading
           treatment and settles in place, and a late name still lands. */
        const settleRaster = Cards?.settleCardRasterForPresentation;
        const rastersSettled = typeof settleRaster === 'function' && detailRasters.length > 0
            ? Promise.allSettled(detailRasters.map(({ cardEl, cardData, sourceCard: rasterSource }) => {
                const settleOne = () => settleRaster(
                    cardEl,
                    cardData,
                    rasterSource
                        ? { oversample: 1, stableGeometry: true, sourceCard: rasterSource }
                        : { oversample: 1, stableGeometry: true },
                );
                const prepare = Cards?.prepareCardRasterForInteraction;
                if (!rasterSource || typeof prepare !== 'function') {
                    try {
                        return Promise.resolve(settleOne());
                    } catch (_) {
                        return Promise.resolve(false);
                    }
                }
                // Lock the source's paint first, exactly as the popup does.
                return Promise.resolve()
                    .then(() => prepare(rasterSource, 'hover'))
                    .catch(() => false)
                    .then(settleOne)
                    .catch(() => false);
            })).then((settlements) => settlements.every((result) => (
                result.status === 'fulfilled' && result.value === true
            )))
            : Promise.resolve(true);
        /* Who discovered this forged card: a Convex read, warmed on hover, raced
           with the rasters rather than started after them, so the name is on the
           first frame. A stale answer is dropped rather than written into
           whatever detail is on screen by then. */
        let discovererNamed = Promise.resolve();
        if (isFoundryCard && visibleParents && visibleParents.length === 2 && MoonfallCloud?.convex) {
            const nameDiscoverer = (name) => {
                if (requestId !== lastDetailRequestId) return;
                const discValue = discovererLine.querySelector('.discoverer-value');
                if (discValue) discValue.textContent = name;
            };
            discovererNamed = loadRecipeDetails(visibleParents[0], visibleParents[1]).then(
                (recipe) => nameDiscoverer(recipe?.discoveredBy?.username
                    || uiText('collection.detail.unknown', 'Unknown')),
                () => nameDiscoverer(uiText('collection.detail.unknown', 'Unknown')),
            );
        }
        const outcome = await Promise.race([
            Promise.all([rastersSettled, discovererNamed]).then(([rasterReady]) => rasterReady),
            cap,
        ]);
        clearTimeout(capTimer);
        if (!isCurrent()) {
            abandon();
            return;
        }
        if (outcome === false) {
            presentCardDetailMessage(stage, uiText('collection.cards.loadFailed', 'Failed to load cards.'));
            return;
        }
        presentCardDetail(stage, detailCraftRow);
    }

    /**
     * The card detail's reason line, in a slot with the button it sits under:
     * the Stellar button when the detail offers one ("the text should be
     * underneath the Stellar button" — the owner, 2026-09-24), otherwise
     * Craft. The caller places the slot where that button would go;
     * `applyCraftRowState` fills the line.
     */
    function buildCraftRow({ craftBtn, stellarBtn, craftingCost, stellarCost }) {
        const anchor = stellarBtn || craftBtn;
        const slot = document.createElement('div');
        slot.className = 'collection-craft-slot';
        slot.appendChild(anchor);

        const reason = document.createElement('p');
        reason.className = 'collection-craft-reason';
        reason.id = 'collection-craft-reason';
        reason.setAttribute('role', 'status');
        reason.hidden = true;
        slot.appendChild(reason);

        return { craftBtn, stellarBtn, craftingCost, stellarCost, anchor, slot, reason };
    }

    function stellarLockText(lockedCardId) {
        return uiText(
            'collection.detail.waitForStellar',
            'Wait for {{cardName}} to finish attuning before crafting another stellar card.',
            {
                cardName: getCachedCard(lockedCardId)?.name
                    || uiText('collection.detail.currentStellarCard', 'your current stellar card'),
            }
        );
    }

    /**
     * WHY A SPEND BUTTON IS DEAD, said right under it.
     *
     * Craft and Stellar refuse for reasons the client can see coming: a
     * balance short of the price, a balance it could not read, and — for
     * Stellar — another print still attuning (one at a time per account, the
     * server's own gate). Each used to be a grey button and nothing more; the
     * lock's reason lived only in a hover tooltip, which no touch screen shows.
     * Production, 2026-09-23: "i am unable to craft a stellar. button is
     * greyed idk why" — 30 Stardust against a 200 Stardust print, on every
     * card, with nothing on screen to say so.
     *
     * So the line under the button states the reason in words and each dead
     * button names that line as its description. The button stays unpressable.
     * Re-run whenever the balance or the lock moves, so the row follows them
     * in place instead of the detail being rebuilt. A button mid-craft stays
     * as `setCraftButtonState` left it.
     */
    function applyCraftRowState(row) {
        if (!row) return;
        const balance = Number(stardust);
        const balanceKnown = Number.isFinite(balance);
        const short = (button, cost) => Boolean(button) && !(balanceKnown && balance >= cost);
        const craftShort = short(row.craftBtn, row.craftingCost);
        const stellarShort = short(row.stellarBtn, row.stellarCost);
        const lockedCardId = row.stellarBtn ? getPendingStellarCardId() : null;

        // A button whose craft is still in flight keeps the disabled state
        // its click guard and `setCraftButtonState` gave it.
        const settle = (button, disabled) => {
            if (!button || button.dataset.craftBusy === '1' || button.dataset.guardPending === '1') return;
            button.disabled = disabled;
        };
        settle(row.craftBtn, craftShort);
        settle(row.stellarBtn, stellarShort || Boolean(lockedCardId));
        if (row.stellarBtn) {
            row.stellarBtn.classList.toggle('collection-craft-btn-locked', Boolean(lockedCardId));
            if (lockedCardId) row.stellarBtn.title = stellarLockText(lockedCardId);
            else row.stellarBtn.removeAttribute('title');
        }

        let reason = '';
        let text = '';
        let explained = [];
        if (!balanceKnown) {
            reason = 'stardust-unknown';
            text = uiText('collection.detail.stardustUnknown', 'Your Stardust balance could not be read.');
            explained = [row.craftBtn, row.stellarBtn];
        } else if (craftShort || stellarShort) {
            // Before the lock: a shortfall is the refusal the player can act on.
            reason = 'stardust';
            text = uiText(
                'collection.detail.notEnoughStardust',
                'Not enough Stardust — you have {{amount}} ✦.',
                { amount: formatStardust(balance) }
            );
            explained = [craftShort && row.craftBtn, stellarShort && row.stellarBtn];
        } else if (lockedCardId) {
            reason = 'stellar-pending';
            text = stellarLockText(lockedCardId);
            explained = [row.stellarBtn];
        }

        row.reason.hidden = !reason;
        row.reason.textContent = text;
        if (reason) row.reason.dataset.reason = reason;
        else delete row.reason.dataset.reason;
        for (const button of [row.craftBtn, row.stellarBtn]) {
            if (!button) continue;
            if (explained.includes(button)) button.setAttribute('aria-describedby', row.reason.id);
            else button.removeAttribute('aria-describedby');
        }
    }

    function refreshDetailCraftRow() {
        applyCraftRowState(activeDetailCraftRow);
    }

    function setCraftButtonState(button, isBusy, label) {
        if (!button) return;
        if (isBusy) {
            button.disabled = true;
            button.dataset.craftBusy = '1';
            if (!button.dataset.originalHtml) {
                button.dataset.originalHtml = button.textContent;
                // Save full child nodes markup for restoration
                button.dataset.originalNodes = '1';
                button._savedChildren = Array.from(button.childNodes).map(n => n.cloneNode(true));
            }
            if (label) {
                button.textContent = '';
                const icon = document.createElement('span');
                icon.className = 'craft-icon';
                icon.textContent = '\u2728';
                button.appendChild(icon);
                button.appendChild(document.createTextNode(' ' + label));
            }
            button.classList.add('crafting');
        } else {
            button.disabled = false;
            delete button.dataset.craftBusy;
            if (button._savedChildren) {
                button.textContent = '';
                button._savedChildren.forEach(n => button.appendChild(n.cloneNode(true)));
                delete button._savedChildren;
                delete button.dataset.originalHtml;
                delete button.dataset.originalNodes;
            }
            button.classList.remove('crafting');
            /* A refused craft ends here with the detail still on screen:
               re-state the row, or the button would stay enabled for a price
               the balance cannot cover. */
            const row = activeDetailCraftRow;
            if (row && (row.craftBtn === button || row.stellarBtn === button)) {
                applyCraftRowState(row);
            }
        }
    }

    async function consumeAndShowCloudAchievementBanners() {
        if (typeof showAchievementBanner !== 'function') return;

        const queueBanner = (title, subtitle, imageUrl, imageKind, bannerOptions) => {
            if (bannerOptions) {
                showAchievementBanner(title, subtitle, imageUrl, imageKind, bannerOptions);
            } else {
                showAchievementBanner(title, subtitle, imageUrl, imageKind);
            }
        };

        try {
            let newUnlocks = [];
            if (typeof MoonfallCloud?.users?.consumeNewCardbackUnlocks === 'function') {
                const response = await MoonfallCloud.users.consumeNewCardbackUnlocks();
                if (Array.isArray(response?.newUnlocks)) {
                    newUnlocks = response.newUnlocks;
                }
            }

            if (newUnlocks.length > 0 && MoonfallCloud?.cardbacks) {
                let allCardbacks = [];
                try {
                    allCardbacks = await MoonfallCloud.cardbacks.list() || [];
                } catch (error) {
                    console.warn('[Collection] Cardback catalog unavailable; retaining unlock identity.', error);
                }
                newUnlocks.forEach((slug) => {
                    const cb = allCardbacks.find((item) => item.slug === slug);
                    queueBanner(
                        uiText('collection.achievements.cardbackUnlocked', 'Cardback Unlocked!'),
                        cb?.name || slug,
                        cb?.imageUrl,
                        'cardback',
                        { cardbackSlug: slug },
                    );
                });
            }
        } catch (e) {
            console.warn('[Collection] Failed to show cardback unlock banner:', e);
        }

        try {
            let newAchievements = [];
            if (typeof MoonfallCloud?.users?.consumeNewRecipeDiscoveryAchievements === 'function') {
                const response = await MoonfallCloud.users.consumeNewRecipeDiscoveryAchievements();
                if (Array.isArray(response?.newAchievements)) newAchievements = response.newAchievements;
            }
            for (const achievement of newAchievements) {
                const cardId = typeof achievement?.cardId === 'string' ? achievement.cardId : '';
                const cardName = achievement?.cardName || cardId || uiText('collection.achievements.unknownCard', 'Unknown Card');
                queueBanner(
                    uiText('collection.achievements.recipeDiscovery', 'Recipe Discovered!'),
                    uiText(
                        'collection.achievements.recipeDiscoverySubtitle',
                        '{{cardName}} is now craftable from Collection.',
                        { cardName }
                    ),
                    sanitizeImageUrlForDirectClient(achievement?.imageUrl) || undefined,
                    'card',
                    cardId ? { cardId } : undefined
                );
            }
        } catch (e) {
            console.warn('[Collection] Failed to show recipe-discovery achievement banner:', e);
        }

        try {
            let newAchievements = [];
            if (typeof MoonfallCloud?.users?.consumeNewFirstDiscoveryAchievements === 'function') {
                const response = await MoonfallCloud.users.consumeNewFirstDiscoveryAchievements();
                if (Array.isArray(response?.newAchievements)) {
                    newAchievements = response.newAchievements;
                }
            }

            for (const achievement of newAchievements) {
                const cardId = typeof achievement?.cardId === 'string' ? achievement.cardId : '';
                const cardName = achievement?.cardName || cardId || uiText('collection.achievements.unknownCard', 'Unknown Card');
                const discovererName = achievement?.discovererName || uiText('collection.detail.unknown', 'Unknown');
                const subtitle = uiText(
                    'collection.achievements.firstDiscoverySubtitle',
                    "{{cardName}} was first discovered by {{discovererName}}. Your name is now recorded as this card's discoverer.",
                    { cardName, discovererName }
                );
                queueBanner(
                    uiText('collection.achievements.firstDiscovery', 'First Discovery!'),
                    subtitle,
                    sanitizeImageUrlForDirectClient(achievement?.imageUrl) || undefined,
                    'card',
                    cardId ? { cardId } : undefined
                );
            }
        } catch (e) {
            console.warn('[Collection] Failed to show first-discovery achievement banner:', e);
        }
    }

    async function craftCard(cardId, craftMode = 'normal', button = null) {
        const mode = craftMode === 'stellar' ? 'stellar' : 'normal';
        const cardName = getCardLabel(cardId);
        try {
            const idToken = await Firebase.getIdToken();
            if (!idToken) {
                showStatus(uiText('collection.detail.authRequired', 'Authentication required.'), 'error');
                return;
            }

            setCraftButtonState(
                button,
                true,
                mode === 'stellar'
                    ? uiText('collection.detail.forging', 'Forging...')
                    : uiText('collection.detail.crafting', 'Crafting...')
            );

            const res = await fetch('/api/foundry/craft-card', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    cardId,
                    craftMode: mode,
                })
            });

            const result = await res.json().catch(() => ({}));
            if (!res.ok || result?.error) {
                // Moonfall's own refusal — a pause, a busy or silent service —
                // in plain words and the player's language (service-pause.js).
                const serviceLine = creationRefusalLine(result, uiText, {
                    locale: document.documentElement.lang || undefined,
                });
                showStatus(serviceLine || result?.error || uiText('collection.detail.craftFailed', 'Failed to craft.'), 'error');
                /* The server refused what this row offered, so the row's
                   picture of the account is out of date — a balance spent in
                   another tab, a Stellar started elsewhere. Read both again
                   and let the row say what is true now. */
                void followProfileBalance();
                void loadStellarAssets([]);
                return;
            }

            if (mode === 'stellar') {
                if (result?.stellarAssets?.cardId && StellarAssets?.update) {
                    StellarAssets.setMyPendingClaimCardId?.(result.stellarAssets.cardId);
                    StellarAssets.update(result.stellarAssets);
                }
                showStatus(buildStellarCraftMessage(cardName, result?.stellarAssets || null), 'success');
            } else {
                showStatus(uiText('collection.detail.craftedSuccess', '{{cardName}} is now in your collection.', {
                    cardName,
                }), 'success');
            }

            // After crafting a stellar variant, auto-transition the detail view
            // to the newly minted stellar card so the user can see its progress.
            if (mode === 'stellar' && activeDetailCardId === cardId) {
                activeDetailVariant = 'stellar';
            }

            await loadProfile();
            await loadRecipes();
            await loadCardbacks();
            await renderCards(undefined, { refresh: true });
            await loadStellarAssets();
            updateStardustDisplay();
            await syncAuxiliaryContexts({ refreshExchangeInventory: true });
            renderCardbacks();

            if (activeDetailCardId) {
                await renderCardDetail(activeDetailCardId, activeDetailVariant);
            }

            await consumeAndShowCloudAchievementBanners();
        } catch (err) {
            console.error('[Collection] craftCard failed:', err);
            showStatus(uiText('collection.detail.craftFailed', 'Failed to craft.'), 'error');
        } finally {
            setCraftButtonState(button, false);
        }
    }

    /**
     * Identity of a recipe read: every result card AND its parents, because
     * `commitRecipes` keeps the existing map when this is unchanged, and the
     * card detail reads parents straight out of it.
     */
    function recipesSignature(byResultCardId) {
        return Array.from(byResultCardId.entries())
            .map(([resultCardId, parents]) => `${resultCardId}>${parents.join('+')}`)
            .sort()
            .join('|');
    }

    /**
     * Adopt a recipe read, and invalidate ONLY if it actually changed anything.
     *
     * `loadRecipes` runs on every open and on every section switch (support data
     * is re-fetched, not memoized across entries), and it used to declare the
     * cards cache stale every single time. That cleared `committedCardsSignature`
     * moments after the grid committed, so the next re-entry recognised nothing
     * and wiped a correct page back to skeletons — measured at eight cards gone
     * 222ms after returning to the tab and returning, identical, 1.2s later.
     * An unchanged recipe set is not a change.
     */
    function commitRecipes(byResultCardId, discovered) {
        /* The FIRST answer for an account is a baseline, not a change: nothing
           was known before it, so nothing on screen can have been built from a
           different set. (The page itself is the server's, which read the same
           recipes.) This is what lets the open commit its first page without
           waiting a round trip for this read. */
        const baseline = !recipesBaselineRead;
        recipesBaselineRead = true;
        if (recipesSignature(byResultCardId) === recipesSignature(recipeByResultCardId)) return;
        recipeByResultCardId = byResultCardId;
        // Replaced only on a real change: `upsertCardCache` also adds
        // server-flagged discoveries to this set, and an identical recipe read
        // has no business discarding them.
        discoveredRecipeCardIds = baseline
            ? new Set([...discoveredRecipeCardIds, ...discovered])
            : discovered;
        if (!baseline) invalidateCardsCache();
    }

    /**
     * One in-flight recipe read, awaitable from two places.
     *
     * Recipes decide ENTITLEMENT — a discovered Moon Foundry recipe puts its
     * result card in the Collection — so the first page has to be BUILT with
     * them. They used to ride only in the slow support batch, which `loadData`
     * awaits AFTER the grid has committed, so `commitRecipes` then reported a
     * genuine change, `invalidateCardsCache('recipes')` nulled the committed
     * signature, and the next tab return rebuilt a page that was already right.
     * Memoizing lets `loadData` await the same read the batch is already doing,
     * instead of firing a second query.
     */
    let recipesReadPromise = null;
    let recipesBaselineRead = false;

    function loadRecipes() {
        if (!recipesReadPromise) {
            recipesReadPromise = readRecipes().finally(() => { recipesReadPromise = null; });
        }
        return recipesReadPromise;
    }

    /* EVERY DISCOVERY, PAGE BY PAGE (card-scale, 21 October 2026). The single
       read stopped at 4,000 discoveries without saying so, so the newest forges
       lost their badges. A malformed or endless answer fails the read, which
       the caller treats as a failed read, never as "discovered nothing". */
    const RECIPE_PAGES_MAX = 2000;
    async function readEveryRecipe() {
        const recipes = [];
        let cursor = null;
        for (let pages = 0; pages < RECIPE_PAGES_MAX; pages += 1) {
            const page = await MoonfallCloud.convex.query('foundry:getMyRecipesPage', cursor ? { cursor } : {});
            if (!page || !Array.isArray(page.items)) throw new Error('recipes_page_invalid');
            recipes.push(...page.items);
            if (page.isDone) return recipes;
            if (typeof page.nextCursor !== 'string' || !page.nextCursor) throw new Error('recipes_page_invalid');
            cursor = page.nextCursor;
        }
        throw new Error('recipes_page_limit');
    }
    async function readRecipes() {
        // NOTHING here may publish a non-answer as fact. A client that has not
        // come up yet, and one that has come up without a credential, have both
        // failed to answer "you have no recipes" — and `isReady()` only says
        // the Convex client EXISTS. The credential matters because Convex
        // answers an unauthenticated `getMyRecipes` with `[]` rather than an
        // error, so a token-less read comes back shaped exactly like the truth;
        // committing it reported every Moon Foundry card as undiscovered
        // ("Discovered by —", "Recipe hidden until you discover this card") for
        // cards the player owns. Committing an empty map also declared a change
        // the support batch's later read had to undo, rebuilding a page that was
        // already correct. A guest with no cloud needs no commit either: the map
        // already starts empty.
        if (
            !MoonfallCloud?.isReady?.()
            || !MoonfallCloud?.convex
            || !MoonfallCloud?.getAuthToken?.()
        ) {
            return;
        }

        let recipes;
        try {
            recipes = await readEveryRecipe();
        } catch (err) {
            // A read that FAILED is not the answer "you have discovered nothing".
            // The old code emptied the maps up front, so a transient error erased
            // every craft/discovery badge on the grid AND reported that erasure
            // as a change, forcing a full rebuild of a page that was correct.
            console.warn('[Collection] Failed to load recipes:', err);
            return;
        }

        const byResultCardId = new Map();
        const discovered = new Set();
        for (const r of recipes) {
            if (!r?.resultCardId || !r?.parent1Id || !r?.parent2Id) continue;
            byResultCardId.set(r.resultCardId, [r.parent1Id, r.parent2Id]);
            discovered.add(r.resultCardId);
        }
        commitRecipes(byResultCardId, discovered);
    }

    async function repairOwnedFoundryDiscoveries() {
        if (!MoonfallCloud?.isReady?.() || !MoonfallCloud?.convex?.mutation) {
            return false;
        }

        if (ownedFoundryDiscoveryRepairProfileId !== profileId) {
            ownedFoundryDiscoveryRepairProfileId = profileId;
            ownedFoundryDiscoveryRepairPromise = null;
        }

        if (!ownedFoundryDiscoveryRepairPromise) {
            ownedFoundryDiscoveryRepairPromise = (async () => {
                let cursor = null;
                let created = 0;
                let repaired = 0;
                for (let page = 0; page < 40; page++) {
                    const args = { pageSize: 250 };
                    if (cursor) args.cursor = cursor;
                    const result = await MoonfallCloud.convex.mutation(
                        'foundry:repairMyOwnedFoundryRecipeDiscoveriesPage',
                        args
                    );
                    created += Number(result?.createdDiscoveries || 0);
                    repaired += Number(result?.repairedDiscoveries || 0);
                    if (result?.isDone) break;
                    cursor = result?.cursor || null;
                    if (!cursor) break;
                }
                if (created > 0 || repaired > 0) {
                    invalidateCardsCache();
                    return true;
                }
                return false;
            })().catch((err) => {
                console.warn('[Collection] Failed to repair owned Foundry discoveries:', err);
                return false;
            });
        }

        return await ownedFoundryDiscoveryRepairPromise;
    }

    async function loadCardbacks() {
        let nextCardbacks = [];

        try {
            if (MoonfallCloud?.cardbacks) {
                nextCardbacks = await MoonfallCloud.cardbacks.list() || [];
            }
        } catch (err) {
            console.warn('[Collection] Failed to load cardbacks:', err);
        }

        if (nextCardbacks.length > 0) {
            cardbacks = nextCardbacks;
            CardbackArt?.prime?.(nextCardbacks);
        } else if (cardbacks.length === 0) {
            cardbacks = [
                { slug: 'default', name: 'Standard Issue', description: 'The default.', isDefault: true, imageUrl: '/assets/images/cardback.webp' }
            ];
        }
    }

    function selectedCardbackDescriptor() {
        const slug = typeof selectedCardback === 'string' && selectedCardback.trim()
            ? selectedCardback.trim()
            : 'default';
        if (slug === 'default') {
            return {
                kind: 'default',
                slug,
                url: '/assets/images/cardback.webp',
            };
        }
        const chosen = cardbacks.find((entry) => entry?.slug === slug);
        const url = sanitizeCardbackImageUrl(chosen?.imageUrl);
        return url
            ? { kind: 'custom-ready', slug, url }
            : { kind: 'custom-pending', slug, url: '' };
    }

    /** The balance the player may actually spend; unknown is never spendable. */
    function spendableStardust() {
        const numeric = Number(stardust);
        return Number.isFinite(numeric) ? numeric : 0;
    }

    function updateStardustDisplay() {
        // The chip used to install a spend menu here, because Collection
        // crafting spent Stardust and the player had to choose which Stardust
        // it came from. Every Stardust credit is spendable everywhere now, so
        // the chip is a balance and a way to the Moon Store — nothing to pick.
        const stardustValue = document.getElementById('collection-stardust-value');
        if (!stardustValue) return;
        const numeric = Number(stardust);
        if (!Number.isFinite(numeric)) {
            // Unknown looks unknown. Printing 0 here made a real balance read
            // zero for the whole load and then jump to the truth.
            delete stardustValue.dataset.countValue;
            stardustValue.textContent = '\u2014';
            return;
        }
        if (FX?.setCountDisplay) {
            FX.setCountDisplay(stardustValue, numeric, { render: formatStardustWithSymbol });
        } else {
            stardustValue.dataset.countValue = String(numeric);
            stardustValue.textContent = formatStardustWithSymbol(numeric);
        }
    }

    /* What the Moon Exchange is handed. While its catalog is still on its
       way (the tab opens before it), `catalogPending` says so: the cards
       meanwhile are only the paged grid's, which draw faces but are not the
       sell column's list. */
    function exchangeContext() {
        return {
            allCards: auxiliaryAllCards.length > 0 ? auxiliaryAllCards : allCards,
            ownedCardMap,
            ownedCardStellarMap,
            currentUserId: profileId,
            stardust: spendableStardust(),
            onStardustUpdate: handleStardustUpdate,
            onInventoryUpdate: handleInventoryUpdate,
            onInventoryInvalidated: handleInventoryInvalidated,
            catalogPending: auxiliaryAllCards.length === 0 && auxiliaryCatalogComplete !== false && Boolean(auxiliaryCardsRequest),
            // A collection too large to list whole: the sell column searches instead.
            catalogComplete: auxiliaryCatalogComplete !== false,
        };
    }

    async function syncAuxiliaryContexts({ refreshExchangeInventory = false } = {}) {
        if (MoonExchange?.setContext) {
            MoonExchange.setContext(exchangeContext());
            if (refreshExchangeInventory && !auxiliaryInventoryStale && typeof MoonExchange?.refreshInventory === 'function') {
                await MoonExchange.refreshInventory();
            }
        }
        if (CardPacks?.setContext) {
            CardPacks.setContext({
                getLegacyCards: ensureAuxiliaryCardsLoaded,
                allCards: auxiliaryAllCards.length > 0 ? auxiliaryAllCards : allCards,
                ownedCardMap,
                ownedCardStellarMap,
                currentUserId: profileId,
                stardust: spendableStardust(),
                selectedCardback: selectedCardbackDescriptor(),
                onStardustUpdate: handleStardustUpdate,
                onInventoryUpdate: handleInventoryUpdate,
                onInventoryInvalidated: handleInventoryInvalidated,
            });
        }
    }

    function handleStardustUpdate(nextStardust) {
        if (!Number.isFinite(nextStardust)) return;
        stardust = nextStardust;
        updateStardustDisplay();
        // A pack, an exchange trade or a craft just moved the balance; the
        // lobby chip behind this modal has to agree with the header above it.
        announceStardust(stardust);
        void syncAuxiliaryContexts();
    }

    function handleInventoryUpdate(cardId, nextQty, variant = 'normal') {
        if (!cardId) return;
        const numericQty = Number(nextQty) || 0;
        const map = variant === 'stellar' ? ownedCardStellarMap : ownedCardMap;
        if (numericQty > 0) {
            map.set(cardId, numericQty);
        } else {
            map.delete(cardId);
        }
        cardsNeedRemoteRefresh = true;
        invalidateCardsCache();
        if (activeSection === 'cards') {
            renderCards(undefined, { refresh: true });
        }
        void syncAuxiliaryContexts({ refreshExchangeInventory: true });
    }

    function handleInventoryInvalidated(accountId) {
        if (!accountId || accountId !== profileId) return;
        // An exchange can finish while this screen is closed. Historical
        // receipts are not current quantities: refresh each dependent surface
        // from its authoritative source before it becomes usable again.
        markHoldingsChanged();
    }

    /**
     * The account's holdings changed somewhere this screen did not see: every
     * page and catalog read before it is stale. An open Cards view re-reads
     * in place (the reconcile keeps each tile that did not change); a closed
     * one reopens held until the new page is in (open()).
     */
    function markHoldingsChanged() {
        auxiliaryInventoryRevision += 1;
        auxiliaryInventoryStale = true;
        auxiliaryAllCards = [];
        auxiliaryCatalogComplete = null;
        cardsNeedRemoteRefresh = true;
        invalidateCardsCache();
        if (activeSection === 'cards') void renderCards(undefined, { refresh: true });
        if (!isOpen) rewarmLobbyWindow();
    }

    /* THE EXCHANGE OPENS BEFORE THE CATALOG (read audit F3, 28 September
       2026). It waited for `/api/cards` — the whole owned catalog, 0.6-1.1 s
       on the WebKit 4G seat — before open() could start. Its listing rows now
       carry their own art and its warm read the entry rows' cards
       (page-scoped, localized), so only its sell column, whose card list is
       this catalog, waits for it: handed over when it lands. A catalog that
       fails is said in the sell column; the market still works. Also after a
       language change invalidates the catalog with the Exchange on screen. */
    function handOverExchangeCatalog(generation) {
        const requestId = ++exchangeCatalogRequestId;
        const isCurrent = () => requestId === exchangeCatalogRequestId
            && collectionSessionIsCurrent(generation, 'exchange');
        const hasCurrentCatalog = (auxiliaryAllCards.length > 0 || auxiliaryCatalogComplete === false)
            && auxiliaryCatalogLanguage === (i18n?.getLanguage?.() || 'en');
        const catalogRead = hasCurrentCatalog ? null : ensureAuxiliaryCardsLoaded();
        // A language refresh replaces the names together when they arrive.
        // Clearing the live list first loses the chosen card and quantity.
        if (auxiliaryAllCards.length === 0 || !catalogRead) {
            MoonExchange?.setContext?.({ ...exchangeContext(), allCards: auxiliaryAllCards, catalogPending: Boolean(catalogRead) });
        }
        if (!catalogRead) return;
        catalogRead.then(() => {
            if (!isCurrent()) return;
            MoonExchange?.setContext?.({ ...exchangeContext(), catalogPending: false });
        }, (err) => {
            if (!isCurrent()) return;
            auxiliaryAllCards = [];
            auxiliaryCatalogLanguage = null;
            auxiliaryCatalogComplete = null;
            console.warn('[Collection] Failed to load exchange cards:', err);
            MoonExchange?.setContext?.({ ...exchangeContext(), allCards: [], catalogPending: false, catalogFailed: true });
        });
    }

    async function openAuxiliarySection(section, generation) {
        const surface = section === 'exchange'
            ? MoonExchange
            : CardPacks;
        if (!surface?.open || !collectionSessionIsCurrent(generation, section)) return false;

        if (section === 'exchange' && surface.showLoadingSkeleton) {
            surface.init?.();
            surface.showLoadingSkeleton();
        }

        if (!collectionSessionIsCurrent(generation, section)) return false;
        if (section === 'exchange') {
            handOverExchangeCatalog(generation);
        } else {
            // Packs negotiates its own bounded opening metadata. Only an older
            // deployment may invoke the supplied compatibility loader.
            surface.setContext?.({
                allCards: auxiliaryAllCards,
                ownedCardMap,
                ownedCardStellarMap,
                currentUserId: profileId,
                stardust: spendableStardust(),
                selectedCardback: selectedCardbackDescriptor(),
                getLegacyCards: ensureAuxiliaryCardsLoaded,
                onStardustUpdate: handleStardustUpdate,
                onInventoryUpdate: handleInventoryUpdate,
                onInventoryInvalidated: handleInventoryInvalidated,
            });
        }
        const result = await surface.open();
        if (!collectionSessionIsCurrent(generation, section)) {
            // A newer opening of the same surface owns its own read generation.
            // The older asynchronous completion must not close that successor.
            if (!isOpen || activeSection !== section) surface.close?.();
            return false;
        }
        return result ?? true;
    }

    /*
     * A TAB THAT CANNOT SHOW ITSELF YET KEEPS THE ONE BEING LEFT ON SCREEN.
     *
     * The Moon Exchange (and the Decks tab, when it was not prepared) holds
     * its body — laid out, unpainted (surface-entry-hold.js) — until its first
     * page is committed, with one loading state only past a short delay or
     * the shared grace. Hiding the tab being left at the press showed an
     * empty content area until then: Cardbacks -> Exchange withdrew its backs
     * 78-338 ms before anything replaced them, in every seat (round 13, the
     * meter's "withdrawn"), and a warm entry now waits its 450 ms grace before
     * any loading state. So the tab being left stays exactly where it was —
     * taken out of the flow, so the held tab lays out in its place as if it
     * had gone — and inert, until the held tab paints its first frame: its
     * release, or its one loading state. Capped.
     */
    const SECTION_HANDOVER_CAP_MS = 2000;
    const SECTION_SHOWN_DISPLAY = { cards: '', deckbuilder: 'flex', cardbacks: 'flex', exchange: 'flex', packs: 'flex' };
    let sectionHandover = null;

    function sectionElement(section) {
        return document.getElementById(`collection-${section}-section`);
    }

    /** Whether the modal is on screen now (not closed, not held unseen). */
    function collectionShownNow() {
        const modal = document.getElementById('collection-modal');
        return isOpen && !collectionPresentationHeld() && Boolean(modal) && modal.style.display !== 'none';
    }

    /** A hold on it: its body laid out and unpainted, under its one loading state or nothing. */
    function sectionHeld(element) {
        if (!element) return false;
        return element.classList.contains('surface-entry-hold')
            || Boolean(element.querySelector('.surface-entry-hold'));
    }

    /** Held with nothing painted yet: a hold on it, and no loading state up. */
    function sectionHeldUnseen(element) {
        return sectionHeld(element) && !element.querySelector('.surface-entry-loading');
    }

    /** Its box while it is still in the flow, in its containing block's terms and on screen. */
    function measureSectionBox(element) {
        if (!element?.isConnected || element.style.display === 'none') return null;
        return {
            left: element.offsetLeft,
            top: element.offsetTop,
            width: element.offsetWidth,
            height: element.offsetHeight,
            rect: element.getBoundingClientRect(),
        };
    }

    function pinSection(element, box, display) {
        const place = ({ left, top, width, height }) => {
            element.style.position = 'absolute';
            element.style.left = `${left}px`;
            element.style.top = `${top}px`;
            element.style.width = `${width}px`;
            element.style.height = `${height}px`;
            element.style.margin = '0';
            element.style.boxSizing = 'border-box';
        };
        element.style.display = display;
        place(box);
        // Onto the very pixels it had, whatever zoom or transform stands
        // between its containing block and the screen (the modal's large-
        // screen zoom): offsets are whole pixels, the painted box is not.
        const now = element.getBoundingClientRect();
        if (now.width > 0 && now.height > 0 && box.rect.width > 0 && box.rect.height > 0) {
            const perX = box.width / now.width;
            const perY = box.height / now.height;
            place({
                left: box.left + (box.rect.left - now.left) * perX,
                top: box.top + (box.rect.top - now.top) * perY,
                width: box.rect.width * perX,
                height: box.rect.height * perY,
            });
        }
        element.setAttribute('inert', '');
        element.setAttribute('aria-hidden', 'true');
    }

    /**
     * What leaving a tab tears down, each of which changes what it paints:
     * the backs' pictures lose their sources (retrying-image.ts), the
     * Exchange disposes unsettled art and clears its status, the Packs clear
     * their card backs. So it runs once the tab is off screen.
     */
    function sectionTearDown(section) {
        if (section === 'cards') {
            return () => {
                for (const controller of cardsWarmOwners) controller.abort();
                cardsWarmController = null;
            };
        }
        if (section === 'exchange') return () => MoonExchange?.close?.();
        if (section === 'packs') return () => CardPacks?.close?.();
        if (section === 'cardbacks') {
            return () => disposeRetryingCardbackImages(document.getElementById('collection-cardback-grid'));
        }
        return null;
    }

    /**
     * The tab being left goes, as it was before it was pinned, and is torn
     * down behind the tab that took over — unless it is the one picked again
     * (`tearDown: false`): then it takes its own place back, whole.
     */
    function unpinSection(handover, { tearDown = true } = {}) {
        const { leaving, saved } = handover;
        if (saved.style === null) leaving.removeAttribute('style');
        else leaving.setAttribute('style', saved.style);
        leaving.style.display = 'none';
        if (!saved.inert) leaving.removeAttribute('inert');
        if (saved.ariaHidden === null) leaving.removeAttribute('aria-hidden');
        else leaving.setAttribute('aria-hidden', saved.ariaHidden);
        if (tearDown) handover.tearDown?.();
    }

    /** Stop watching, and leave the tab being left pinned for the caller to decide. */
    function detachSectionHandover() {
        const handover = sectionHandover;
        sectionHandover = null;
        if (!handover) return null;
        handover.observer?.disconnect();
        clearTimeout(handover.timer);
        return handover;
    }

    function finishSectionHandover() {
        const handover = detachSectionHandover();
        if (handover) unpinSection(handover);
    }

    /*
     * It ends when the tab picked paints its first frame — or when a tab
     * left while it was itself held (showing its one loading state) is
     * released: its module is left running until the hand-over ends, and its
     * release would reveal it, abandoned and inert, over the area of the tab
     * picked. It goes in that task instead, before a frame paints it.
     */
    function watchSectionHandover(handover, entering) {
        handover.entering = entering;
        if (typeof MutationObserver === 'function') {
            const leavingReleased = () => handover.leavingHeld && !sectionHeld(handover.leaving);
            handover.observer = new MutationObserver(() => {
                if (sectionHandover !== handover) return;
                if (!sectionHeldUnseen(entering) || leavingReleased()) finishSectionHandover();
            });
            const watched = { attributes: true, attributeFilter: ['class'], childList: true, subtree: true };
            handover.observer.observe(entering, watched);
            if (handover.leavingHeld) handover.observer.observe(handover.leaving, watched);
        }
        handover.timer = setTimeout(() => {
            if (sectionHandover === handover) finishSectionHandover();
        }, SECTION_HANDOVER_CAP_MS);
        sectionHandover = handover;
    }

    /**
     * After the switch: the tab being left stays pinned only while the tab
     * picked shows nothing yet, and is torn down when it goes. `carried` is a
     * hand-over already under way (a third tab picked before the held one
     * showed): what the player sees is still that one's tab being left, and
     * the held tab abandoned (`tearDown`, never shown) goes now.
     */
    function settleSectionHandover({ leaving, leavingSection, box, display, carried, tearDown }) {
        const entering = sectionElement(activeSection);
        if (!leaving || !entering || !sectionHeldUnseen(entering) || (!carried && !box)) {
            if (carried) unpinSection(carried);
            tearDown?.();
            return;
        }
        if (carried) {
            carried.leaving.style.display = carried.display;
            watchSectionHandover(carried, entering);
            tearDown?.();
            return;
        }
        const handover = {
            leaving,
            leavingSection,
            // Kept showing its own loading state (a held tab with nothing
            // painted is never kept: switchSection).
            leavingHeld: sectionHeld(leaving),
            display,
            tearDown,
            saved: {
                style: leaving.getAttribute('style'),
                inert: leaving.hasAttribute('inert'),
                ariaHidden: leaving.getAttribute('aria-hidden'),
            },
        };
        pinSection(leaving, box, display);
        watchSectionHandover(handover, entering);
    }

    function switchSection(section, {
        skipSectionOpen = false,
        skipCardsRender = false,
        ownerGeneration = openGeneration,
    } = {}) {
        const previousSection = activeSection;
        const nextSection = getValidCollectionSection(section);
        // What the player is looking at now, measured while it is in the flow.
        let carried = nextSection !== previousSection ? detachSectionHandover() : null;
        if (carried && carried.leavingSection === nextSection) {
            // Picked again: it takes its own place back, whole.
            unpinSection(carried, { tearDown: false });
            carried = null;
        } else if (carried && skipSectionOpen) {
            unpinSection(carried);
            carried = null;
        }
        // A Cards tab showing a card detail is left at once: closing the
        // detail turns it back into the grid, which it would then show while
        // the tab picked is held. So is a tab still held with nothing painted:
        // there is nothing of it to keep, and its release would reveal it.
        const onScreen = carried ? carried.leaving : sectionElement(previousSection);
        const leaving = nextSection !== previousSection && !skipSectionOpen && collectionShownNow()
            && !(previousSection === 'cards' && activeDetailCardId !== null)
            && (carried || !sectionHeldUnseen(onScreen))
            ? onScreen
            : null;
        const leavingBox = leaving && !carried ? measureSectionBox(leaving) : null;
        const leavingDisplay = leaving && !carried ? SECTION_SHOWN_DISPLAY[previousSection] : null;
        // loadData paints Cards before the rest of Collection bootstrap has
        // finished, then reconciles Cards -> Cards. That is not a surface
        // transition: clearing here would strand a stationary pointer with no
        // preview until it leaves and re-enters the card. Real section changes
        // still invalidate both visible and pending preview work.
        //
        // The card detail follows the same rule. `open()` runs switchSection a
        // second time after its bootstrap awaits, and tearing the detail down
        // there stole a card the player had already tapped — the detail flashed
        // open and vanished whenever the tap beat the reconciliation home.
        // The mobile filter drawer belongs to the section, and it follows the
        // very same rule for the very same reason. Closing it unconditionally
        // meant the reconciliation pass shut a drawer the player had just
        // opened, with no input of any kind — traced on a real iPhone page box:
        // `is-open` was dropped with no pointerdown, click, keydown or scroll
        // in front of it, and the player's next tap on Filters then re-opened
        // the drawer instead of closing it.
        if (nextSection !== previousSection) {
            invalidateHoverSurface();
            // The section picked comes up under wherever the pointer rests: a
            // tab pressed from the keyboard left the mouse on the grid.
            hoverIntent.disarm();
            closeCardDetail();
            setMobileFiltersOpen(false);
        }
        activeSection = nextSection;
        const modal = document.getElementById('collection-modal');
        if (modal) {
            modal.classList.toggle('collection-packs-active', activeSection === 'packs');
            modal.classList.toggle('collection-cardbacks-active', activeSection === 'cardbacks');
            modal.classList.toggle('collection-deckbuilder-active', activeSection === 'deckbuilder');
        }
        // The tab being left is torn down once it is off screen: now, unless
        // it stays on screen for a held tab (settleSectionHandover).
        const tearDownLeaving = previousSection !== activeSection ? sectionTearDown(previousSection) : null;

        document.querySelectorAll('#collection-modal .collection-tabs .deck-tab').forEach(tab => {
            const selected = tab.dataset.section === activeSection;
            tab.classList.toggle('active', selected);
            tab.setAttribute('aria-selected', String(selected));
            tab.tabIndex = selected ? 0 : -1;
        });

        const cardsSection = document.getElementById('collection-cards-section');
        const deckBuilderSection = document.getElementById('collection-deckbuilder-section');
        const cardbacksSection = document.getElementById('collection-cardbacks-section');
        const exchangeSection = document.getElementById('collection-exchange-section');
        const packsSection = document.getElementById('collection-packs-section');
        // A tab kept on screen for a held one keeps its display: the hand-over
        // decides when it goes (settleSectionHandover, or its end). A second
        // switch to the held tab (a re-press, or open() reconciling) hid it,
        // and the empty content area came back while that tab was held.
        const kept = carried?.leaving || sectionHandover?.leaving || null;
        // Clear the inline display when active (rather than hardcoding 'block')
        // so the stylesheet owns the cards section's layout — desktop stays the
        // default block, and mobile.css can make it a fit-to-viewport flex column.
        if (cardsSection && cardsSection !== kept) cardsSection.style.display = activeSection === 'cards' ? '' : 'none';
        if (deckBuilderSection && deckBuilderSection !== kept) deckBuilderSection.style.display = activeSection === 'deckbuilder' ? 'flex' : 'none';
        if (cardbacksSection && cardbacksSection !== kept) cardbacksSection.style.display = activeSection === 'cardbacks' ? 'flex' : 'none';
        // The Exchange is a height-bounded flex surface: its sell composer and
        // market each own an internal data lane. Setting `block` inline defeated
        // the stylesheet's flex contract and made the whole market fall below the
        // sell form on short landscape screens.
        // Before the section is displayed: the sell column's answer (form or
        // "opens after N wins") is applied from the warmed config so the first
        // frame is whole — see MoonExchange.prepare().
        if (activeSection === 'exchange') MoonExchange?.prepare?.();
        if (activeSection === 'packs') CardPacks?.prepare?.();
        if (exchangeSection && exchangeSection !== kept) exchangeSection.style.display = activeSection === 'exchange' ? 'flex' : 'none';
        if (packsSection && packsSection !== kept) packsSection.style.display = activeSection === 'packs' ? 'flex' : 'none';

        if (activeSection === 'cards') {
            // A page kept on screen takes the fit measured now, with its
            // columns, in this frame; a new capacity waits for its own page.
            if (!syncCardsPageSize({ preserveOffset: true })) refitShownCardsPage();
        } else if (activeSection === 'cardbacks') {
            syncCardbacksPageSize({ preserveOffset: true });
            // Cardbacks are already loaded during the Collection bootstrap.
            // Paint that cached row synchronously after the panel becomes
            // measurable; the background refresh below may then replace the
            // data without exposing the old hidden-panel page size.
            if (cardbacks.length > 0) renderCardbacks();
        }

        if (skipSectionOpen) {
            if (carried) unpinSection(carried);
            tearDownLeaving?.();
            return null;
        }

        let sectionPromise = null;
        if (activeSection === 'cards' && !skipCardsRender) {
            // Gate ONLY on what this grid actually consumes. The old gate was
            // the whole support-data batch, whose slowest member is a ~500ms
            // character fetch for the deck builder — so returning to Cards
            // destroyed a correct page half a second after the player arrived.
            // Forge costs are a small memoized read the craft badge does need;
            // everything else decorates the committed page in place.
            // The page kept: the one built ahead of it carries on.
            sectionPromise = committedCardsGridIsCurrent()
                ? Promise.resolve(attachStellarVideosToGrid()).then(resumeCardsPrebuild)
                : loadForgeCosts()
                    .then(() => renderCards())
                    .then(() => loadStellarAssets())
                    .then(() => attachStellarVideosToGrid());
            void loadCollectionSupportData().then(applyCardsSupportDecorations);
        } else if (activeSection === 'deckbuilder') {
            sectionPromise = ensureDeckBuilderSection();
        } else if (activeSection === 'cardbacks') {
            sectionPromise = loadCollectionSupportData().then(() => renderCardbacks());
        } else if (
            activeSection === 'exchange'
            && suspendedSectionOpenGeneration !== ownerGeneration
        ) {
            sectionPromise = openAuxiliarySection('exchange', ownerGeneration);
        } else if (
            activeSection === 'packs'
            && suspendedSectionOpenGeneration !== ownerGeneration
        ) {
            sectionPromise = openAuxiliarySection('packs', ownerGeneration);
        }
        // Every hold the picked tab takes is begun by now (its prepare, or the
        // Decks tab's open), and nothing has been painted since the press.
        settleSectionHandover({
            leaving,
            leavingSection: previousSection,
            box: leavingBox,
            display: leavingDisplay,
            carried,
            tearDown: tearDownLeaving,
        });
        return sectionPromise;
    }

    function switchCardTab(tab) {
        closeCardDetail();
        const nextCardTab = tab === 'spells' ? 'spells' : 'characters';
        const cardTypeChanged = nextCardTab !== activeCardTab;
        activeCardTab = nextCardTab;
        cardsPage = 1;
        if (cardTypeChanged) {
            currentFilters = { collection: '', mana: '' };
            activeSpellOwnerFilter = 'all';
        }

        const collectionFilter = document.getElementById('collection-filter-collection');
        const manaFilter = document.getElementById('collection-filter-mana');
        if (cardTypeChanged && collectionFilter) collectionFilter.value = '';
        if (cardTypeChanged && manaFilter) manaFilter.value = '';
        document.querySelectorAll('#collection-modal .collection-card-tabs .deck-tab').forEach(tabBtn => {
            const selected = tabBtn.dataset.cardTab === activeCardTab;
            tabBtn.classList.toggle('active', selected);
            tabBtn.setAttribute('aria-pressed', String(selected));
        });
        const spellOwnerFilter = document.getElementById('collection-filter-spell-owner');
        if (spellOwnerFilter) {
            if (cardTypeChanged) spellOwnerFilter.value = 'all';
            spellOwnerFilter.style.display = activeCardTab === 'spells' ? 'inline-block' : 'none';
        }
        renderCards();
    }

    /* While the filter sheet is open, the surface underneath must not move.
       iOS scrolls the page to reveal a focused form control — and every control
       in this sheet is a native <select>, whose picker is what triggers that
       scroll. No headless engine renders that picker, so the jump reproduces
       only on a real phone ("opening filters ... scrolls down an breaks ui").
       Rather than guess at the offender, pin every scroll position that was
       true when the sheet opened and restore it for as long as it is open.
       Nothing here blocks scrolling once the sheet closes. */
    let filterScrollLock = null;

    function lockScrollWhileFiltersOpen(popover) {
        releaseFilterScrollLock();
        const scrollers = [document.scrollingElement, document.body]
            .concat(Array.from(document.querySelectorAll('#collection-modal, #collection-modal *')))
            .filter((el) => el && !popover.contains(el));
        const marks = scrollers.map((el) => [el, el.scrollTop, el.scrollLeft]);
        const restore = () => {
            marks.forEach(([el, top, left]) => {
                if (el.scrollTop !== top) el.scrollTop = top;
                if (el.scrollLeft !== left) el.scrollLeft = left;
            });
        };
        document.addEventListener('scroll', restore, true);
        window.addEventListener('scroll', restore, true);
        filterScrollLock = () => {
            document.removeEventListener('scroll', restore, true);
            window.removeEventListener('scroll', restore, true);
        };
    }

    function releaseFilterScrollLock() {
        if (!filterScrollLock) return;
        try { filterScrollLock(); } catch (_) { /* listener already gone */ }
        filterScrollLock = null;
    }

    function setMobileFiltersOpen(open, { returnFocus = false } = {}) {
        const popover = document.getElementById('collection-filter-popover');
        const toggle = document.getElementById('collection-mobile-filters-toggle');
        if (!popover || !toggle) return;
        popover.classList.toggle('is-open', Boolean(open));
        toggle.setAttribute('aria-expanded', String(Boolean(open)));
        if (open) lockScrollWhileFiltersOpen(popover);
        else releaseFilterScrollLock();
        if (!open && returnFocus) toggle.focus();
    }

    /**
     * Placeholders are for a grid with NOTHING to show — and, since the open
     * is held until its first page is committed, only for the one moment that
     * genuinely has nothing: an open that reached its cap before the page did
     * (`presentCollection`), or a retry after a failed read.
     *
     * This used to wipe the container unconditionally, and it was called twice
     * per open (once by `open()`, once by `renderCards`) plus again on every
     * re-entry and page turn — so a page of correct cards the player was
     * already reading got destroyed and rebuilt, replaying the entrance
     * cascade each time. Progressive rendering (docs/asset-loading.md law 2)
     * says the opposite: keep showing what is true until the next truth is
     * ready. The next page is built off-screen in the paint stage and swapped
     * in atomically by `commitStage`; a page building behind it is kept.
     *
     * `replaceStale` is the capped open presenting over tiles that answer a
     * different question (a reopen on the page the player last left).
     */
    function renderCardsSkeletons({ replaceStale = false } = {}) {
        const container = document.getElementById('collection-cards-grid');
        if (!container) return;
        if (!replaceStale && gridShowsSomething(container)) return;
        if (container.querySelectorAll(':scope > .skeleton-card').length === cardsPageSize) return;
        applyCardsGridColumns(cardsPageSize);
        const stage = container.querySelector(':scope > .collection-card-paint-stage');
        Array.from(container.children).forEach((child) => {
            if (child !== stage) disposeCollectionExactImages(child);
        });
        const wells = [];
        for (let i = 0; i < cardsPageSize; i++) {
            const skel = document.createElement('div');
            skel.className = 'deck-card-item collection-card-item skeleton-card';
            const inner = document.createElement('div');
            inner.className = 'skeleton-card-inner';
            const img = document.createElement('div');
            img.className = 'skeleton-card-image';
            const title = document.createElement('div');
            title.className = 'skeleton-card-title';
            const desc = document.createElement('div');
            desc.className = 'skeleton-card-desc';
            inner.appendChild(img);
            inner.appendChild(title);
            inner.appendChild(desc);
            skel.appendChild(inner);
            wells.push(skel);
        }
        container.replaceChildren(...wells, ...(stage ? [stage] : []));
    }

    /*
     * A PAGE PASSED OVER IS DROPPED AT ONCE. A render builds its page in a
     * paint stage and commits it only if it is still the page asked for; a
     * later press (fast hands: Next, Next) makes it answer nothing. Its stage
     * used to stay until its own commit — every raster it had queued in
     * cards.js ran to the end first, 100-300 ms each on the Chrome phone seat,
     * and the presses after it were answered 240-590 ms late (wd-D run, 28
     * September 2026). Out of the document, its tiles' queued raster steps are
     * skipped (cards.js settles only a connected card).
     */
    let renderStage = null;

    function dropRenderStage() {
        const stage = renderStage;
        renderStage = null;
        if (!stage?.isConnected) return;
        disposeCollectionExactImages(stage);
        stage.remove();
    }

    /** The one loading state on screen: the page of wells, the Collection presented over it. */
    function gridShowsWells(container) {
        return !collectionPresentationHeld()
            && Boolean(container?.querySelector?.(':scope > .skeleton-card'));
    }

    /** The grid's one loading state is up: the open's wells, or the page a re-query keeps under its glints. */
    function gridShowsLoadingState(container) {
        return gridShowsWells(container)
            || Boolean(container?.classList?.contains('collection-cards-grid--stale'));
    }

    /** Cards, or the truthful "no cards match" answer — anything but a failure or nothing. */
    function gridShowsSomething(container) {
        return gridHasCommittedPage(container)
            || Boolean(container?.querySelector(':scope > .collection-empty:not([data-load-failed]):not([data-index-wait])'));
    }

    /**
     * The page's own pictures, asked for on screen and gated (capped) for its
     * reveal. `onWarm` receives the warm itself, uncapped — or null when there
     * is nothing to warm — which the look-ahead waits for (readNextPageAhead).
     */
    async function warmVisibleCardPage(entries, hold = null, onWarm = null, signal = null) {
        const preloader = AssetPreloader;
        const cards = (entries || []).map((entry) => entry?.card).filter(Boolean);
        if (typeof preloader?.warmCards !== 'function' || cards.length === 0) {
            onWarm?.(null);
            return;
        }
        try {
            const warm = preloader.warmCards(cards, { priority: 'high', signal });
            notePresentationArt(hold, warm, entries);
            onWarm?.(warm);
            const urls = pictureUrlsOf(entries);
            Promise.resolve(warm).then(() => { if (!signal?.aborted) notePicturesInHand(urls); }, () => {});
            if (typeof preloader.gateWarm === 'function') {
                await preloader.gateWarm(warm);
            }
        } catch (err) {
            // Warming is a bounded visual optimization. The direct card image
            // resolver remains authoritative on every failed/unsupported path.
            console.warn('[Collection] Visible card warm failed', err);
            onWarm?.(null);
        }
    }

    async function settleVisibleCollectionCard(cardElement, cardData) {
        const settle = Cards?.settleCardRasterForPresentation;
        if (typeof settle !== 'function') return false;
        try {
            return await settle(cardElement, cardData, {
                oversample: 1,
                stableGeometry: true,
            }) === true;
        } catch (_) {
            return false;
        }
    }

    /** @type {{ button: HTMLElement, release: () => void } | null} the pager press being answered */
    let cardsPagerAck = null;
    // The page count of the last page answered (a press past the last page asks for nothing).
    let cardsTotalPages = 0;
    let cardsPrefetchTimer = null;
    let staleGridTimer = null;
    /** @type {WeakMap<Element, any>} each grid tile's page entry */
    const tileEntries = new WeakMap();
    /**
     * The tiles of the page the player just left, by signature, at most one
     * page of them. Prev, and a reopen (which always starts on page 1), put the
     * very same nodes back — their canvases already settled at this geometry —
     * instead of decoding the art again: paging onward warms the next page's
     * art into the shared decoded-image LRU (16 sources), which pushes out the
     * art of the page before.
     */
    const retiredTiles = new Map();

    /**
     * Whether a tile's art survives leaving the document. Detaching a tile
     * hands its paint to cards.js's owner sweep, which cancels a loader still
     * running and strips an <img>'s source (its loading class stays). Only a
     * settled canvas, or the truthful no-art terminal, comes back as it left;
     * kept, anything else returned as a well that shimmered forever, with no
     * loader left behind it and no settle on the way back.
     */
    function tileArtSurvivesRetirement(node) {
        return !node.querySelector?.(
            '.card-image > .card-art-loading, .card-image > img.card-art-paint:not(.card-art-unavailable)'
        );
    }

    function retireCardTile(node) {
        const signature = node?.dataset?.tileSignature;
        node?.remove?.();
        if (!signature || node.classList?.contains('skeleton-card') || !tileArtSurvivesRetirement(node)) {
            disposeCollectionExactImages(node);
            return;
        }
        retiredTiles.delete(signature);
        retiredTiles.set(signature, node);
        while (retiredTiles.size > CARDS_PAGE_SIZE) {
            const [oldest, evicted] = retiredTiles.entries().next().value;
            retiredTiles.delete(oldest);
            disposeCollectionExactImages(evicted);
        }
    }

    function forgetRetiredTiles() {
        retiredTiles.forEach((node) => disposeCollectionExactImages(node));
        retiredTiles.clear();
    }

    function releaseCardsPagerAck() {
        const ack = cardsPagerAck;
        cardsPagerAck = null;
        ack?.release?.();
    }

    /** What a page's answer is for: the question, the page and its capacity. */
    function cardsPageKey(query, page, pageSize) {
        return JSON.stringify([query, page, pageSize]);
    }

    /*
     * A PRESS BACK TO THE PAGE ON SCREEN ENDS THE TURN (review of wave E, 28
     * September 2026). Prev pressed while Next's page was on its way rendered
     * the page on screen anew: the grid went busy again, its answer was read
     * again (from the network once the page in memory was a minute old) and
     * planned against itself, and the turn ended only when that came back —
     * where the summoner select and the avatar picker end it at once. A press
     * asking, with another page on its way, for the very page on screen —
     * the same question (account, tab, summoner, filters, sort, search,
     * language), page and capacity, its answers still current — now drops
     * the page on its way in the task of the press and reads nothing. The
     * page on screen gets back what it leads to: the look-ahead of the page
     * after it and the page built ahead (kept, it carries on). A page of an
     * older question is never kept: the key is the question it answered, and
     * only its commit records it.
     */
    function endCardsTurnOnShownPage() {
        const shown = shownCardsTurn;
        if (!shown || shown.requestId === lastCardsRequestId || cardsNeedRemoteRefresh) return false;
        const container = document.getElementById('collection-cards-grid');
        if (!gridHasCommittedPage(container)) return false;
        if (shown.key !== cardsPageKey(cardsQuery(), cardsPage, cardsPageSize)) return false;
        // The render on its way answers nothing now.
        lastCardsRequestId += 1;
        if (cardsWarmController !== shown.warmController) cardsWarmController?.abort();
        cardsWarmController = shown.warmController;
        shown.requestId = lastCardsRequestId;
        dropRenderStage();
        container.removeAttribute('aria-busy');
        container.dataset.rasterState = 'ready';
        markCardsGridStale(container, false);
        releaseCardsPagerAck();
        // Clips the turn stopped (stopUnplayedStellarClips) come back.
        attachStellarVideosToGrid();
        currentPageArtSettled = shown.artSettled;
        readNextPageAhead(shown.pagination, shown.artSettled);
        // The look-aheads of the page it was heading to go.
        retireLookAheads();
        scheduleNextPagePrefetch(shown.pagination);
        return true;
    }

    /*
     * A PAGER BUTTON ANSWERING A PRESS STILL TAKES THE NEXT ONE. The shared
     * busy style (`.btn-pending`, pending-states.css) turns pointer events off
     * so a guarded action cannot be sent twice — but a second Next while a
     * page loads is not a repeat, it asks for the page after it. With pointer
     * events off it landed on the pager row instead: never acknowledged, and
     * fast hands ended on the page before the last one they asked for (round
     * 15, 28 September 2026, every seat: "press 3 of 3 was never acknowledged
     * ... ended on page:3, not page:4"). So the pager keeps them on, and each
     * press shows the button's own pressed state (main.css) as its answer.
     */
    function acknowledgePagerPress(button) {
        const release = acknowledgePress(button);
        button.style.pointerEvents = 'auto';
        return () => {
            release();
            button.style.removeProperty('pointer-events');
        };
    }

    /**
     * A re-query (a page turn, a filter, a search) keeps the page it replaces
     * on screen until the new one is built — the doctrine's "keep showing what
     * is true until the next truth is ready". Past the shared art grace that
     * page takes the re-query's ONE loading state: a glint crossing each of
     * its cards in turn, and a light along the foot of the pager button
     * pressed (deck-builder.css; it used to be a still grey dim, which the
     * owner read as "possibly stuck").
     * The grace counts from the first press still waiting: a press after it,
     * before the page lands, does not start it again, or a player pressing
     * Next every 120 ms would see nothing at all until they stopped.
     */
    function markCardsGridStale(container, stale) {
        if (!stale) {
            clearTimeout(staleGridTimer);
            staleGridTimer = null;
            if (container) showCardsGridLoading(container, false);
            return;
        }
        if (!container || staleGridTimer !== null || container.classList.contains('collection-cards-grid--stale')) return;
        staleGridTimer = setTimeout(() => {
            staleGridTimer = null;
            showCardsGridLoading(container, true);
        }, STALE_PAGE_GRACE_MS);
    }

    /**
     * The grid's loading state and its pager's go on and off together, in one
     * task. The glints start from the button pressed — Next lights the last
     * card first, anything else the first — and keep that direction until
     * the state ends: turning it round would restart every glint mid-card.
     */
    function showCardsGridLoading(container, loading) {
        if (loading) {
            container.dataset.glintFrom = cardsPagerAck?.button?.id === 'collection-cards-next' ? 'right' : 'left';
        } else {
            delete container.dataset.glintFrom;
        }
        container.classList.toggle('collection-cards-grid--stale', loading);
        document.getElementById('collection-cards-pagination')?.classList.toggle('collection-pagination--loading', loading);
    }

    /**
     * The deal-in is an ENTRANCE, and it is spent once played. `fx-stagger-in`
     * is a CSS animation, and a CSS animation restarts whenever its element is
     * shown again — so a class left on the tiles replayed the whole cascade
     * every time the grid came back from `display: none` (closing a card
     * detail, returning to the tab, reopening the modal).
     */
    function spendDealIn(elements) {
        const list = Array.from(elements || []).filter((element) => element?.classList);
        const spend = (element) => {
            element.classList.remove('fx-stagger-in');
            element.style.removeProperty('--stagger-delay');
        };
        list.forEach((element) => {
            element.addEventListener('animationend', () => spend(element), { once: true });
        });
        // 300 ms fade + the 12-step cap on the 28 ms stagger, with margin: the
        // same release when an engine never reports the end (reduced motion).
        setTimeout(() => list.forEach(spend), 300 + (11 * 28) + 200);
    }

    function dealInCardsGrid(container) {
        if (!container) return;
        FX?.staggerChildren?.(container);
        spendDealIn(container.children);
    }

    /**
     * Read page `page` of the current query (and warm its art, unless it is the
     * look-ahead's page: 'next') unless it is held already. Resolves to whether
     * the page is held now.
     * @returns {Promise<boolean>}
     */
    function prefetchCardsPage(page, priority = 'low') {
        if (!isOpen || !(page >= 1)) return Promise.resolve(false);
        const query = cardsQuery();
        const held = cardsPages.peek(query, page, cardsPageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS });
        if (held) {
            // A picture already warmed costs nothing to ask for again.
            if (priority !== 'low') warmPageArt(held.page.items, priority);
            return Promise.resolve(true);
        }
        return cardsPages.request(query, page, cardsPageSize, {
            prefetch: true,
            priority,
            maxAgeMs: PAGE_PRESENT_MAX_AGE_MS,
        }).then(Boolean);
    }

    /*
     * THE LOOK-AHEAD (the owner, 26 September 2026: background data at most a
     * few pages ahead — data pages are cheap — and pictures only one page
     * ahead, only after the current screen's pictures have finished, dropped
     * when the player moves on).
     *  - Answers: the next LOOK_AHEAD_PAGES pages' (~1.2 KB each; two Convex
     *    calls each on the server while its 30 s catalog context holds), so
     *    presses in quick succession paint from memory. The next page's is
     *    read the moment this page's answer is in: read after the commit, it
     *    took 1.2-1.4 s more on the WebKit phone seat and was often still out
     *    at the press (round 14). The ones after it once the page is on
     *    screen (readAnswersBeyondNext).
     *  - Pictures: the next page's first (a page is 2.5-4.2 MB), asked for
     *    once this page's own pictures have settled, never beside them, in
     *    this surface's own background scope, ahead of other surfaces'
     *    background work (NEXT_PAGE_ART_RANK); then the rest of the window
     *    (below, planFarLookAhead: the owner, 28 September 2026, "it's fine
     *    to load at least 3 pages in advance"). Another question, a page
     *    other than that one, or the close drops them: the scope's close
     *    drops what is queued and aborts what it alone is downloading — a
     *    picture the page on screen has asked for meanwhile is foreground,
     *    which no scope cancels, so a render drops the old look-ahead only
     *    after asking for its own pictures (retireLookAheads).
     *  - At a steady pace (load-scheduler.ts `steady`), two at a time. A
     *    background-soon picture starts only in an idle slot of the main
     *    thread (up to 300 ms), 250 ms after any press and 150 ms after a
     *    data read, two at a time for the whole app, and a picture the screen
     *    starts aborts it: on the Chrome phone seat (EU 4G, 4x CPU) the next
     *    page's pictures, already in the HTTP cache, started 180-380 ms apart
     *    while the page on screen settled and its tiles were built ahead, so
     *    four cached pictures took 1.3 s to be in hand, and Next onto pages 3
     *    and 4 still waited 1.4-3.1 s after 2.5 s of reading (catalog probe,
     *    28 September 2026). A steady scope waits only for a picture the
     *    screen is loading, and is never pre-empted: what the player outruns
     *    is dropped by closing its scope instead (renderCards).
     */
    const LOOK_AHEAD_PAGES = 3;
    const LOOK_AHEAD_AT_ONCE = 2;
    // The screens a Collection warm may download on (asset-preloader.ts SURFACE_SCREENS).
    const LOOK_AHEAD_SCREENS = /** @type {const} */ (['boot', 'lobby', 'deck-selector', 'collection', 'store', 'foundry']);
    /** @type {any} the look-ahead of the page after the one on screen */
    let lookAhead = null;
    /** @type {any[]} look-aheads a render replaced, dropped once it asked for its own pictures */
    let retiringLookAheads = [];

    function lookAheadScheduler() {
        return typeof LoadScheduler?.scope === 'function' ? LoadScheduler : null;
    }

    /** The promise one of the look-ahead's pictures arrives by: true in hand, false dropped or never asked. */
    function lookAheadArrival(ahead, url) {
        let arrival = ahead.arrivals.get(url);
        if (!arrival) {
            let resolve = null;
            const promise = new Promise((done) => { resolve = done; });
            arrival = { promise, resolve };
            ahead.arrivals.set(url, arrival);
            // Asked for after the pictures were: none of them, or the look-ahead is gone.
            if (ahead.closed || (ahead.declared && !ahead.urls.includes(url))) arrival.resolve(false);
        }
        return arrival;
    }

    function readNextPageAhead(pagination, pageArtSettled) {
        const page = Number(pagination?.page) || 0;
        const totalPages = Number(pagination?.totalPages) || 0;
        const query = cardsQuery();
        const pageSize = cardsPageSize;
        const next = page >= 1 && page < totalPages ? page + 1 : 0;
        const key = next ? JSON.stringify([query, next, pageSize]) : null;
        if (lookAhead?.key !== key) {
            if (lookAhead) retiringLookAheads.push(lookAhead);
            lookAhead = next ? {
                key,
                query,
                queryKey: JSON.stringify(query),
                page: next,
                pageSize,
                // The page answers it was read from (pageDataEpoch).
                epoch: pageDataEpoch,
                closed: false,
                declared: false,
                urls: [],
                scope: null,
                arm: null,
                arrivals: new Map(),
                answer: cardsPages.request(query, next, pageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS }),
                settled: null,
            } : null;
            if (lookAhead) {
                let settle = null;
                lookAhead.settled = new Promise((done) => { settle = done; });
                lookAhead.settle = settle;
            }
            nextPageArtReady = lookAhead ? lookAhead.settled : null;
        } else if (lookAhead && lookAhead.epoch !== pageDataEpoch) {
            /* The answers went stale (a holdings change, a new recipe set, a
               discovery repair), not the question: the page is read again, and
               the pictures already asked for stay on the wire until that
               answer says which it still draws (declareLookAheadPictures).
               Keyed by the answers, the look-ahead was replaced and its scope
               closed at once — the real scheduler aborts what no other scope
               holds — and its successor asked for the very same pictures from
               zero (review of read audit F9(e), 27 September 2026). */
            lookAhead.epoch = pageDataEpoch;
            lookAhead.answer = cardsPages.request(query, next, pageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS });
            lookAhead.declared = false;
        }
        // The pictures wait for THIS render's page pictures (a render that
        // was superseded never settles them; the one that replaces it re-arms).
        if (lookAhead) armLookAhead(lookAhead, pageArtSettled);
    }

    /*
     * The answers after the next page's: data only, no picture, read once the
     * page is on screen (scheduleNextPagePrefetch) rather than with its answer,
     * so a search typed quickly reads them for the question it settles on,
     * not for every keystroke.
     */
    function readAnswersBeyondNext(page, totalPages) {
        const query = cardsQuery();
        for (let later = page + 2; later <= Math.min(page + LOOK_AHEAD_PAGES, totalPages); later += 1) {
            void cardsPages.request(query, later, cardsPageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS });
        }
    }

    function armLookAhead(ahead, pageArtSettled) {
        const arm = {};
        ahead.arm = arm;
        Promise.all([ahead.answer, pageArtSettled]).then(([served]) => {
            if (ahead.arm !== arm || ahead.closed || ahead.declared) return;
            ahead.declared = true;
            declareLookAheadPictures(ahead, served);
        }, () => {});
    }

    /**
     * Ask for the pictures of the page `served` answers. Asked again for the
     * same page (its answers went stale), the pictures it still draws stay
     * with the scope that has them on the wire: an unchanged set changes
     * nothing; another set is asked for in a new scope BEFORE the old one
     * lets go, so what both draw keeps its download. A failed read says
     * nothing new, and what is on the wire stays.
     */
    function declareLookAheadPictures(ahead, served) {
        const previous = ahead.scope;
        if (previous && !served) return;
        const urls = pictureUrlsOf(served?.page?.items);
        const unchanged = Boolean(previous) && urls.length === ahead.urls.length
            && urls.every((url) => ahead.urls.includes(url));
        ahead.urls = urls;
        // Asked for a picture this page does not show: nothing will arrive.
        ahead.arrivals.forEach((arrival, url) => {
            if (!urls.includes(url)) arrival.resolve(false);
        });
        if (unchanged) return;
        const scheduler = lookAheadScheduler();
        if (!scheduler || urls.length === 0) {
            urls.forEach((url) => lookAheadArrival(ahead, url).resolve(false));
            ahead.scope = null;
            previous?.close?.();
            ahead.settle();
            return;
        }
        const scope = scheduler.scope('collection-next-page', {
            runsOn: LOOK_AHEAD_SCREENS,
            maxInFlight: LOOK_AHEAD_AT_ONCE,
            steady: true,
        });
        ahead.scope = scope;
        const pictures = urls.map((url) => {
            const arrival = lookAheadArrival(ahead, url);
            return Promise.resolve(scope.soon([{ url, rank: NEXT_PAGE_ART_RANK }])).then((result) => {
                // A later answer's scope asked for it again: that one answers.
                if (ahead.scope !== scope) return;
                // Arrived, or failed: either way no longer on the wire.
                const settled = Number(result?.ready) > 0 || Number(result?.failed) > 0;
                if (settled && !ahead.closed) notePicturesInHand([url]);
                arrival.resolve(Number(result?.ready) > 0);
            }, () => {
                if (ahead.scope === scope) arrival.resolve(false);
            });
        });
        previous?.close?.();
        Promise.all(pictures).then(() => {
            if (ahead.scope === scope) ahead.settle();
        });
    }

    function dropLookAhead(ahead) {
        if (!ahead || ahead.closed) return;
        ahead.closed = true;
        ahead.scope?.close?.();
        ahead.arrivals.forEach((arrival) => arrival.resolve(false));
        ahead.settle?.();
    }

    /** The look-aheads a render replaced: dropped once it has asked for its own pictures. */
    function retireLookAheads() {
        const retiring = retiringLookAheads;
        retiringLookAheads = [];
        retiring.forEach(dropLookAhead);
    }

    /** Drop every look-ahead: the Collection closed, or its answers went stale. */
    function dropAllLookAheads() {
        retireLookAheads();
        dropLookAhead(lookAhead);
        lookAhead = null;
        nextPageArtReady = null;
        dropFarLookAhead();
        endLobbyWindow();
    }

    /** The look-ahead of `page` of `queryKey` at `pageSize`, if it is the one out. */
    function lookAheadFor(queryKey, page, pageSize) {
        const ahead = lookAhead;
        return ahead && !ahead.closed && ahead.queryKey === queryKey
            && ahead.page === page && ahead.pageSize === pageSize ? ahead : null;
    }

    /*
     * THE REST OF THE WINDOW (the owner, 28 September 2026: "it's fine to load
     * at least 3 pages in advance for everything. we should just not load it
     * all, pagination is there for a reason"; "spells is a predictable tab. If
     * we [preload] characters, we should also preload spells"). The next page
     * is the look-ahead's (above): its pictures come first, and only its tiles
     * are built ahead. Once they are in, the pictures of the rest of the
     * window follow as bytes only — never decoded, never part of a loading
     * state — in this order: the other card tab's first page (this search and
     * sort, with the filters a tab switch resets; downloaded once for its
     * question, then in the HTTP cache), the two pages after the next one,
     * and the page behind when its answer is still held (a page the player
     * jumped over). One scope for all of it, ranked in that order
     * behind other surfaces' background work (rank 0), at most two at a time;
     * a constrained link (Save-Data, 2G, 3G) is not asked to carry it.
     *
     * A new page on screen plans it again: what the new window still wants is
     * asked for in a new scope BEFORE the old one lets go, so its download
     * carries on; what it no longer wants (a page passed over) is dropped. A
     * turn that has to download its own pictures drops the whole window once
     * it has asked for them, so under its loading state only what that page
     * needs is on the wire (the owner: "if there is a loading bar, it should
     * always load just the minimum"); its commit plans the window again.
     */
    const FAR_AHEAD_RANK = 1;
    /** @type {{ urls: string[], scope: any } | null} */
    let farAhead = null;
    // Each plan and each drop: a plan whose reads answer after a later one is stale.
    let farAheadPlan = 0;

    /** The question the other card tab opens on: this search and sort, the filters a switch resets. */
    function otherCardTabQuery(query) {
        return {
            ...query,
            tab: query?.tab === 'spells' ? 'characters' : 'spells',
            spellOwner: 'all',
            collection: '',
            mana: '',
        };
    }

    /** What a link that must save its data is not asked to carry. */
    function admitFarAhead(policy) {
        return !policy?.constrained;
    }

    /** Ask for `groups` (lists of picture URLs, most likely first) in one ranked scope. */
    function declareFarLookAhead(groups) {
        const pictures = [];
        const seen = new Set();
        groups.forEach((urls, index) => (urls || []).forEach((url) => {
            if (!url || seen.has(url)) return;
            seen.add(url);
            pictures.push({ url, rank: FAR_AHEAD_RANK + index });
        }));
        const urls = pictures.map((picture) => picture.url);
        const previous = farAhead;
        if (previous && previous.urls.length === urls.length && urls.every((url, index) => previous.urls[index] === url)) return;
        const scheduler = lookAheadScheduler();
        if (!scheduler || pictures.length === 0) {
            farAhead = null;
            previous?.scope?.close?.();
            return;
        }
        const scope = scheduler.scope('collection-look-ahead', {
            runsOn: LOOK_AHEAD_SCREENS,
            maxInFlight: LOOK_AHEAD_AT_ONCE,
            admit: admitFarAhead,
            steady: true,
        });
        farAhead = { urls, scope };
        pictures.forEach((picture) => {
            Promise.resolve(scope.soon([picture])).then((result) => {
                if (farAhead?.scope === scope && (Number(result?.ready) > 0 || Number(result?.failed) > 0)) {
                    notePicturesInHand([picture.url]);
                }
            }, () => {});
        });
        // What both windows want is held by the new scope now: the old one lets go of the rest.
        previous?.scope?.close?.();
        endLobbyWindow();
    }

    /**
     * Plan the window of the page on screen (`pagination`), once the next
     * page's pictures are in (or this page's own, on the last page).
     */
    function planFarLookAhead(pagination) {
        const plan = ++farAheadPlan;
        const page = Number(pagination?.page) || 0;
        const totalPages = Number(pagination?.totalPages) || 0;
        if (!page) return;
        const query = cardsQuery();
        const pageSize = cardsPageSize;
        const generation = openGeneration;
        const onScreen = new Set(pictureUrlsOf(pagination?.items));
        const gate = nextPageArtReady || currentPageArtSettled || Promise.resolve();
        const current = () => plan === farAheadPlan && collectionSessionIsCurrent(generation);
        Promise.resolve(gate).then(() => {
            if (!current()) return null;
            // The other tab first: one page, downloaded once for its question, then
            // in the HTTP cache for every later window.
            const reads = [cardsPages.request(otherCardTabQuery(query), 1, pageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS })];
            for (let later = page + 2; later <= Math.min(page + LOOK_AHEAD_PAGES, totalPages); later += 1) {
                reads.push(cardsPages.request(query, later, pageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS }));
            }
            // The page behind only when its answer is held: never a read for it.
            reads.push(page > 1 ? cardsPages.peek(query, page - 1, pageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS }) : null);
            return Promise.all(reads);
        }).then((answers) => {
            if (!answers || !current()) return;
            const next = new Set(lookAhead && !lookAhead.closed ? lookAhead.urls : []);
            declareFarLookAhead(answers.map((served) => pictureUrlsOf(served?.page?.items)
                .filter((url) => !onScreen.has(url) && !next.has(url))));
        }, () => {});
    }

    /** Drop the window beyond the next page (a turn that must download its own page, a close). */
    function dropFarLookAhead() {
        farAheadPlan += 1;
        const previous = farAhead;
        farAhead = null;
        previous?.scope?.close?.();
    }

    // These are automatic warms, never gates on a tab the player opens. A
    // valid page still streaming its art owns the link regardless of elapsed
    // time. Follow page turns, including a turn made while its read is pending.
    async function afterPagePictures(generation, run, pending = () => currentPageArtSettled) {
        while (collectionSessionIsCurrent(generation)) {
            const current = currentPageArtSettled;
            const next = pending();
            await Promise.allSettled([current, next]);
            if (current !== currentPageArtSettled || next !== pending()) continue;
            if (collectionSessionIsCurrent(generation)) run();
            return;
        }
    }

    function packWrapperAfterPage(generation, section) {
        if (section !== 'cards') return null;
        return new Promise((resolve) => { packWrapperGate = { generation, resolve }; });
    }

    function releasePackWrapperAfterPage(generation) {
        const gate = packWrapperGate;
        if (!gate || gate.generation !== generation) return;
        packWrapperGate = null;
        void afterPagePictures(generation, gate.resolve);
    }

    /*
     * THE CARDBACKS TAB'S BACKS WAIT FOR THE NEXT PAGE'S PICTURES. They are
     * rendered behind the Cards grid with eager images, so the tab presents
     * every back at once when it is entered — but five ~1 MB PNGs started the
     * moment the grid committed took the link from page 2's pictures, the
     * Cards flow's next view, for 4-5 s on a 4G phone (round 14, 26 September
     * 2026, Chrome and WebKit phone probes; the baseline's backs were lazy).
     * Another tab is "the rest": it loads right after the next page's
     * pictures, or the current page on the last page. A
     * press on the tab (its intent warm, and the tab's own render) never
     * waits for this.
     */
    function renderCardbacksAfterNextPage(generation) {
        void afterPagePictures(generation, () => {
            renderCardbacks();
            warmCardbackArt('low', { all: true });
        }, () => nextPageArtReady || currentPageArtSettled);
    }

    /*
     * THE DECKS TAB'S FIRST PAGE, AHEAD OF ITS PRESS. Its chooser page
     * (/api/deck-builder/characters-page: two Convex queries one after the
     * other on the server, ~590 ms on the cable seat) was read only on intent,
     * ~380 ms before the press, so the tab showed its one loading state on
     * every warm and revisit visit (round 15, 28 September 2026: "2
     * placeholder(s) on a warm cache"). Under the owner's rules (data a few
     * pages ahead is cheap; pictures one page ahead, once the screen's own
     * pictures have settled, dropped when the player moves on):
     *   - its DATA — the first summoner page and at most two pages ahead,
     *     ~1 KB each, into the deck builder's summoner cache (so the saved
     *     decks' summoners need no details read at the press) — is read here,
     *     once the Cards page's own reads have answered (loadData calls this
     *     after them);
     *   - its four PORTRAITS (~0.5 MB each) are asked for, at low priority,
     *     only once the page's own pictures and the one page ahead of it have
     *     settled, like the Cardbacks tab's backs (a tab's pictures that
     *     started beside page 2's took its link for 4-5 s on a 4G phone,
     *     round 14), capped, and never for a Collection that has closed;
     *   - the Collection's close drops the portraits still loading
     *     (DeckBuilder.dropWarm, in close()).
     * The saved decks (a whole list) are still read only on intent or the
     * press. Cost per open: up to three roster reads (two Convex queries
     * each on the server), none within 30 s of the last (the deck builder
     * keeps them), and four pictures the browser then holds.
     */
    function warmDecksTab(generation) {
        if (typeof DeckBuilder?.warm !== 'function') return;
        void DeckBuilder.warm({ pictures: false });
        void afterPagePictures(generation, () => { void DeckBuilder.warm(); }, () => nextPageArtReady || currentPageArtSettled);
    }

    function scheduleNextPagePrefetch(pagination) {
        clearTimeout(cardsPrefetchTimer);
        cardsPrefetchTimer = null;
        const page = Number(pagination?.page) || 0;
        if (!page || page >= (Number(pagination?.totalPages) || 0)) {
            // No page after this one: a build the page's render kept answers nothing.
            dropCardsPrebuild();
            // The rest of its window still stands (the other tab, the page behind).
            if (page) planFarLookAhead(pagination);
            return;
        }
        const requestId = lastCardsRequestId;
        // After the committed page has had its frame, never competing with it.
        // Usually joins the read readNextPageAhead started with the answer.
        cardsPrefetchTimer = setTimeout(() => {
            cardsPrefetchTimer = null;
            void prefetchCardsPage(page + 1, 'next').then((held) => {
                if (held) scheduleCardsPrebuild(page + 1, requestId);
                else if (requestId === lastCardsRequestId) dropCardsPrebuild();
            });
            if (requestId !== lastCardsRequestId) return;
            readAnswersBeyondNext(page, Number(pagination.totalPages) || 0);
            planFarLookAhead(pagination);
        }, 0);
    }

    /**
     * PAGE N+1, BUILT WHILE PAGE N IS READ.
     *
     * Measured 25 September 2026 (snappy round 13, veteran account, Chrome
     * desktop): Next showed its page 1.75-2.0 s after the press, in 15-19
     * main-thread tasks of 50-130 ms — the eight tiles' rasters — with no
     * network at all (the page and its art had been prefetched). So the tiles
     * are built and settled AHEAD, in a hidden paint stage inside the grid (the
     * grid's own geometry, so the raster Next needs), one tile per idle period.
     * renderCards takes each settled tile the way it takes a retired one, and
     * with all of them in hand the swap happens in the task of the press.
     *
     * Bounded to one page ahead. Any render takes what matches and drops the
     * rest (a filter, a search, a tab, the page turn itself), and so does
     * closing. A render of the same question and page (a revalidation, a
     * refresh, a re-picked sort) takes nothing and keeps it: it carries on
     * after the commit, keeping every tile its page's answer still draws.
     * An input yields: the tile in flight is dropped, and the build resumes
     * once input has been quiet. The page on screen is never touched: this is
     * work the player would otherwise wait for after Next.
     *
     * Tiles are built from the still card (`stillOnly`): a Stellar card's
     * three clips (preloading, one playing) are not fetched for a page nobody
     * may open. Each carries the signature it has once its loop is attached,
     * which the commit always does (attachStellarVideosToGrid), so Next
     * decorates it rather than rebuilding it.
     */
    function scheduleCardsPrebuild(page, requestId) {
        // A newer render decides what happens to the page built ahead.
        if (requestId !== lastCardsRequestId) return;
        if (!isOpen || activeSection !== 'cards' || cardsPage !== page - 1) {
            dropCardsPrebuild();
            return;
        }
        const query = cardsQuery();
        const queryKey = JSON.stringify(query);
        const kept = cardsPrebuild;
        if (
            kept
            && kept.page === page
            && kept.pageSize === cardsPageSize
            && kept.queryKey === queryKey
            && (!kept.stage || kept.stage.isConnected)
        ) {
            // Building this very page already.
            if (!kept.taken) return;
            // Kept by the page's own render: it carries on from its answer,
            // read again, keeping the tiles that answer still draws.
            kept.requestId = requestId;
            kept.taken = false;
            kept.waiting = false;
            kept.entries = null;
            kept.index = 0;
            requestCardsPrebuildIdle(kept);
            return;
        }
        dropCardsPrebuild();
        const prebuild = {
            page,
            pageSize: cardsPageSize,
            requestId,
            query,
            queryKey,
            /** @type {HTMLElement | null} */
            stage: null,
            /** @type {any[] | null} */
            entries: null,
            index: 0,
            /** @type {HTMLElement | null} the tile whose raster is settling now */
            inFlight: null,
            /** @type {Set<HTMLElement>} tiles whose raster settled */
            settled: new Set(),
            /** @type {Set<string>} signatures whose picture has arrived (awaitPrebuildArt) */
            artReady: new Set(),
            // A picture this build waits for, still on its way.
            awaitingArt: false,
            /** @type {{ cancel: () => void } | null} */
            idle: null,
            // The grid was hidden when its turn came (a card detail, another tab).
            waiting: false,
            // A render took it: it builds nothing more.
            taken: false,
        };
        cardsPrebuild = prebuild;
        requestCardsPrebuildIdle(prebuild);
    }

    /** The next idle period, or the first one at least `delayMs` from now. */
    function requestCardsPrebuildIdle(prebuild, delayMs = 0) {
        if (prebuild.idle || prebuild.taken || cardsPrebuild !== prebuild) return;
        const run = (deadline) => {
            prebuild.idle = null;
            runCardsPrebuildSlice(prebuild, deadline);
        };
        const hasIdle = typeof window.requestIdleCallback === 'function';
        if (hasIdle && delayMs <= 0) {
            const handle = window.requestIdleCallback(run);
            prebuild.idle = { cancel: () => window.cancelIdleCallback?.(handle) };
            return;
        }
        const now = () => window.performance?.now?.() ?? Date.now();
        let handle = null;
        const timer = setTimeout(() => {
            if (hasIdle) {
                handle = window.requestIdleCallback(run);
                return;
            }
            const start = now();
            run({
                didTimeout: false,
                timeRemaining: () => Math.max(0, PREBUILD_FALLBACK_SLICE_MS - (now() - start)),
            });
        }, hasIdle ? delayMs : Math.max(delayMs, PREBUILD_FALLBACK_DELAY_MS));
        prebuild.idle = {
            cancel: () => {
                clearTimeout(timer);
                if (handle !== null) window.cancelIdleCallback?.(handle);
            },
        };
    }

    function cardsPrebuildIsCurrent(prebuild) {
        return cardsPrebuild === prebuild
            && !prebuild.taken
            && isOpen
            && prebuild.requestId === lastCardsRequestId
            && cardsPage === prebuild.page - 1
            && cardsPageSize === prebuild.pageSize
            && JSON.stringify(cardsQuery()) === prebuild.queryKey
            && (!prebuild.stage || prebuild.stage.isConnected);
    }

    /** The grid is laid out where the player can see it (not behind a card detail or another tab). */
    function cardsGridOnScreen() {
        return activeSection === 'cards'
            && document.getElementById('collection-cards-browser')?.style.display !== 'none';
    }

    function discardPrebuiltTile(node) {
        disposeCollectionExactImages(node);
        node.remove();
    }

    /** One idle period's work: at most one tile, built and set settling. */
    function runCardsPrebuildSlice(prebuild, deadline) {
        if (!cardsPrebuildIsCurrent(prebuild)) {
            if (cardsPrebuild === prebuild && !prebuild.taken) dropCardsPrebuild();
            return;
        }
        if (!cardsGridOnScreen()) {
            // Nothing to settle against; resumeCardsPrebuild picks it up.
            prebuild.waiting = true;
            return;
        }
        const container = document.getElementById('collection-cards-grid');
        if (!container) {
            dropCardsPrebuild();
            return;
        }
        if (collectionPresentationHeld() || !(Number(deadline?.timeRemaining?.()) >= PREBUILD_MIN_IDLE_MS)) {
            requestCardsPrebuildIdle(prebuild);
            return;
        }
        if (!prebuild.entries) {
            const held = cardsPages.peek(prebuild.query, prebuild.page, prebuild.pageSize, {
                maxAgeMs: PAGE_PRESENT_MAX_AGE_MS,
            });
            if (!held) {
                dropCardsPrebuild();
                return;
            }
            prebuild.entries = Array.isArray(held.page?.items) ? held.page.items : [];
            pruneCardsPrebuild(prebuild, container);
        }

        // A tile the render will find elsewhere is not built twice: after
        // Prev, the page ahead is the one just left, in the retired tiles;
        // a build carried on after its page re-rendered already holds some.
        const settledAhead = prebuiltTilesBySignature();
        let entry = null;
        let signature = '';
        while (prebuild.index < prebuild.entries.length) {
            const candidate = prebuild.entries[prebuild.index];
            signature = candidate?.card?.id ? cardTileSignature(candidate, publishedCardsScale(container)) : '';
            if (signature && !retiredTiles.has(signature) && !settledAhead.has(signature)) {
                entry = candidate;
                break;
            }
            prebuild.index += 1;
        }
        if (!entry) return;
        if (awaitPrebuildArt(prebuild, entry, signature)) return;

        const { wrapper, raster } = buildCardTile(entry, { stillOnly: true });
        wrapper.dataset.tileSignature = signature;
        if (!prebuild.stage) {
            const stage = document.createElement('div');
            stage.className = 'collection-card-paint-stage collection-card-prebuild-stage';
            stage.setAttribute('aria-hidden', 'true');
            container.appendChild(stage);
            prebuild.stage = stage;
        }
        prebuild.stage.appendChild(wrapper);
        if (!raster) {
            prebuild.settled.add(wrapper);
            prebuild.index += 1;
            requestCardsPrebuildIdle(prebuild);
            return;
        }
        prebuild.inFlight = wrapper;
        void settleVisibleCollectionCard(raster.cardElement, raster.cardData).then((settled) => {
            // Dropped meanwhile: an input, a render, a close.
            if (prebuild.inFlight !== wrapper) return;
            prebuild.inFlight = null;
            // A raster that did not settle is the render's to build and wait for.
            if (settled && wrapper.isConnected) prebuild.settled.add(wrapper);
            else discardPrebuiltTile(wrapper);
            prebuild.index += 1;
            requestCardsPrebuildIdle(prebuild);
        });
    }

    /**
     * A TILE IS BUILT AHEAD ONLY ONCE ITS PICTURE HAS ARRIVED, by the page's
     * look-ahead (readNextPageAhead); the build never pulls a picture
     * forward. It used to: each tile's settle asked for its picture at the
     * reveal's priority, one tile at a time, and the tile's own <img> started
     * a second, ungoverned request — a foreground load beside page 1's
     * Stellar loops that also held every other background picture back
     * (round 14, 26 September 2026, WebKit and Chrome phone probes on 4G:
     * Next then waited 4.6-5.2 s). True while it waits: the slice returns,
     * and the build resumes when the picture lands (or fails, or is dropped:
     * the tile is then built as the render would build it, its well
     * truthful). Without a scheduler nothing is warmed, and it is built now.
     */
    function awaitPrebuildArt(prebuild, entry, signature) {
        if (prebuild.artReady.has(signature)) return false;
        const url = pictureUrlOf(entry?.card);
        const ahead = url && lookAheadScheduler()
            ? lookAheadFor(prebuild.queryKey, prebuild.page, prebuild.pageSize)
            : null;
        const warm = ahead ? lookAheadArrival(ahead, url).promise : null;
        if (!warm) {
            prebuild.artReady.add(signature);
            return false;
        }
        prebuild.awaitingArt = true;
        const resume = () => {
            prebuild.awaitingArt = false;
            prebuild.artReady.add(signature);
            // An input meanwhile still holds the build until it has been quiet.
            const quietLeft = PREBUILD_INPUT_QUIET_MS - (Date.now() - lastPrebuildInputAt);
            requestCardsPrebuildIdle(prebuild, quietLeft > 0 ? quietLeft : 0);
        };
        Promise.resolve(warm).then(resume, resume);
        return true;
    }

    /**
     * The settled tiles its page's answer, read again, no longer draws as
     * they are (a card that left the account, new text, another geometry) go;
     * the rest stay for the page turn.
     */
    function pruneCardsPrebuild(prebuild, container) {
        const drawn = new Set(prebuild.entries
            .filter((entry) => entry?.card?.id)
            .map((entry) => cardTileSignature(entry, publishedCardsScale(container))));
        prebuild.settled.forEach((node) => {
            if (node.parentElement === prebuild.stage && drawn.has(node.dataset.tileSignature)) return;
            prebuild.settled.delete(node);
            discardPrebuiltTile(node);
        });
    }

    /**
     * An input takes the main thread back: the tile in flight goes, and the
     * build waits until input has been quiet (PREBUILD_INPUT_QUIET_MS), not
     * just for the next idle period, which falls inside a held press.
     */
    function yieldCardsPrebuild() {
        lastPrebuildInputAt = Date.now();
        const prebuild = cardsPrebuild;
        if (!prebuild || prebuild.taken || (!prebuild.inFlight && !prebuild.idle)) return;
        const inFlight = prebuild.inFlight;
        prebuild.inFlight = null;
        if (inFlight) discardPrebuiltTile(inFlight);
        prebuild.idle?.cancel();
        prebuild.idle = null;
        if (!prebuild.waiting) requestCardsPrebuildIdle(prebuild, PREBUILD_INPUT_QUIET_MS);
    }

    /** The grid is back on screen (a card detail closed, the Cards tab again). */
    function resumeCardsPrebuild() {
        const prebuild = cardsPrebuild;
        if (!prebuild?.waiting) return;
        prebuild.waiting = false;
        requestCardsPrebuildIdle(prebuild);
    }

    /**
     * A render starts: the build stops (the tile in flight goes), and what has
     * settled waits, still in its stage, for the render to take it.
     */
    function takeCardsPrebuild() {
        const prebuild = cardsPrebuild;
        if (!prebuild || prebuild.taken) return;
        prebuild.taken = true;
        prebuild.idle?.cancel();
        prebuild.idle = null;
        const inFlight = prebuild.inFlight;
        prebuild.inFlight = null;
        if (inFlight) discardPrebuiltTile(inFlight);
    }

    /** Whether the page built ahead is the one after `page` of this very question. */
    function cardsPrebuildIsAheadOf(query, page, pageSize) {
        const prebuild = cardsPrebuild;
        return Boolean(prebuild)
            && prebuild.page === page + 1
            && prebuild.pageSize === pageSize
            && prebuild.queryKey === JSON.stringify(query);
    }

    /** The settled prebuilt tiles still in their stage, by the signature each carries now. */
    function prebuiltTilesBySignature() {
        const tiles = new Map();
        const prebuild = cardsPrebuild;
        prebuild?.settled.forEach((node) => {
            if (node.parentElement === prebuild.stage && node.isConnected && node.dataset.tileSignature) {
                tiles.set(node.dataset.tileSignature, node);
            }
        });
        return tiles;
    }

    function dropCardsPrebuild() {
        const prebuild = cardsPrebuild;
        if (!prebuild) return;
        cardsPrebuild = null;
        prebuild.taken = true;
        prebuild.idle?.cancel();
        prebuild.idle = null;
        prebuild.inFlight = null;
        prebuild.settled.clear();
        if (prebuild.stage) {
            disposeCollectionExactImages(prebuild.stage);
            prebuild.stage.remove();
        }
    }

    /**
     * Everything a tile's pixels are made of. Two tiles with the same signature
     * look identical, so a refresh keeps the one already on screen (its node,
     * its settled canvas, its playing Stellar loop) instead of building a twin.
     */
    function cardTileSignature(entry, scale) {
        const { card, variant, ownedQty } = entry;
        const isStellarVariant = variant === 'stellar';
        const stellarState = isStellarVariant ? getStellarState(card.id) : null;
        return JSON.stringify([
            buildCardElementData(card, variant, stellarState?.asset),
            variant,
            Number(ownedQty) || 0,
            stellarState
                ? [stellarState.isGenerating, stellarState.isFailed, stellarState.canRetry,
                    formatEtaWindowText(stellarState.asset)]
                : null,
            isStellarVariant ? firstDiscoveryBadges.stellar.has(card.id) : firstDiscoveryBadges.fuse.has(card.id),
            isNonCraftableCard(card),
            forgeCosts?.CRAFTING ?? 10,
            i18n?.getLanguage?.() || 'en',
            // The raster was settled at this geometry.
            scale || '',
            Number(window.devicePixelRatio) || 1,
        ]);
    }

    /**
     * One grid tile, built detached. Returns the wrapper and its raster, if any.
     * `stillOnly` builds a Stellar card without its clips (the page built
     * ahead): the commit attaches the loop (attachStellarVideosToGrid).
     */
    function buildCardTile(entry, { stillOnly = false } = {}) {
        const { card, variant, ownedQty } = entry;
        const isOwned = ownedQty > 0;
        const isStellarVariant = variant === 'stellar';
        const stellarState = isStellarVariant ? getStellarState(card.id) : null;
        let raster = null;

        const cardWrapper = document.createElement('div');
        const classes = ['deck-card-item', 'collection-card-item'];
        if (!isOwned) classes.push('unowned');
        classes.push('craftable');
        if (isStellarVariant) classes.push('collection-card-item-stellar');
        if (stellarState?.isGenerating) classes.push('collection-card-item-stellar-loading');
        if (stellarState?.isFailed) classes.push('collection-card-item-stellar-failed');
        const isFirstFuse = !isStellarVariant && firstDiscoveryBadges.fuse.has(card.id);
        const isFirstStellar = isStellarVariant && firstDiscoveryBadges.stellar.has(card.id);
        if (isFirstFuse || isFirstStellar) classes.push('collection-card-item-first');
        cardWrapper.className = classes.join(' ');
        if (isFirstFuse || isFirstStellar) {
            // The tile inherits the card's deterministic discovery hues;
            // the ribbon and glow read them via CSS variables.
            MoonfallDiscoveryPalette?.applyTo(cardWrapper, card.id, isFirstStellar ? 'stellar' : undefined);
            const badge = document.createElement('div');
            badge.className = isFirstStellar
                ? 'collection-first-badge collection-first-badge--stellar'
                : 'collection-first-badge';
            badge.textContent = isFirstStellar
                ? uiText('collection.badges.firstStellar', 'First Stellar')
                : uiText('collection.badges.firstDiscovery', 'First Discovery');
            cardWrapper.appendChild(badge);
        }
        const cardKey = `${card.id}:${variant}`;
        cardWrapper.dataset.cardId = card.id;
        cardWrapper.dataset.variant = variant;
        cardWrapper.dataset.cardKey = cardKey;
        // What it shows of its print, until it is rebuilt (stellarTileIsOutdated).
        if (isStellarVariant) cardWrapper.dataset.stellarKey = JSON.stringify(stellarTileKey(stellarState));
        // What it was built from, so an in-place decoration can restate its signature.
        tileEntries.set(cardWrapper, entry);

        if (Cards?.createCardElement) {
            const cardData = buildCardElementData(card, variant, stillOnly ? null : stellarState?.asset);
            const cardEl = Cards.createCardElement(
                cardData,
                `collection-${card.id}-${variant}`
            );
            cardEl.classList.add('deck-builder-card');
            if (isStellarVariant) {
                cardEl.dataset.stellarPreview = 'collection';
            }
            cardWrapper.appendChild(cardEl);
            raster = { cardElement: cardEl, cardData };
            if (CardTilt?.bindHost) {
                CardTilt.bindHost(cardWrapper, cardEl);
            }
        } else {
            const directImage = sanitizeImageUrlForDirectClient(card.image) || '';
            cardWrapper.innerHTML = `
                <div class="deck-card-image">
                    <img alt="${escapeHtml(card.name)}" loading="lazy">
                </div>
                <div class="deck-card-name">${escapeHtml(card.name)}</div>
                <div class="deck-card-cost">${card.cost || 0}</div>
            `;
            const fallbackArt = cardWrapper.querySelector('.deck-card-image img');
            if (directImage) {
                loadCollectionExactImage(fallbackArt, directImage, {
                    fit: card.type === 'Character' ? 'cover' : 'contain',
                });
            } else {
                fallbackArt?.classList.add('card-art-unavailable');
            }
        }

        if (card.type === 'Spell' && card.character) {
            const ownerBadge = document.createElement('div');
            ownerBadge.className = 'collection-card-owner-badge';
            // Carries its own key so the label can be resolved again when
            // the character names land, without re-rendering the grid.
            ownerBadge.dataset.characterKey = card.character;
            const ownerLabel = getCharacterLabel(card.character);
            ownerBadge.textContent = ownerLabel;
            ownerBadge.title = uiText('collection.detail.ownerTitle', 'Summoner: {{owner}}', {
                owner: ownerLabel,
            });
            cardWrapper.appendChild(ownerBadge);
        }

        if (isStellarVariant) {
            const stellarBadge = document.createElement('div');
            stellarBadge.className = 'collection-card-stellar-badge';
            stellarBadge.textContent = uiText('collection.detail.stellarBadge', '✦ Stellar');
            cardWrapper.appendChild(stellarBadge);

            let statusLabel = '';
            let isStellarPending = false;
            if (stellarState?.isGenerating) {
                statusLabel = uiText('collection.detail.attuning', 'Attuning');
                isStellarPending = true;
            } else if (stellarState?.isFailed) {
                statusLabel = stellarState?.canRetry
                    ? uiText('collection.detail.retryReady', 'Retry Ready')
                    : uiText('collection.detail.refunded', 'Refunded');
            } else if (stellarState?.canRetry) {
                statusLabel = uiText('collection.detail.retryReady', 'Retry Ready');
            }

            if (statusLabel) {
                const statusBadge = document.createElement('div');
                statusBadge.className = 'collection-card-stellar-status';
                if (isStellarPending) {
                    statusBadge.classList.add('collection-card-stellar-status-pending');
                    const spinner = document.createElement('span');
                    spinner.className = 'collection-card-stellar-status-spinner';
                    statusBadge.appendChild(spinner);
                }
                const statusLabelEl = document.createElement('span');
                statusLabelEl.className = 'collection-card-stellar-status-label';
                statusLabelEl.textContent = statusLabel;
                statusBadge.appendChild(statusLabelEl);

                if (isStellarPending) {
                    const etaText = formatEtaWindowText(stellarState?.asset);
                    if (etaText) {
                        statusBadge.title = uiText('collection.detail.stellarAttunement', 'Stellar attunement: {{eta}}', {
                            eta: etaText,
                        });
                        const etaLabel = document.createElement('span');
                        etaLabel.className = 'collection-card-stellar-status-eta';
                        etaLabel.textContent = formatEtaWindowText(stellarState?.asset, { includeAbout: false });
                        statusBadge.appendChild(etaLabel);
                    } else {
                        statusBadge.title = uiText('collection.detail.stellarInProgress', 'Stellar attunement in progress');
                    }
                }
                cardWrapper.appendChild(statusBadge);
            }
        }

        if (!isOwned) {
            const lock = document.createElement('div');
            lock.className = 'deck-card-lock';
            lock.textContent = '🔒';
            lock.title = uiText('collection.detail.lockTitle', 'This card is not in your collection yet');
            cardWrapper.appendChild(lock);

            // Only quote a craft price the Foundry would actually honor.
            // Base and pack cards are granted, never forged.
            if (!isNonCraftableCard(card)) {
                const costBadge = document.createElement('div');
                costBadge.className = 'collection-card-cost';
                const craftingCost = forgeCosts?.CRAFTING ?? 10;
                costBadge.textContent = uiText('collection.detail.craftCost', 'Craft {{cost}} ✦', {
                    cost: formatStardust(craftingCost),
                });
                costBadge.title = uiText('collection.detail.craftCost', 'Craft {{cost}} ✦', {
                    cost: craftingCost,
                });
                cardWrapper.appendChild(costBadge);
            }
        }

        if (isOwned) {
            const qtyBadge = document.createElement('div');
            qtyBadge.className = 'collection-card-cost collection-card-owned-quantity';
            qtyBadge.textContent = `x${ownedQty}`;
            qtyBadge.title = variant === 'stellar'
                ? uiText('collection.detail.ownedStellarQty', 'In collection (Stellar): {{qty}}', { qty: ownedQty })
                : uiText('collection.detail.ownedNormalQty', 'In collection (Normal): {{qty}}', { qty: ownedQty });
            cardWrapper.appendChild(qtyBadge);
        }

        cardWrapper.addEventListener('click', () => {
            openCardDetail(card.id, variant, { sourceTile: cardWrapper });
        });
        // Intent: what this card's detail needs starts arriving on hover.
        cardWrapper.addEventListener('pointerenter', () => warmCardDetail(card.id));
        cardWrapper.addEventListener('focus', () => warmCardDetail(card.id));

        // The frame the loading state's glint crosses (deck-builder.css): empty at rest.
        const glint = document.createElement('span');
        glint.className = 'collection-card-loading-glint';
        glint.setAttribute('aria-hidden', 'true');
        cardWrapper.appendChild(glint);

        attachCardHoverPreview(cardWrapper, card, variant);
        return { wrapper: cardWrapper, raster };
    }

    /**
     * The grid's page. One read (usually from memory: see collection-page-cache.js),
     * one off-screen build of the tiles that changed, one swap.
     *
     * Nothing about the page on screen changes until the next one is whole:
     * the old tiles stay (under the loading state past the grace when they
     * answer a different question), the new ones settle in the paint stage,
     * and the commit puts them, the pager and the column count in place in
     * the same task. A tile
     * whose signature is unchanged is KEPT — a reopen or refresh that reads
     * the same page changes nothing on screen, not even node identity.
     */
    async function renderCards(filter = currentSearch, {
        resetScroll = false,
        refresh = false,
        revalidateAfterMs = Infinity,
    } = {}) {
        const container = document.getElementById('collection-cards-grid');
        const renderGeneration = openGeneration;
        if (!container || !collectionSessionIsCurrent(renderGeneration, 'cards')) return;
        const renderIsCurrent = () => (
            requestId === lastCardsRequestId
            && collectionSessionIsCurrent(renderGeneration, 'cards')
        );

        clearHoverPreview();
        setCurrentSearch(filter);
        const scrollTop = resetScroll ? 0 : container.scrollTop;
        const requestId = ++lastCardsRequestId;
        if (cardsWarmController !== shownCardsTurn?.warmController) cardsWarmController?.abort();
        const warmController = new AbortController();
        cardsWarmOwners.add(warmController);
        warmController.signal.addEventListener('abort', () => cardsWarmOwners.delete(warmController), { once: true });
        cardsWarmController = warmController;
        const previousShown = shownCardsTurn;
        let settlePageArt = () => {};
        let pagePicturesLanded = false;
        const pageArtSettled = new Promise((resolve) => {
            settlePageArt = () => {
                pagePicturesLanded = true;
                warmController.signal.removeEventListener('abort', settlePageArt);
                resolve(undefined);
            };
        });
        warmController.signal.addEventListener('abort', settlePageArt, { once: true });
        currentPageArtSettled = pageArtSettled;
        // The page the last render was building answers nothing now.
        dropRenderStage();
        // The page built ahead stops building; what settled waits for the plan.
        takeCardsPrebuild();
        // The page size feeds the read, so it is resolved first.
        syncCardsPageSize({ preserveOffset: true });
        const showedSomething = gridShowsSomething(container);
        // A placeholder only for a grid on screen with nothing to show (a retry).
        if (!showedSomething && !collectionPresentationHeld()) renderCardsSkeletons();
        // The deal-in is an ENTRANCE: it is earned by a surface the player
        // actually saw arrive. A render over a committed page is a refresh or a
        // re-query, and replaying a 300ms fade with up to 308ms of stagger over
        // cards already on screen is precisely the "elements load randomly and
        // reload" that was reported.
        const isEntrance = !gridHasCommittedPage(container);
        const answersSameQuestion = paintedCardsContentSignature === cardsContentSignature();
        markCardsGridStale(container, showedSomething && !answersSameQuestion);
        /* A grid with no page on it takes its measured shape (the desktop
           grid's balanced class) BEFORE it goes busy: its busy state is the
           page's one loading state, and it must close on the element that
           opened it. Taken at the commit instead, the grid went busy as one
           element and came back as another, so the page landing read as 48
           late tiles, not as that loading state's answer (wd-D run, 28
           September 2026, Chrome desktop, cold open). A grid showing
           something (a committed page, or "no cards match") keeps the shape
           it has until the next answer lands. */
        if (!gridShowsSomething(container)) applyCardsGridColumns(cardsPageSize);
        container.setAttribute('aria-busy', 'true');
        container.dataset.rasterState = 'loading';
        // Before the await: the phone's Filters button is tapped inside this
        // window, and the bootstrap list must not read as the complete one.
        reserveCollectionFilterWidth();
        updateCollectionFilterOptions();

        const finishRender = () => {
            settledCardsRequestId = requestId;
            releaseCardsPagerAck();
            markCardsGridStale(container, false);
            markCollectionPresentable('gridReady');
        };

        const query = cardsQuery();
        const pageSize = cardsPageSize;
        // Read afresh since the change already (the lobby's window, rewarmLobbyWindow), and holding this page.
        const answersReadAfterChange = cardsNeedRemoteRefresh && !refresh
            && freshAnswersEpoch === pageDataEpoch
            && Boolean(cardsPages.peek(query, cardsPage, pageSize, { maxAgeMs: PAGE_PRESENT_MAX_AGE_MS }));
        const shouldRefresh = refresh || (cardsNeedRemoteRefresh && !answersReadAfterChange);
        if (shouldRefresh) cardsPages.invalidate(); // stale after mutations
        /* The fit of this page's capacity, measured as it started; a later
           measurement of the same capacity is fresher. A page lands with the
           fit of its own capacity even when the viewport has moved on to
           another (a window dragged across capacities): the columns and scale
           measured for another number of cards never go on it, and the
           debounced rebuild brings the viewport's own page. */
        const startFit = { scale: cardsGridScale, columns: cardsGridColumns, pageSize };
        const pageFit = () => (cardsGridMeasured && cardsGridCapacity === pageSize
            ? { scale: cardsGridScale, columns: cardsGridColumns, pageSize }
            : startFit);
        const served = await cardsPages.request(query, cardsPage, cardsPageSize, {
            refresh: shouldRefresh,
            maxAgeMs: PAGE_PRESENT_MAX_AGE_MS,
            priority: 'high',
        });
        if (!served) {
            if (!renderIsCurrent()) return;
            settlePageArt();
            console.error('[Collection] Failed to render cards:', new Error('Failed to fetch collection page'));
            dropCardsPrebuild();
            // Nothing is on screen for a look-ahead to lead on from.
            dropAllLookAheads();
            disposeCollectionExactImages(container);
            previousShown?.warmController.abort();
            container.innerHTML = `<div class="collection-empty" data-load-failed="true">${escapeHtml(uiText('collection.cards.loadFailed', 'Failed to load cards.'))}</div>`;
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'failed';
            // A dropped request on mobile data used to be terminal: an empty
            // grid and a dropdown stuck on its bootstrap pair, recoverable only
            // by closing the Collection.
            const retry = document.createElement('button');
            retry.type = 'button';
            retry.id = 'collection-cards-retry';
            retry.className = 'btn deck-btn collection-cards-retry';
            retry.textContent = uiText('collection.cards.retry', 'Retry');
            retry.addEventListener('click', () => {
                void renderCards(undefined, { resetScroll: true, refresh: true });
            });
            container.appendChild(retry);
            updateCollectionFilterOptions();
            finishRender();
            return;
        }

        if (!renderIsCurrent()) return;
        const pagination = served.page;
        if (shouldRefresh || answersReadAfterChange) {
            cardsNeedRemoteRefresh = false;
        }
        cardsPage = Number(pagination?.page) || 1;
        currentPageEntries = Array.isArray(pagination?.items) ? pagination.items : [];
        availableSpellOwners = Array.isArray(pagination?.spellOwners) ? pagination.spellOwners : availableSpellOwners;
        if (pagination?.spellOwnerNames && typeof pagination.spellOwnerNames === 'object') {
            spellOwnerNamesByKey = new Map(Object.entries(pagination.spellOwnerNames));
        }
        if (Array.isArray(pagination?.collections)) {
            availableCollections = pagination.collections;
            collectionsResolved = true;
        }
        updateSpellOwnerFilterOptions();
        updateCollectionFilterOptions();
        currentPageEntries.forEach((entry) => {
            if (!entry?.card?.id) return;
            upsertCardCache([{
                ...entry.card,
                ownedNormalQty: entry.variant === 'normal' ? entry.ownedQty : (getOwnedNormalQty(entry.card.id) || 0),
                ownedStellarQty: entry.ownedStellarQty || (entry.variant === 'stellar' ? entry.ownedQty : (getOwnedStellarQty(entry.card.id) || 0)),
            }]);
            if (entry.variant === 'normal' && Number(entry.ownedQty) > 0) {
                ownedCardMap.set(entry.card.id, Number(entry.ownedQty) || 0);
                if (!ownedCards.includes(entry.card.id)) ownedCards.push(entry.card.id);
            }
            if (entry.variant === 'stellar' && Number(entry.ownedQty) > 0) {
                ownedCardStellarMap.set(entry.card.id, Number(entry.ownedQty) || 0);
                if (!ownedCards.includes(entry.card.id)) ownedCards.push(entry.card.id);
            }
        });
        const displayCards = currentPageEntries;
        // This page's own pictures: the look-ahead's wait until they have
        // settled, and the look-ahead this render replaced goes once they are
        // asked for on screen (the one it holds may be this page's own).
        // A page of another question with pictures still to download: once it
        // has asked for them, the window beyond the next page is dropped, so
        // under its loading state only what this page needs is on the wire.
        const downloadsOwnPictures = (entries) => !answersSameQuestion
            && pictureUrlsOf(entries).some((url) => !picturesInHand.has(url));
        let dropsFarAhead = false;
        const pageArtAskedFor = (warm) => {
            retireLookAheads();
            if (dropsFarAhead) {
                dropFarLookAhead();
                stopUnplayedStellarClips(container);
            }
            Promise.resolve(warm).then(() => settlePageArt(), () => settlePageArt());
        };
        currentPageArtSettled = pageArtSettled;
        readNextPageAhead(pagination, pageArtSettled);
        // An answer older than the caller tolerates is shown, then re-read
        // behind it: the swap only touches what the fresh read changed.
        const revalidate = !shouldRefresh && Date.now() - served.fetchedAt > revalidateAfterMs;
        const afterCommit = () => {
            scheduleNextPagePrefetch(pagination);
            if (revalidate && renderIsCurrent()) {
                void cardsPages.request(query, cardsPage, cardsPageSize, { maxAgeMs: 0, priority: 'high' })
                    .then((fresh) => {
                        if (fresh && renderIsCurrent()) void renderCards(undefined, {});
                    });
            }
        };

        if (pagination.totalItems === 0) {
            dropCardsPrebuild();
            pageArtAskedFor(null);
            disposeCollectionExactImages(container);
            previousShown?.warmController.abort();
            container.innerHTML = `<div class="collection-empty">${escapeHtml(uiText('collection.cards.noMatches', 'No cards match your filters.'))}</div>`;
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'empty';
            renderedCardCount = 0;
            applyCardsGridColumns(0, pageFit());
            updateCardsPagination(pagination);
            committedCardsSignature = cardsRenderSignature();
            shownCardsTurn = {
                key: cardsPageKey(query, cardsPage, pageSize),
                requestId,
                pagination,
                artSettled: pageArtSettled,
                warmController,
            };
            paintedCardsContentSignature = cardsContentSignature();
            finishRender();
            afterCommit();
            return;
        }
        // The scale this page is shown at: its fit's, which the commit
        // publishes with the page's columns (applyCardsGridColumns).
        const pageScale = () => pageFit().scale || publishedCardsScale(container);
        // Reconcile against what is on screen: a tile identical to the one at
        // its position stays; only the others are warmed and built. A tile the
        // player saw on the page just left (retired), or one settled ahead of
        // this page (prebuilt), is taken as it is.
        const planSlots = () => {
            const onScreen = /** @type {HTMLElement[]} */ (Array.from(container.children)).filter((child) => (
                child.classList?.contains('collection-card-item') && !child.classList.contains('skeleton-card')
            ));
            const prebuilt = prebuiltTilesBySignature();
            return {
                existing: onScreen,
                slots: displayCards.map((entry, index) => {
                    const signature = cardTileSignature(entry, pageScale());
                    const kept = onScreen[index]?.dataset.tileSignature === signature ? onScreen[index] : null;
                    const retired = kept ? null : retiredTiles.get(signature) || null;
                    const ahead = kept || retired ? null : prebuilt.get(signature) || null;
                    return { entry, signature, kept, retired, prebuilt: ahead, node: kept };
                }),
            };
        };
        // The entries no tile on hand already answers: the ones warmed and built.
        const entriesToBuild = () => planSlots().slots
            .filter((slot) => !slot.kept && !slot.retired && !slot.prebuilt)
            .map((slot) => slot.entry);
        // A Stellar card's media still being read by the lobby's warm: its
        // tile is built with its loop, not without it and decorated later.
        const stellarRead = stellarMediaWarmFor(displayCards);
        if (stellarRead) {
            /* The pictures need no Stellar media: they are asked for now, and
               only the tiles wait for the read. Asked for after it, a cold
               phone open waited out the join's 1.5 s cap before its pictures
               were even promoted from the lobby's warm (round 14, 26 September
               2026, WebKit phone probe). warmVisibleCardPage below joins this
               warm; the scheduler keeps one request per picture. */
            const planned = entriesToBuild();
            if (planned.length > 0) warmPageArt(planned, 'high', warmController.signal);
            retireLookAheads();
            if (downloadsOwnPictures(planned)) {
                dropFarLookAhead();
                stopUnplayedStellarClips(container);
            }
            let capTimer = null;
            await Promise.race([
                stellarRead,
                new Promise((resolve) => { capTimer = setTimeout(resolve, STELLAR_WARM_JOIN_CAP_MS); }),
            ]);
            clearTimeout(capTimer);
            if (!renderIsCurrent()) return;
        }
        const toBuild = entriesToBuild();
        // A kept card may still be streaming its art. Transfer that demand to
        // this page before the previous page releases its warm at handover.
        const pendingKept = planSlots().slots.filter(({ kept }) => {
            const paint = kept?.querySelector('.card-image > img.card-art-paint');
            return paint && !paint.classList.contains('card-art-unavailable')
                && !(paint.complete && paint.naturalWidth > 0);
        }).map((slot) => slot.entry);
        const toWarm = [...toBuild, ...pendingKept];
        // What the open's hold has left past the grace (presentPastGrace).
        const heldOpen = collectionPresentationHeld() ? presentation : null;
        dropsFarAhead = downloadsOwnPictures(toBuild);
        if (toWarm.length > 0) {
            await warmVisibleCardPage(toWarm, heldOpen, pageArtAskedFor, warmController.signal);
        } else {
            notePresentationArt(heldOpen, null);
            pageArtAskedFor(null);
        }
        if (!renderIsCurrent()) return;
        // Planned again after the await: the page on screen may have been swapped meanwhile.
        const plannedScale = pageScale();
        const { existing, slots } = planSlots();
        // The page the player just left comes back as the very tiles they saw.
        slots.forEach((slot) => {
            if (!slot.retired) return;
            retiredTiles.delete(slot.signature);
            slot.node = slot.retired;
        });

        const stage = document.createElement('div');
        stage.className = 'collection-card-paint-stage';
        stage.setAttribute('aria-hidden', 'true');
        /* Built at the scale it is shown at. A page of a new capacity (a
           resize) is built while the page on screen keeps the scale of its
           own columns, so its tiles take theirs here: built at the old one,
           each would re-raster at the commit (cards.js; a 100-300 ms task per
           card on the Chrome phone seat). */
        if (plannedScale && plannedScale !== publishedCardsScale(container)) {
            stage.style.setProperty('--catalog-card-scale', plannedScale);
            stage.style.setProperty('--collection-card-scale', plannedScale);
        }
        container.appendChild(stage);
        renderStage = stage;
        container.dataset.rasterState = 'settling';
        // The prebuilt tiles move from their stage to this one in the same
        // task, so they never leave the document and their settled rasters
        // stay owned (cards.js disposes a paint that leaves it); the rest of
        // the page built ahead answers nothing now. A render of the same
        // question and page takes none of it, and the page is still ahead of
        // it: kept, it carries on after the commit (scheduleCardsPrebuild).
        slots.forEach((slot) => {
            if (!slot.prebuilt) return;
            slot.node = slot.prebuilt;
            stage.appendChild(slot.node);
        });
        const keepAhead = !slots.some((slot) => slot.prebuilt)
            && cardsPrebuildIsAheadOf(query, cardsPage, cardsPageSize);
        if (!keepAhead) dropCardsPrebuild();
        const rasterCandidates = [];
        slots.forEach((slot) => {
            if (slot.node) return;
            const { wrapper, raster } = buildCardTile(slot.entry);
            wrapper.dataset.tileSignature = slot.signature;
            slot.node = wrapper;
            stage.appendChild(wrapper);
            if (raster) rasterCandidates.push(raster);
        });

        const settlements = rasterCandidates.map(({ cardElement, cardData }) => (
            settleVisibleCollectionCard(cardElement, cardData)
        ));

        // Bounded reveal (docs/asset-loading.md law 2 + grace): the page
        // commits when every raster settles or at the reveal cap, whichever
        // comes first. Metadata already arrived, so a slow or failed raster
        // degrades that one card to its truthful art-well placeholder while
        // its art keeps streaming in — it never holds or kills the page.
        let stageCommitted = false;
        const commitStage = () => {
            if (stageCommitted) return;
            const isCurrent = renderIsCurrent()
                && stage.isConnected
                && document.getElementById('collection-cards-grid') === container;
            if (!isCurrent) {
                disposeCollectionExactImages(stage);
                stage.remove();
                return;
            }
            stageCommitted = true;
            // The loading state ends BEFORE the new tiles go in: inserted
            // under the old stale dim, they took their first style at 0.55
            // and faded up — the new page arrived dimmed.
            markCardsGridStale(container, false);

            const kept = new Set(slots.map((slot) => slot.kept).filter(Boolean));
            const changed = slots.some((slot) => !slot.kept) || existing.length !== slots.length;
            /* The one loading state on screen (the page of wells a held open
               presented past its grace) is replaced WHOLE, in this frame. The
               deal-in staggers the tiles over ~200 ms from opacity 0, so over
               wells the player is already looking at the page arrived tile
               by tile: 40-48 pop-ins in 2-4 waves on every seat (round 15, 28
               September 2026). The entrance belongs to a page presented with
               the Collection itself (deferredEntrance), which it enters with. */
            const replacesWells = gridShowsWells(container);
            // The page built ahead, kept by this render, is not a tile.
            const aheadStage = cardsPrebuild?.stage || null;
            Array.from(container.children).forEach((child) => {
                if (child === stage || child === aheadStage || kept.has(child)) return;
                retireCardTile(child);
            });
            // Kept tiles never move (each stays at its own index); new ones are
            // slotted in around them, then the stage goes.
            slots.forEach((slot, index) => {
                const anchor = container.children[index];
                if (anchor !== slot.node) container.insertBefore(slot.node, anchor || stage);
            });
            stage.remove();
            // A returning tile's Stellar loop was paused when it left the page.
            slots.forEach((slot) => {
                if (!slot.retired) return;
                slot.node.querySelectorAll('.stellar-card').forEach((cardEl) => {
                    if (cardEl.querySelector('.stellar-loop-video')) Cards?.autoPlayStellarVideo?.(cardEl);
                });
            });
            renderedCardCount = displayCards.length;
            applyCardsGridColumns(displayCards.length, pageFit());
            // A resize while the page was built moved its fit on: its tiles
            // are drawn at the fit published now, and say so.
            if (publishedCardsScale(container) !== plannedScale) slots.forEach((slot) => refreshTileSignature(slot.node));
            updateCardsPagination(pagination);
            container.removeAttribute('aria-busy');
            container.dataset.rasterState = 'ready';
            container.scrollTop = scrollTop;

            if (isEntrance && !replacesWells) {
                // An open held off-screen deals its page in when it presents.
                if (collectionPresentationHeld()) presentation.deferredEntrance = true;
                else dealInCardsGrid(container);
            }
            // New cards under a resting pointer are not being pointed at.
            if (changed) hoverIntent.disarm();

            committedCardsSignature = cardsRenderSignature();
            shownCardsTurn = {
                key: cardsPageKey(query, cardsPage, pageSize),
                requestId,
                pagination,
                artSettled: pageArtSettled,
                warmController,
            };
            // What the cards ON SCREEN are about. Deliberately NOT cleared by
            // invalidateCardsCache(): that call means the DATA went stale, not
            // that the player is looking at the wrong cards.
            paintedCardsContentSignature = cardsContentSignature();
            previousShown?.warmController.abort();
            finishRender();

            // Load Stellar media after the still-image presentation is
            // committed, then hydrate those same card nodes in place.
            // `displayCards`, not the module field: loadRecipes() calls
            // invalidateCardsCache() on every entry, which empties
            // currentPageEntries — and it can land between this render and its
            // commit, leaving the page's Stellar art permanently unhydrated.
            const stellarIdsOnPage = displayCards
                .filter((entry) => entry.variant === 'stellar' && Number(entry.ownedQty) > 0)
                .map((entry) => entry.card.id);
            const unloadedStellarIds = stellarIdsOnPage.filter((id) => !getStellarAsset(id));
            // In this task for every tile whose media is in hand: one built
            // ahead from its still gets its loop as it appears, whatever the
            // rest of the page still waits for.
            attachStellarVideosToGrid();
            if (unloadedStellarIds.length > 0) {
                loadStellarAssets(unloadedStellarIds).then(() => {
                    if (renderIsCurrent()) attachStellarVideosToGrid();
                });
            }
            // A page planned before one of its prints moved: that tile is rebuilt now.
            followStellarPrints();
            // The summoners of the Spells now on screen, for their details.
            const spellOwners = displayCards.map((entry) => spellOwnerOf(entry.card)).filter(Boolean);
            if (spellOwners.length > 0) void readSpellOwners(spellOwners);
            afterCommit();
        };

        // A first open's ribbons still on their way: its tiles take them in
        // the stage, before the commit (firstDiscoveryJoinFor).
        const ribbonsJoined = firstDiscoveryJoinFor(displayCards, renderGeneration);
        const ribbons = ribbonsJoined && ribbonsJoined.then(() => {
            if (renderIsCurrent()) applyFirstDiscoveryDecorations();
        });
        if (settlements.length === 0 && !ribbons) {
            commitStage();
            return;
        }
        let revealCap = null;
        const armRevealCap = (ms, onCap) => {
            revealCap = setTimeout(() => {
                collectionRevealTimers.delete(revealCap);
                onCap();
            }, ms);
            collectionRevealTimers.add(revealCap);
        };
        const disarmRevealCap = () => {
            clearTimeout(revealCap);
            collectionRevealTimers.delete(revealCap);
        };
        armRevealCap(COLLECTION_REVEAL_CAP_MS, () => {
            // The one loading state is up and pictures are still arriving: the page waits longer to replace it whole.
            if (!pagePicturesLanded && gridShowsLoadingState(container)) {
                armRevealCap(COLLECTION_WELLS_REVEAL_CAP_MS - COLLECTION_REVEAL_CAP_MS, commitStage);
                return;
            }
            commitStage();
        });
        void Promise.all([Promise.all(settlements), ribbons]).then(([results]) => {
            disarmRevealCap();
            if (!results.every((result) => result === true)) {
                console.warn('[Collection] Some card rasters did not settle; committing with per-card placeholders.');
            }
            commitStage();
        }).catch(() => {
            disarmRevealCap();
            commitStage();
        });
    }


    /**
     * Hand the enlarged preview to whatever card is genuinely under the cursor.
     *
     * The popup is fixed, pointer-interactive, and drawn OVER the card it
     * enlarged and its neighbours — so sliding toward the next card keeps the
     * pointer inside the popup the whole way and the neighbour's `mouseenter`
     * never fires. (The wrapper's `mouseleave` also declines to close while the
     * pointer enters the popup, which is right for reading the card and exactly
     * what makes switching impossible.) `elementsFromPoint` is plural on
     * purpose: the singular form would only ever return the popup itself.
     */
    function attachCardHoverPreview(cardWrapper, card, variant = 'normal') {
        if (!Cards?.createCardElement) return;

        let hoverActive = false;
        let hoverGeneration = 0;
        // The hover a neighbour's preview handed to this card, while it builds.
        let handoffEnter = null;
        // Touch: tap enlarges, tap again acts. The guard above already admits the
        // synthetic (untrusted) mouseenter this dispatches while rejecting the
        // compatibility one a real tap produces.
        TouchPreview?.attach?.(cardWrapper);
        const presentHoverPreview = async () => {
            hoverActive = true;
            const generation = ++hoverGeneration;
            const surfaceGeneration = hoverSurfaceGeneration;
            // The preview's raster steps share cards.js's queue with a tile
            // built ahead; the page the player is reading goes first.
            yieldCardsPrebuild();
            clearHoverPreview({ keepHandedOff: true });

            const sourceCard = cardWrapper.querySelector(':scope > .card');
            const prepareRaster = Cards.prepareCardRasterForInteraction;
            if (typeof prepareRaster === 'function') {
                let rasterReady = false;
                try {
                    rasterReady = await prepareRaster(sourceCard, 'hover');
                } catch (_) {
                    rasterReady = false;
                }
                if (rasterReady !== true) return;
            }
            if (
                !hoverActive
                || generation !== hoverGeneration
                || surfaceGeneration !== hoverSurfaceGeneration
                || activeSection !== 'cards'
                || !cardWrapper.isConnected
                || !isOpen
                // The card-detail view is a different surface layered over the
                // grid, and the preview is `z-index: 2147483647` — nothing can
                // be layered above it. So a preview spawned by a card BEHIND the
                // detail sits on top of the detail's own chrome and eats its
                // clicks: "← Back" became a dead button, which is how a real
                // Stellar run kept failing before it reached its generation.
                // The preview is deliberately clickable (the catalog flow taps
                // it), so the fix is not to spawn one, never to make it
                // pointer-transparent. The detail's own cards are behind
                // nothing, so they keep enlarging.
                || isHoverSourceBehindCardDetail(cardWrapper)
            ) return;

            const sourceRect = cardWrapper.getBoundingClientRect();

            const preview = document.createElement('div');
            preview.id = 'card-hover-preview';
            preview.className = 'card-hover-preview collection-hover-preview';
            preview.style.zIndex = HOVER_PREVIEW_Z_INDEX;
            preview.style.left = `${Math.round(sourceRect.left)}px`;
            preview.style.top = `${Math.round(sourceRect.top)}px`;
            preview.style.visibility = 'hidden';

            const stellarState = variant === 'stellar' ? getStellarState(card.id) : null;
            const cardData = buildCardElementData(card, variant, stellarState?.asset);

            const cardEl = Cards.createCardElement(cardData, `collection-preview-${card.id}-${variant}`);
            cardEl.style.transformOrigin = HOVER_PREVIEW_OPTIONS.origin;
            cardEl.style.setProperty('--card-hover-transform', 'scale(1) rotate(0deg) translateY(0%)');
            if (variant === 'stellar') {
                cardEl.dataset.stellarPreview = 'collection';
            }
            preview.appendChild(cardEl);

            const putDown = () => {
                if (variant === 'stellar') {
                    Cards.setStellarHover(cardEl, false);
                }
                preview.remove();
            };
            preview.addEventListener('mouseleave', () => {
                // Handed off, it no longer takes the pointer, so the next move
                // reports a leave; it stays until the neighbour's is revealed.
                if (HoverHandoff?.isHandedOff?.(preview)) return;
                putDown();
            });
            HoverHandoff?.bind?.(preview, {
                source: cardWrapper,
                selector: '.collection-card-item',
                clear: clearHoverPreview,
                handoff: true,
                release: putDown,
            });
            preview.addEventListener('click', () => {
                if (variant === 'stellar') {
                    Cards.setStellarHover(cardEl, false);
                }
                preview.remove();
                openCardDetail(card.id, variant, { sourceTile: cardWrapper });
            });
            preview.style.cursor = 'pointer';
            preview.dataset.currentCardKey = `${card.id}:${variant}`;

            preview.addEventListener('wheel', (e) => {
                forwardWheelScroll(preview, e, [
                    '.deck-builder-cards',
                    '.collection-content',
                    '.collection-section-cards',
                    '.deck-builder-container',
                ]);
            }, { passive: false });

            document.body.appendChild(preview);
            applyStableHoverPreviewScale(cardEl, variant, sourceRect, preview, sourceCard);

            // CSS zoom and viewport units can resolve their final subpixel box
            // one rendering frame after this connected popup is scaled. Keep
            // it hidden through that frame, then validate (and, if necessary,
            // rebuild) its locked canvas from decoded authority before reveal.
            // The source transfer also prevents a thumbnail generation or a
            // pending fresh IMG from ever becoming first paint.
            const settleRaster = Cards.settleCardRasterForPresentation;
            let settledRaster = false;
            if (typeof settleRaster === 'function') {
                try {
                    settledRaster = await settleRaster(cardEl, cardData, {
                        oversample: 1,
                        stableGeometry: true,
                        sourceCard,
                        coverVisualExpansion: true,
                    });
                } catch (_) {
                    settledRaster = false;
                }
            }
            if (
                settledRaster !== true
                // Torn down while it settled (a shared clear finds it by id):
                // revealing it off the page would register its tilt, start a
                // Stellar card's hover media for nobody, and let a handed-off
                // preview go although nothing took its place.
                || !preview.isConnected
                || !hoverActive
                || generation !== hoverGeneration
                || surfaceGeneration !== hoverSurfaceGeneration
                || activeSection !== 'cards'
                || !cardWrapper.isConnected
                || !isOpen
                // Settling the raster is the long await, and the detail can open
                // across it — a tile clicked while its own preview was still
                // settling would reveal that preview over the detail. Its source
                // is hidden by then, so no `mouseleave` can ever follow and the
                // popup stays there. Re-check on the way out, not only on the
                // way in.
                || isHoverSourceBehindCardDetail(cardWrapper)
                // Likewise a detail still being built behind the grid: it is
                // about to take the screen from this card.
                || (!cardWrapper.closest('.collection-card-detail')
                    && document.querySelector('.collection-card-detail-stage'))
            ) {
                preview.remove();
                return;
            }

            if (CardTilt?.registerPreview) {
                CardTilt.registerPreview(cardEl);
            }
            if (variant === 'stellar') {
                Cards.setStellarHover(cardEl, true);
            }
            preview.style.removeProperty('visibility');
            // The preview this card took the hover from leaves in this task.
            HoverHandoff?.releaseHandedOff?.();
        };
        cardWrapper.addEventListener('mouseenter', (event) => {
            if (event?.isTrusted !== false && TouchPreview?.isSuppressingMouse?.()) return;
            // Cards that appeared under a resting pointer are not being pointed at.
            if (!hoverIntent.admits(event)) return;
            /* A neighbour's preview handed this card the hover, and made itself
               transparent to the pointer; the browser then reports its own
               enter onto this card on the next move. That is the same hover,
               not a new one: restarting it would drop the build under way. */
            const handoff = HoverHandoff?.isHandoffEnter?.(event) ? {} : null;
            if (!handoff && handoffEnter) return;
            if (handoff) handoffEnter = handoff;
            const endHover = HoverHandoff?.beginHover?.();
            void presentHoverPreview().finally(() => {
                if (handoffEnter === handoff) handoffEnter = null;
                endHover?.();
            });
        });
        TouchPreview?.attach?.(cardWrapper);

        cardWrapper.addEventListener('mouseleave', (e) => {
            hoverActive = false;
            hoverGeneration += 1;
            // The pointer really left: its next enter is a new hover.
            handoffEnter = null;
            const preview = currentHoverPreview();
            if (preview && e.relatedTarget && (preview === e.relatedTarget || preview.contains(e.relatedTarget))) {
                return;
            }
            clearHoverPreview({ keepHandedOff: true });
        });
    }

    /**
     * What a back's tile says about it: whether it is the player's, whether it
     * is the one equipped, and the words for that. The page build and the
     * in-place selection reconcile read the same answer, so an equipped tile
     * reads exactly as a freshly built one would.
     */
    function cardbackTileState(catalogRow) {
        // The catalog list is kept across a language switch; the tile reads
        // its copy in the language selected at render time.
        const cb = MoonfallCloud?.cardbacks?.localize?.(catalogRow) || catalogRow;
        const isUnlocked = cb.isDefault || (unlockedCardbacks && unlockedCardbacks.includes(cb.slug));
        const isSelected = selectedCardback === cb.slug;
        const hintLabel = cb.unlockHint || uiText('collection.cardbacks.locked', 'Locked');
        const equipLabel = uiText('collection.cardbacks.equip', 'Equip');
        const equippedLabel = uiText('collection.cardbacks.equipped', 'Equipped');
        let actionLabel = '';
        if (isUnlocked && !isSelected) actionLabel = equipLabel;
        else if (isSelected) actionLabel = equippedLabel;
        const stateLabel = isSelected ? equippedLabel : (isUnlocked ? equipLabel : hintLabel);
        return {
            cb,
            isUnlocked,
            isSelected,
            hintLabel,
            actionLabel,
            actionClass: `btn deck-btn select-cardback-btn${isSelected ? ' equipped' : ''}`,
            accessibleLabel: `${cb.name}: ${stateLabel}`,
            selectedLabel: uiText('collection.cardbacks.selected', 'Selected'),
        };
    }

    function buildCardbackItemHtml(catalogRow) {
        const {
            cb,
            isUnlocked,
            isSelected,
            hintLabel,
            actionLabel,
            actionClass,
            accessibleLabel,
            selectedLabel,
        } = cardbackTileState(catalogRow);
        const declaredImageUrl = sanitizeCardbackImageUrl(cb.imageUrl)
            || (cb.isDefault ? '/assets/images/cardback.webp' : '');
        const safeImageUrl = declaredImageUrl ? escapeHtml(encodeURI(declaredImageUrl)) : '';
        const safeName = escapeHtml(cb.name);
        const safeDesc = escapeHtml(cb.description || '');
        const safeSlug = escapeHtml(cb.slug);
        const safeHint = escapeHtml(hintLabel);

        const lockOverlay = !isUnlocked
            ? `<div class="cardback-lock-overlay"><span class="lock-icon">🔒</span><div class="lock-hint">${safeHint}</div></div>` : '';
        const lockCopy = !isUnlocked
            ? `<div class="cardback-lock-copy" aria-hidden="true">${safeHint}</div>` : '';
        const selectedBadge = isSelected
            ? `<div class="cardback-selected-badge">${escapeHtml(selectedLabel)}</div>` : '';
        const action = actionLabel
            ? `<span class="${actionClass}" aria-hidden="true">${escapeHtml(actionLabel)}</span>`
            : '';

        const commonAttributes = `class="cardback-item ${isSelected ? 'selected' : ''} ${!isUnlocked ? 'locked' : ''}" data-slug="${safeSlug}" aria-label="${escapeHtml(accessibleLabel)}"`;
        const openTag = isUnlocked
            ? `<button type="button" ${commonAttributes} aria-pressed="${isSelected ? 'true' : 'false'}"${isSelected ? ' disabled' : ' data-cardback-action="equip"'}>`
            : `<div ${commonAttributes} role="group">`;
        const closeTag = isUnlocked ? '</button>' : '</div>';

        const imageSourceAttributes = safeImageUrl
            ? ` src="${safeImageUrl}" data-cardback-source="${safeImageUrl}"`
            : '';
        const pendingClass = !safeImageUrl && !cb.isDefault ? ' cardback-image-pending' : '';
        // Eager, never lazy: one page of backs is rendered behind the Cards
        // grid on open so it is fetched and decoded before the tab is entered.

        return `${openTag}
            <div class="cardback-image-wrapper cardback-tiltable">
                <img${imageSourceAttributes} data-cardback-slug="${safeSlug}" alt="${safeName}" class="cardback-image${pendingClass}">
                ${lockOverlay}${selectedBadge}
            </div>
            <div class="cardback-info">
                <div class="cardback-name">${safeName}</div>
                <div class="cardback-desc">${safeDesc}</div>
                ${lockCopy}
            </div>
            ${action}
        ${closeTag}`;
    }

    /**
     * An equip (or a selection read back with the profile) changes which back
     * is selected, not which backs are on the page, so it is drawn on the
     * tiles already there. It used to rebuild the page when the server
     * answered: every tile, picture and tilt host was replaced, and the back
     * under the resting pointer snapped flat and lost its glare (flicker hunt,
     * 25 September 2026). Only the tiles whose selection changed are touched.
     */
    function reconcileCardbackSelection(grid, pageItems) {
        const rows = new Map(pageItems.map((row) => [row.slug, row]));
        grid.querySelectorAll(':scope > .cardback-item[data-slug]').forEach((node) => {
            const tile = /** @type {HTMLElement} */ (node);
            const row = rows.get(tile.dataset.slug);
            if (!row) return;
            const state = cardbackTileState(row);
            if (tile.classList.contains('selected') === state.isSelected) return;
            tile.classList.toggle('selected', state.isSelected);
            tile.setAttribute('aria-label', state.accessibleLabel);
            if (tile instanceof HTMLButtonElement) {
                tile.setAttribute('aria-pressed', state.isSelected ? 'true' : 'false');
                tile.disabled = state.isSelected;
                if (state.isSelected) delete tile.dataset.cardbackAction;
                else tile.dataset.cardbackAction = 'equip';
            }

            const wrapper = tile.querySelector(':scope > .cardback-image-wrapper');
            const badge = wrapper?.querySelector(':scope > .cardback-selected-badge');
            if (!state.isSelected) {
                badge?.remove();
            } else if (wrapper && !badge) {
                const selectedBadge = document.createElement('div');
                selectedBadge.className = 'cardback-selected-badge';
                selectedBadge.textContent = state.selectedLabel;
                wrapper.appendChild(selectedBadge);
            }

            let action = tile.querySelector(':scope > .select-cardback-btn');
            if (!state.actionLabel) {
                action?.remove();
                return;
            }
            if (!action) {
                action = document.createElement('span');
                action.className = state.actionClass;
                action.setAttribute('aria-hidden', 'true');
                tile.appendChild(action);
            } else {
                action.className = state.actionClass;
            }
            action.textContent = state.actionLabel;
        });
    }

    /*
     * The backs the Cardbacks tab opens on, fetched and DECODED before the tab
     * is entered: on Collection open (behind the Cards grid) and on hover,
     * focus or first touch of the tab. Measured before: the tab fetched them on
     * entry (`loading="lazy"` wells inside a panel that had just been shown),
     * so its first frame was the cohort hold and the backs dealt in ~0.6 s
     * later. Warmed, every back is already presentable and the cohort never
     * holds. At most one page (five), a handful of pictures kept, never the
     * catalog.
     */
    const cardbackArtWarm = new Map();
    const CARDBACK_ART_WARM_LIMIT = 10;

    function cardbackArtUrl(catalogRow) {
        return sanitizeCardbackImageUrl(catalogRow?.imageUrl)
            || (catalogRow?.isDefault ? '/assets/images/cardback.webp' : '');
    }

    /*
     * AND EVERY OTHER BACK, AS BYTES (the owner, 28 September 2026: "carbacks
     * are very limited. We have only 10 ... we could afford to preload all of
     * the carbacks. Or ... the first 10 carbacks ... it won't curse the
     * player with unnecessary waiting"). A small fixed set may preload
     * (docs/asset-loading.md law 3), so the backs beyond the page on screen
     * are fetched — never decoded — two at a time, capped at
     * CARDBACK_LOOK_AHEAD_CAP should the set ever grow (three pages of the
     * widest grid, ten times the five a page shows today). Without CORS, as
     * the tiles load them, so a tile finds these very bytes. The Collection
     * asks once the next page's pictures are in (renderCardbacksAfterNextPage),
     * the lobby once its first page's are (warmLobbyWindow).
     */
    const CARDBACK_LOOK_AHEAD_CAP = 30;
    const CARDBACK_BYTES_AT_ONCE = 2;
    const cardbackBytesAsked = new Set();
    let cardbackBytesQueue = [];
    let cardbackBytesInFlight = 0;

    function warmCardbackBytes() {
        if (!Array.isArray(cardbacks) || typeof Image !== 'function') return;
        cardbacks.slice(0, CARDBACK_LOOK_AHEAD_CAP).forEach((catalogRow) => {
            const url = cardbackArtUrl(catalogRow);
            if (!url || cardbackArtWarm.has(url) || cardbackBytesAsked.has(url)) return;
            cardbackBytesAsked.add(url);
            cardbackBytesQueue.push(url);
        });
        pumpCardbackBytes();
    }

    function pumpCardbackBytes() {
        while (cardbackBytesInFlight < CARDBACK_BYTES_AT_ONCE && cardbackBytesQueue.length > 0) {
            const url = cardbackBytesQueue.shift();
            cardbackBytesInFlight += 1;
            const image = new Image();
            image.decoding = 'async';
            image.fetchPriority = 'low';
            let done = false;
            const finish = (failed) => {
                if (done) return;
                done = true;
                cardbackBytesInFlight -= 1;
                // A back that did not arrive may be asked for again later.
                if (failed) cardbackBytesAsked.delete(url);
                pumpCardbackBytes();
            };
            image.addEventListener('load', () => finish(false), { once: true });
            image.addEventListener('error', () => finish(true), { once: true });
            image.src = url;
        }
    }

    function warmCardbackArt(priority = 'low', { all = false } = {}) {
        if (!Array.isArray(cardbacks) || cardbacks.length === 0) return;
        const start = Math.max(0, (cardbackPage - 1) * cardbacksPageSize);
        cardbacks.slice(start, start + CARDBACKS_PER_PAGE).forEach((catalogRow) => {
            const url = cardbackArtUrl(catalogRow);
            if (!url || cardbackArtWarm.has(url)) return;
            // No crossOrigin: the same request mode as the tile's own <img>, so
            // the tile reuses these bytes and this decode.
            const image = new Image();
            image.decoding = 'async';
            image.fetchPriority = priority === 'high' ? 'high' : 'low';
            image.src = url;
            if (typeof image.decode === 'function') image.decode().catch(() => {});
            cardbackArtWarm.set(url, image);
            while (cardbackArtWarm.size > CARDBACK_ART_WARM_LIMIT) {
                cardbackArtWarm.delete(cardbackArtWarm.keys().next().value);
            }
        });
        if (all) warmCardbackBytes();
    }

    function disposeRetryingCardbackImages(root) {
        if (!root) return;
        cancelCardbackArtCohort();
        root.querySelectorAll('.cardback-image').forEach((image) => {
            image.__collectionCardbackArtLease?.dispose?.();
            delete image.__collectionCardbackArtLease;
            delete image.dataset.cardbackObserverSlug;
            window.MoonfallRetryingImage?.dispose?.(image);
            if (image.dataset.retryingImageState !== 'loaded') {
                delete image.dataset.cardbackRetryOwner;
            }
        });
    }

    /*
     * The cardback page's art arrives as ONE cohort (docs/asset-loading.md
     * law 2, the cohort rule). Every back is a ~1 MB PNG, and five of them
     * raced the network on tab entry: each popped in the moment its own bytes
     * landed, in whatever order the connection delivered — "cardbacks loading
     * order seems a bit chaotic". The Cards grid next door commits a page at
     * once and deals it in left to right; this gives the backs the same feel.
     *
     * Only the ART WELLS wait. The tile chrome — name, description, lock,
     * Equip — paints immediately. The wells of a freshly presented page are
     * held together and released in one left-to-right deal-in once every back
     * has decoded pixels, or at the shared reveal cap when one is slow; a back
     * that lands after the cap fades in on its own. A page rebuilt behind the
     * player (an equip, a language change, a resize) never re-hides a back that
     * was already on screen: only its still-loading members are held.
     */
    const CARDBACK_ART_HOLD_CLASS = 'cardback-image-cohort-hold';
    let cardbackArtCohort = null;

    function isCardbackImagePresentable(image) {
        return Boolean(image?.complete) && image.naturalWidth > 0 && image.naturalHeight > 0;
    }

    function whenCardbackImageSettles(image) {
        return new Promise((resolve) => {
            const finish = () => {
                image.removeEventListener('load', onLoad);
                image.removeEventListener('error', finish);
                resolve();
            };
            const onLoad = () => {
                // Decoded pixels, not merely fetched bytes: decoding five large
                // PNGs on the reveal frame would be a visible hitch. A failed or
                // superseded decode still settles; the cap and the retrying
                // loader own what happens next.
                const decoded = typeof image.decode === 'function'
                    ? image.decode()
                    : Promise.resolve();
                Promise.resolve(decoded).catch(() => {}).then(finish);
            };
            if (isCardbackImagePresentable(image)) {
                onLoad();
                return;
            }
            image.addEventListener('load', onLoad);
            image.addEventListener('error', finish);
        });
    }

    function dealInCardbackImages(images) {
        if (typeof FX?.staggerElements === 'function') {
            FX.staggerElements(images);
            // Spent once played, or every return to the tab replays it.
            spendDealIn(images);
        }
    }

    function cancelCardbackArtCohort() {
        const cohort = cardbackArtCohort;
        if (!cohort) return;
        cardbackArtCohort = null;
        clearTimeout(cohort.timer);
        cohort.cancelled = true;
        // Back to "never presented": the next visible render holds them again
        // before anything can paint.
        cohort.images.forEach((image) => {
            image.classList.remove(CARDBACK_ART_HOLD_CLASS);
            delete image.dataset.cardbackReveal;
        });
    }

    function fadeInCardbackImageWhenItLands(image) {
        image.addEventListener('load', () => dealInCardbackImages([image]), { once: true });
    }

    function holdCardbackArtCohort(grid, { entrance = false } = {}) {
        // A hidden panel loads nothing (its wells are lazy and unpainted); the
        // hold is armed by the render that makes the tab visible.
        if (!grid || !isCardbacksSectionVisible()) return;
        if (cardbackArtCohort && cardbackArtCohort.grid === grid) return;
        const images = [];
        grid.querySelectorAll(':scope > .cardback-item .cardback-image').forEach((image) => {
            if (image.dataset.cardbackReveal === 'ready') return;
            if (!image.hasAttribute('src')) {
                // No source yet (its URL is still publishing, or a failed load
                // is waiting to retry): it cannot land soon and must not stall
                // the page. It fades in by itself whenever it does.
                image.dataset.cardbackReveal = 'ready';
                fadeInCardbackImageWhenItLands(image);
                return;
            }
            if (!entrance && isCardbackImagePresentable(image)) {
                // A refresh keeps every back the player can already see; only
                // an entrance (nothing on screen yet) holds decoded backs too,
                // so the whole page deals in as one.
                image.dataset.cardbackReveal = 'ready';
                return;
            }
            images.push(image);
        });
        if (images.length === 0) return;
        if (images.every(isCardbackImagePresentable)) {
            // Warm: exactly like a direct render, no animation to earn.
            images.forEach((image) => { image.dataset.cardbackReveal = 'ready'; });
            return;
        }

        const cohort = { grid, images, timer: null, cancelled: false, released: false };
        images.forEach((image) => {
            image.classList.add(CARDBACK_ART_HOLD_CLASS);
            image.dataset.cardbackReveal = 'held';
        });
        const release = () => {
            if (cohort.released || cohort.cancelled) return;
            cohort.released = true;
            clearTimeout(cohort.timer);
            if (cardbackArtCohort === cohort) cardbackArtCohort = null;
            const arrived = [];
            images.forEach((image) => {
                image.classList.remove(CARDBACK_ART_HOLD_CLASS);
                image.dataset.cardbackReveal = 'ready';
                // Past the cap: a straggler fades in alone when it lands.
                if (isCardbackImagePresentable(image)) arrived.push(image);
                else fadeInCardbackImageWhenItLands(image);
            });
            dealInCardbackImages(arrived);
        };
        cardbackArtCohort = cohort;
        cohort.timer = setTimeout(release, COLLECTION_REVEAL_CAP_MS);
        Promise.all(images.map(whenCardbackImageSettles)).then(release);
    }

    function bindCardbackImageSource(image, value) {
        const source = sanitizeCardbackImageUrl(value);
        if (!source || image.dataset.cardbackRetryOwner === source) return Boolean(source);
        window.MoonfallRetryingImage?.dispose?.(image);
        image.dataset.cardbackSource = source;
        image.dataset.cardbackRetryOwner = source;
        image.classList.remove('cardback-image-pending', 'cardback-image-unavailable');
        window.MoonfallRetryingImage?.load?.(image, source);
        return true;
    }

    function bindRetryingCardbackImages(root) {
        if (!root || typeof window.MoonfallRetryingImage?.load !== 'function') return;
        root.querySelectorAll('.cardback-image[data-cardback-slug]').forEach((image) => {
            bindCardbackImageSource(image, image.dataset.cardbackSource || '');
            const slug = image.dataset.cardbackSlug || 'default';
            if (image.dataset.cardbackObserverSlug === slug) return;
            image.__collectionCardbackArtLease?.dispose?.();
            image.dataset.cardbackObserverSlug = slug;
            if (typeof CardbackArt?.observe !== 'function') return;
            image.__collectionCardbackArtLease = CardbackArt.observe(
                image,
                slug,
                (descriptor) => {
                    if (image.dataset.cardbackObserverSlug !== slug) return;
                    if (descriptor?.kind === 'custom-pending') {
                        if (!image.dataset.cardbackSource) {
                            window.MoonfallRetryingImage?.dispose?.(image);
                            image.removeAttribute('src');
                            delete image.dataset.cardbackRetryOwner;
                            image.classList.remove('cardback-image-unavailable');
                            image.classList.add('cardback-image-pending');
                        }
                        return;
                    }
                    bindCardbackImageSource(image, descriptor?.url);
                },
            );
        });
    }

    function renderCardbacks() {
        const grid = document.getElementById('collection-cardback-grid');
        if (!grid) return;

        syncCardbacksPageSize({ preserveOffset: true });
        const pagination = paginateList(cardbacks, cardbacksPageSize, cardbackPage);
        cardbackPage = pagination.page;

        // Entering this tab called render twice ~500ms apart — once from the
        // cached list, once when support data resolved — and `innerHTML =` threw
        // away every <img> and re-registered every tilt host both times. Rebuild
        // only when the tiles would actually differ; the rest of this function
        // (pager, counters) is cheap and always runs. The selection is not in
        // the key: an equip is reconciled on the tiles below, never a rebuild.
        const renderKey = JSON.stringify([
            pagination.page,
            pagination.pageItems.map((cardback) => [
                cardback.slug,
                cardback.imageUrl || '',
                Boolean(cardback.isDefault),
            ]),
            unlockedCardbacks,
            i18n?.getLanguage?.() || 'en',
        ]);
        // Stamped on the node, not a module variable: if the grid element is
        // ever replaced, its render key goes with it and the next call rebuilds.
        const gridIsPopulated = grid.querySelector(':scope > .cardback-item');
        // An entrance is a page the player has not seen any art of yet; a
        // rebuild over presented art is a refresh and must not re-hide it.
        const entrance = !grid.querySelector('.cardback-image[data-cardback-reveal="ready"]');
        if (grid.dataset.cardbackRenderKey !== renderKey
            || (!gridIsPopulated && pagination.pageItems.length > 0)) {
            grid.dataset.cardbackRenderKey = renderKey;
            // Note: all dynamic values are escaped via escapeHtml/encodeURI in buildCardbackItemHtml
            disposeRetryingCardbackImages(grid);
            grid.innerHTML = pagination.pageItems.map(buildCardbackItemHtml).join('');

            // Register 3D tilt on cardback image wrappers
            grid.querySelectorAll('.cardback-tiltable').forEach(el => {
                if (CardTilt?.register) {
                    CardTilt.register(el);
                }
            });
        } else {
            reconcileCardbackSelection(grid, pagination.pageItems);
        }
        bindRetryingCardbackImages(grid);
        holdCardbackArtCohort(grid, { entrance });

        // Update pagination controls
        const prevBtn = document.getElementById('cardback-prev');
        const nextBtn = document.getElementById('cardback-next');
        const pageInfo = document.getElementById('cardback-page-info');
        if (prevBtn) prevBtn.disabled = pagination.page <= 1;
        if (nextBtn) nextBtn.disabled = pagination.page >= pagination.totalPages;
        if (pageInfo) {
            pageInfo.textContent = uiText('common.pageOf', `Page ${pagination.page} of ${pagination.totalPages}`, {
                page: pagination.page,
                total: pagination.totalPages,
            });
        }

        const paginationEl = document.getElementById('cardback-pagination');
        if (paginationEl) paginationEl.style.display = pagination.totalPages <= 1 ? 'none' : '';

        const countEl = document.getElementById('collection-cardbacks-count');
        if (countEl) {
            const ownedCount = cardbacks.filter(cb => cb.isDefault || unlockedCardbacks.includes(cb.slug)).length;
            countEl.textContent = uiText('collection.cardbacks.ownedCount', '{{owned}}/{{total}} owned', {
                owned: ownedCount,
                total: cardbacks.length,
            });
        }
    }

    function showStatus(message, tone = 'info') {
        showToast(message, tone);
    }

    function showRecipeDiscoveryBanner({ cardId, cardName, imageUrl } = {}) {
        if (typeof showAchievementBanner !== 'function') return;

        const name = cardName || (cardId ? getCardLabel(cardId) : uiText('collection.achievements.unknownCard', 'Unknown Card'));
        const title = uiText('collection.achievements.recipeDiscovery', 'Recipe Discovered!');
        const subtitle = uiText(
            'collection.achievements.recipeDiscoverySubtitle',
            '{{cardName}} is now craftable from Collection.',
            { cardName: name }
        );
        const directImageUrl = sanitizeImageUrlForDirectClient(imageUrl) || undefined;
        if (!cardId && !directImageUrl) {
            showAchievementBanner(title, subtitle, undefined);
            return;
        }
        showAchievementBanner(
            title,
            subtitle,
            directImageUrl,
            'card',
            cardId ? { cardId } : undefined,
        );
    }

    function escapeHtml(text) {
        return String(text ?? '').replace(/[&<>"']/g, (char) => ({
            '&': '&amp;',
            '<': '&lt;',
            '>': '&gt;',
            '"': '&quot;',
            "'": '&#39;',
        }[char]));
    }

    return {
        init,
        open,
        close,
        showRecipeDiscoveryBanner,
        isOpen: () => isOpen,
        getActiveSection: () => activeSection,
        // Entry-intent prefetch (docs/asset-loading.md law 3): callers with a
        // strong open signal (lobby button hover/focus/first touch) refresh the
        // first page if the idle warm is getting old. Best-effort; repeatable.
        prefetchFirstPage: () => warmForLobby({ priority: 'high', maxAgeMs: PAGE_INTENT_REFRESH_AFTER_MS }),
        warmForLobby: () => warmForLobby(),
        getSelectedCardback: selectedCardbackDescriptor,
    };
})();

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => Collection.init());
} else {
    Collection.init();
}

export { Collection };
